import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';
import { CoreSpaceHierarchyNode } from '@densityco/lib-api-types';
import { arrayMove } from 'react-movable';
import robustPointInPolygon from 'robust-point-in-polygon';

import { Action } from './actions';

import {
  parsePlanDXF,
  ParsedPlanDXF,
  PlanDXFExport,
  PlanDXFAsset,
  PlanDXF,
} from 'lib/dxf';
import Floorplan from 'lib/floorplan';
import { PlacementMode } from 'components/floorplan';
import { LengthUnit, Meters, Seconds } from 'lib/units';
import { Viewport } from 'lib/viewport';
import {
  isFloorplanPhotoGroupId,
  isFloorplanPlanSensorId,
  isFloorplanSpaceId,
  isFloorplanThresholdId,
  isFloorplanAreaOfConcernId,
} from 'lib/api';
import WallSegment from 'lib/wall-segment';
import PlanSensor, {
  EntryFunction,
  OpenAreaFunction,
  WaffleFunction,
  SensorCoverageIntersectionVectors,
  SensorConnection,
  SensorValidation,
  CopiedSensor,
} from 'lib/sensor';

import AreaOfConcern from 'lib/area-of-concern';
import Space, { CopiedSpace, SpaceValidation } from 'lib/space';
import { Heatmap } from 'lib/heatmap';
import HeightMap from 'lib/heightmap';
import Reference, { ReferenceRuler, ReferenceHeight } from 'lib/reference';
import Threshold from 'lib/threshold';
import PhotoGroup, { PhotoGroupPhoto } from 'lib/photo-group';
import { DEFAULT_PIXELS_PER_CAD_UNIT } from 'lib/dxf';
import {
  FloorplanCoordinates,
  CADCoordinates,
  ImageCoordinates,
  ViewportCoordinates,
  calculatePolygonCentroid,
} from 'lib/geometry';
import { modulo } from 'lib/math';
import { PointCloud } from 'lib/pointcloud';
import FloorplanCollection from 'lib/floorplan-collection';
import { FloorplanTargetInfo } from 'components/track-visualizer';
import {
  CSVImportMutation,
  CSVImportUpdate,
  CSVImportError,
  SpaceMetaCSVImportError,
  SpaceMetaCSVImportUpdate,
} from 'lib/csv';
import { computeDefaultCADOrigin, computeNewFloorplanForCAD } from 'lib/cad';
import Measurement from 'lib/measurement';

import { Update } from 'types/update';

const savePlanningStateToLocalStorage = (planningState: Planning) => {
  localStorage.setItem(
    'planningLatest',
    JSON.stringify({
      ...planningState,
      showHeatMap: false,
      showOpenAreaCoverageExtents: false,
      showCeilingHeightMap: false,
      showLiveStreamOpenArea: false,
      showLiveStreamOpenAreaTracks: false,
      showLiveStreamOpenAreaPoints: false,
      showLiveStreamEntry: false,
      showLiveStreamEntryCounts: false,
      showLiveStreamEntryTracks: false,
      showLiveStreamEntryPoints: false,
    })
  );
};

export function checkSensorPlacement(
  state: State,
  threshold: Threshold,
  space: Space
): number {
  //TODO: do something else instead of return 0?

  if (threshold.relatedPlanSensors.length === 0) {
    return 0;
  }

  const planSensor = state.planSensors.items.get(
    threshold.relatedPlanSensors[0]
  ) as PlanSensor;

  if (!planSensor) {
    return 0;
  }

  const result = robustPointInPolygon(Space.toPolygon(space).flat(), [
    planSensor.position.x,
    planSensor.position.y,
  ]);

  return result === 1 ? -1 : 1;
}

export type SelectionModeAction =
  | 'threshold.linkSpace'
  | 'threshold.linkPlanSensor'
  | undefined;

export type MutationOptions = {
  objectIds?: Array<string>;
  initialActionCreator: (objectIds: Array<string>) => Action;
  rollbackActionCreator: (objectIds: Array<string>) => Action;
  syncToAPI: (
    state: State,
    direction: 'forwards' | 'backwards',
    objectIds: Array<string>
  ) => Promise<void>;
};

export type UndoStackItem = {
  id: string;
  createdAt: string;

  forwardsActionCreator: (objectIds: Array<string>) => Action;
  backwardsActionCreator: (objectIds: Array<string>) => Action;
  syncToAPI: (
    state: State,
    direction: 'forwards' | 'backwards',
    objectIds: Array<string>
  ) => Promise<void>;
  objectIds: Array<string>;
};
export const UndoStackItem = {
  create(
    forwardsActionCreator: UndoStackItem['forwardsActionCreator'],
    backwardsActionCreator: UndoStackItem['backwardsActionCreator'],
    syncToAPI: UndoStackItem['syncToAPI'],
    objectIds: Array<string> = []
  ) {
    return {
      id: uuidv4(),
      createdAt: moment.utc().format(),

      forwardsActionCreator,
      backwardsActionCreator,
      syncToAPI,
      objectIds,
    };
  },
};

export type UndoStack = {
  stack: Array<UndoStackItem>;
  uuidMapping: Map<string, string>;
  currentIndex: number;
  loading: boolean;
  updatedAt: string | null;
};
export const UndoStack = {
  create(): UndoStack {
    return {
      stack: [],
      uuidMapping: new Map(),
      currentIndex: -1,
      loading: false,
      updatedAt: null,
    };
  },

  clear(undoStack: UndoStack): UndoStack {
    undoStack.stack = [];
    undoStack.uuidMapping = new Map();
    undoStack.currentIndex = -1;
    undoStack.loading = false;
    undoStack.updatedAt = null;
    return undoStack;
  },

  list(undoStack: UndoStack): Array<UndoStackItem> {
    return undoStack.stack;
  },

  count(undoStack: UndoStack): number {
    return undoStack.stack.length;
  },

  append(undoStack: UndoStack, item: UndoStackItem): UndoStack {
    const newStack = undoStack.stack.slice(0, undoStack.currentIndex + 1);
    return {
      ...undoStack,
      stack: [...newStack, item],
      currentIndex: undoStack.currentIndex + 1,
    };
  },

  removeLastItem(undoStack: UndoStack): UndoStack {
    return {
      ...undoStack,
      stack: undoStack.stack.slice(0, -1),
    };
  },

  undoEnabled(undoStack: UndoStack) {
    if (undoStack.loading) {
      return false;
    }
    return undoStack.stack.length > 0 ? undoStack.currentIndex >= 0 : false;
  },
  redoEnabled(undoStack: UndoStack) {
    if (undoStack.loading) {
      return false;
    }
    return undoStack.currentIndex < undoStack.stack.length - 1;
  },

  // If an id that is in an undo stack item changes, update the undo stack
  changeObjectId(
    undoStack: UndoStack,
    oldId: string,
    newId: string
  ): UndoStack {
    undoStack.uuidMapping.set(oldId, newId);

    const newStack = {
      ...undoStack,
      stack: undoStack.stack.map((item) => {
        let wasUpdated = false;

        const newObjectIds = item.objectIds.map((objectId) => {
          if (objectId === oldId) {
            wasUpdated = true;
            return newId;
          } else {
            return objectId;
          }
        });

        if (wasUpdated) {
          return { ...item, objectIds: newObjectIds };
        } else {
          return item;
        }
      }),
    };

    return newStack;
  },

  // Perform a relative move by "amount" in the undo stack
  async go(
    undoStack: UndoStack,
    initialState: State,
    dispatch: (action: Action) => void,
    amount: number,
    revertIfFailed = true,
    skipSyncToAPI = false
  ): Promise<number> {
    if (amount === 0) {
      return undoStack.currentIndex;
    }

    let state = initialState;

    const isMovingForwards = amount > 0;
    const startIndex = Math.max(
      Math.min(
        undoStack.currentIndex + (isMovingForwards ? 1 : 0),
        undoStack.stack.length - 1
      ),
      -1
    );
    const endIndex = Math.max(
      Math.min(undoStack.currentIndex + amount, undoStack.stack.length - 1),
      -1
    );

    let opCount = 0;

    // Move from the current index in the direction indicated by `amount`
    const loop = async (i: number): Promise<number> => {
      // console.log('LOOP ITERATION', i);
      const currentItem = state.undoStack.stack[i];
      // console.log('    ->', isMovingForwards ? 'forwards' : 'backwards', currentItem);

      const action = isMovingForwards
        ? currentItem.forwardsActionCreator(currentItem.objectIds)
        : currentItem.backwardsActionCreator(currentItem.objectIds);

      dispatch(action);

      // console.log('ACTION', action);
      state = reducer(state, action);

      let result = Promise.resolve();
      if (!skipSyncToAPI) {
        result = currentItem.syncToAPI(
          state,
          isMovingForwards ? 'forwards' : 'backwards',
          currentItem.objectIds
        );
      }

      return result
        .then(() => {
          opCount += 1;

          // console.log('OP:', isMovingForwards, i, startIndex, endIndex);
          isMovingForwards ? (i += 1) : (i -= 1);

          if (isMovingForwards ? i > endIndex : i <= endIndex) {
            // console.log('DONE!', i);
            return Promise.resolve(endIndex);
          }
          // console.log('NEXT LOOP!', i);
          return loop(i);
        })
        .catch(async (err) => {
          // Undo the optimistic update action that was applied
          const invertedAction = isMovingForwards
            ? currentItem.backwardsActionCreator(currentItem.objectIds)
            : currentItem.forwardsActionCreator(currentItem.objectIds);

          dispatch(invertedAction);
          state = reducer(state, invertedAction);

          if (revertIfFailed) {
            // Perform a "go" in the opposite direction to undo
            await UndoStack.go(
              { ...undoStack, currentIndex: i },
              state,
              dispatch,
              isMovingForwards ? -1 * opCount : opCount,
              false,
              skipSyncToAPI
            );
            return undoStack.currentIndex;
          } else {
            throw new Error(
              `Failed to run syncToAPI on undo stack item at index ${i}: ${err}`
            );
          }
        });
    };

    return loop(startIndex);
  },

  async undo(
    undoStack: UndoStack,
    initialState: State,
    dispatch: (action: Action) => void,
    skipSyncToAPI = false
  ) {
    return UndoStack.go(
      undoStack,
      initialState,
      dispatch,
      -1,
      true,
      skipSyncToAPI
    );
  },
  async redo(
    undoStack: UndoStack,
    initialState: State,
    dispatch: (action: Action) => void,
    skipSyncToAPI = false
  ) {
    return UndoStack.go(
      undoStack,
      initialState,
      dispatch,
      1,
      true,
      skipSyncToAPI
    );
  },

  // Perform an absolute move to an index in the undo stack
  async to(
    undoStack: UndoStack,
    initialState: State,
    dispatch: (action: Action) => void,
    index: number,
    skipSyncToAPI = false
  ) {
    const relativeOffset = index - undoStack.currentIndex;
    return UndoStack.go(
      undoStack,
      initialState,
      dispatch,
      relativeOffset,
      true,
      skipSyncToAPI
    );
  },
};

export type MappedPoint = {
  timestamp: number;
  sensorId: PlanSensor['id'];
  floorplanPosition: FloorplanCoordinates;
} & (
  | {
      isSimulated: false;
      sensorPoint?: PointCloud.Point;
    }
  | {
      isSimulated: true;
    }
);

export type AggregatedData = Array<MappedPoint>;
export namespace AggregatedData {
  const MAX_AGE = 10; // seconds

  export function releaseExpiredData(
    data: Array<MappedPoint>
  ): Array<MappedPoint> {
    const now = Seconds.fromMilliseconds(Date.now());
    return data.filter((point) => {
      return now - point.timestamp < MAX_AGE;
    });
  }
}

interface LastSensorIndices {
  lastOASensorIndex?: number;
  lastEntrySensorIndex?: number;
}

export type Planning = {
  showSensors: boolean;
  showSensorNoisePolygons: boolean;
  showSensorsOpenArea: boolean;
  showSensorsOpenAreaLR: boolean;
  showSensorsOpenAreaMR: boolean;
  showSensorsOpenAreaSR: boolean;

  showOpenAreaLabels: boolean;
  showOpenAreaCoverage: boolean;
  showOpenAreaCoverageExtents: boolean;
  showOpenAreaDoorFOV: boolean;

  showLiveStreamOpenArea: boolean;
  showLiveStreamOpenAreaTracks: boolean;
  showLiveStreamOpenAreaPoints: boolean;
  showLiveStreamEntry: boolean;
  showLiveStreamEntryCounts: boolean;
  showLiveStreamEntryDashboard: boolean;
  showLiveStreamEntryTracks: boolean;
  showLiveStreamEntryPoints: boolean;

  showSensorsEntry: boolean;
  showSensorsEntryLR: boolean;
  showSensorsEntryTOF: boolean;
  showEntryCoverage: boolean;
  showEntryLabels: boolean;

  showSensorsWaffle: boolean;

  showFloorplanImage: boolean;
  showSpaces: boolean;
  showSpacesEntry: boolean;
  showSpacesOpenArea: boolean;
  showSpaceNames: boolean;
  showThresholds: boolean;
  showIngressEgressRegions: boolean;
  showTriggerRegions: boolean;
  showSensorTracks: boolean;
  showAreasOfConcern: boolean;
  showRulers: boolean;
  showHeights: boolean;
  showScale: boolean;
  showPhotoGroups: boolean;
  showCeilingHeightMap: boolean;
  showWalls: boolean;
  showHeatMap: boolean;
  showSpacesWaffle: boolean;

  showOriginMarker: boolean;
};

export enum LayerId {
  HEIGHTMAP = 'heightmap',
  WALLS = 'walls',
  HEATMAP = 'heatmap',
}

export enum PasteChoice {
  HEIGHT = 'height',
  ROTATION = 'rotation',
  TYPE = 'type',
  SHAPE = 'shape',
  CAPACITY = 'capacity',
  FUNCTION = 'function',
  LABELS = 'labels',
  INCREMENT = 'increment',
}

export enum PastableEntity {
  SENSOR = 'sensor',
  SPACE = 'space',
}

export type SensorPoint = {
  frameNumber: number;
  x: number;
  y: number;
  z: number;
  velocity: number;
  snr: number;
  noise: number;
};

export type TimestampedFrameInfo = {
  timestamp: number;
  frame: {
    number: number;
    buffer: ArrayBuffer;
  };
};

export type State = {
  planMetadata: {
    floorId: string;
    buildingId: string;
    buildingName?: string;
    organizationName?: string;
  };
  locked: boolean;
  panels: {
    settingsOpen: boolean;
    csvOpen: boolean;
    dxfOpen: boolean;
  };
  uuidMap: Map<string, string>;
  defaultSensorType: {
    openArea: OpenAreaFunction;
    entry: EntryFunction;
    waffle: WaffleFunction;
  };
  defaultOAHardware: 'oa1' | 'oa1b';
  defaultEntryHardware: 's5' | 's5b';
  syncHeightsButtonEnabled: boolean;
  unsavedModifications: boolean;
  undoStack: UndoStack;
  renderOrder: 'forwards' | 'backwards';
  floorplan: Floorplan;
  floorplanImage: HTMLImageElement | null;
  floorplanImageKey: string | null;
  floorplanCADOrigin: FloorplanCoordinates;
  spaceHierarchyDataLoadingStatus: 'pending' | 'loading' | 'error' | 'complete';
  heightMap: { enabled: false } | ({ enabled: true } & HeightMap);
  heightMapUpdated: boolean;
  walls: FloorplanCollection<WallSegment>;
  wallsFullyPopulated: boolean;
  viewport: Viewport;
  spaces: FloorplanCollection<Space>;
  areasOfConcern: FloorplanCollection<AreaOfConcern>;
  planSensors: FloorplanCollection<PlanSensor>;
  sensorConnections: Map<PlanSensor['id'], SensorConnection>;
  planSensorCoverageIntersectionVectors: Map<
    PlanSensor['id'],
    'empty' | 'loading' | SensorCoverageIntersectionVectors
  >;
  lastSensorIndices?: {
    lastOASensorIndex?: number;
    lastEntrySensorIndex?: number;
  };
  references: FloorplanCollection<Reference>;
  thresholds: FloorplanCollection<Threshold>;
  photoGroups: FloorplanCollection<PhotoGroup>;
  photoGroupIdsToDelete: Array<PhotoGroup['id']>;
  validations: Map<
    string,
    'empty' | 'loading' | Array<SpaceValidation | SensorValidation>
  >;
  duplicateSensorParams: { rotation: number; height: number } | null;
  duplicateSpaceBoxParams: {
    name: string;
    width: number;
    height: number;
    countingMode: Space['countingMode'];
  } | null;
  duplicateSpaceCircleParams: {
    name: string;
    radius: number;
    countingMode: 'oa';
  } | null;
  duplicateSpacePolygonParams: {
    name: string;
    vertices: Array<FloorplanCoordinates>;
    originalPosition: FloorplanCoordinates;
    countingMode: Space['countingMode'];
  } | null;
  duplicateAreaOfConcernParams: {
    vertices: Array<FloorplanCoordinates>;
    originalPosition: FloorplanCoordinates;
    name: string;
  } | null;
  objectListType:
    | 'sensor'
    | 'areaofconcern'
    | 'space'
    | 'threshold'
    | 'reference'
    | 'photogroup'
    | 'layer';
  placementMode: null | PlacementMode;
  highlightedObject: null | {
    type:
      | 'sensor'
      | 'areaofconcern'
      | 'space'
      | 'threshold'
      | 'photogroup'
      | 'reference'
      | 'layer';
    id: string;
  };
  focusedObject: null | {
    type: 'sensor' | 'areaofconcern' | 'space' | 'layer' | 'threshold';
    id: string;
  };
  focusedObjectLink: null | string;
  // The focused photo group is kept track of seperately because a photo group can be focused at the
  // same time as a sensor / space
  focusedPhotoGroupId: null | string;
  spaceOccupancy: ReadonlyMap<
    Space['id'],
    {
      occupied: boolean;
      confidence: number;
      dwellTime: number;
      lastUpdate: number;
    }
  >;
  aggregatedPointsData: AggregatedData;
  aggregatedTracksData: Array<FloorplanTargetInfo>;
  planning: Planning;
  cachedPlanningState: Planning | null;
  measurement?: Measurement;
  displayUnit: LengthUnit;
  entityPasteMode: {
    mode: PasteChoice;
    affix?: 'post' | 'pre';
    labelMode?: 'append' | 'replace';
    amount?: number;
  };
  tracksStartDate: string | null;
  tracksEndDate: string | null;
  tracksCount: number;
  // The below is duplicating `PlanState.status` but there doesn't seem to be a great way of
  // accessing that state in the Editor component
  savePending: boolean;
  lastSavedAt: string;
  boundingBoxFilter: 'none';

  scaleEdit:
    | { status: 'inactive' }
    | { status: 'fetching_image_upload_url' }
    | {
        status: 'uploading';
        fileUploadPercent: number;
      }
    | {
        status: 'measuring';
        floorplanImage: HTMLImageElement;
        floorplan: Floorplan;
        objectKey?: string;
      }
    | {
        status: 'image_registration';
        floorplanImage: HTMLImageElement;
        floorplan: Floorplan;
        objectKey?: string;
        loading: boolean;
      };

  latestDXF:
    | { status: 'uploading'; fileUploadPercent?: number }
    | { status: 'upload_error' }
    | {
        status: PlanDXF['status'];
        id: PlanDXF['id'];
        createdAt: PlanDXF['created_at'];
      }
    | null;
  latestDXFEdit:
    | { active: false }
    | {
        active: true;
        planDXF: ParsedPlanDXF;
        cadFileUnit: LengthUnit;
        cadFileScale: number;
        pixelsPerCADUnit: number;
        floorplanCADOrigin: FloorplanCoordinates;
        loading: boolean;
      };
  activeDXFId: PlanDXF['id'] | null;
  activeDXFFullRasterUrl: PlanDXFAsset['object_url'] | null;
  csvImport:
    | {
        active: false;
        uploading: boolean;
        mutations: null;
        csvUpdates: null;
        csvErrors: null;
      }
    | {
        active: true;
        uploading: boolean;
        mutations: {
          forwards: Array<CSVImportMutation>;
          backwards: Array<CSVImportMutation>;
        };
        csvUpdates: Array<CSVImportUpdate>;
        csvErrors: Array<CSVImportError>;
      };
  spaceMetaCSVImport:
    | {
        active: false;
        uploading: boolean;
        spaceMetaCsvUpdates: null;
        spaceMetaCsvErrors: null;
      }
    | {
        active: true;
        uploading: boolean;
        spaceMetaCsvUpdates: Array<SpaceMetaCSVImportUpdate>;
        spaceMetaCsvErrors: Array<SpaceMetaCSVImportError>;
      };
  activeExport:
    | { status: 'requesting' }
    | { status: 'request-failed' }
    | PlanDXFExport
    | { status: 'saving' }
    | { status: 'saving-complete' }
    | { status: 'saving-failed' }
    | null;

  heightMapImport:
    | { view: 'disabled' }
    | { view: 'uploading-image'; fileName: string; fileUploadPercent?: number }
    | ({ view: 'ready' } & HeightMap)
    | ({ view: 'enabled' } & HeightMap);

  wallsEdit:
    | { active: false }
    | {
        active: true;
        imageLineSegmentImport:
          | { active: false }
          | {
              active: true;
              strokeColors: Array<string>;
              layers: Array<string>;
              lineSegments: Array<{
                id: string;
                strokeColors: Array<string>;
                layers: Array<string>;
                positionA: ImageCoordinates;
                positionB: ImageCoordinates;
              }>;
              imageWidth: number;
              imageHeight: number;
            };
        walls: FloorplanCollection<WallSegment>;
      };

  heatmap: { enabled: false } | ({ enabled: true } & Heatmap);
  selectionMode:
    | {
        active: true;
        expectedSelectionType:
          | 'sensor'
          | 'areaofconcern'
          | 'space'
          | 'threshold';
        pendingAction: SelectionModeAction;
        initialActionCreator?: string;
      }
    | {
        active: false;
      };
  bulkSelection: {
    ids: Set<string>;
    action: null | 'move' | 'select' | 'editSpaces';
  };
  keys: {
    ctrl: boolean;
    ctrlShift: boolean;
  };
  copiedEntitySettings?: {
    id: string;
    type: PastableEntity | '';
    sensor?: CopiedSensor;
    space?: CopiedSpace;
  };
  misc: {
    showWallImportReminder: boolean;
  };
};

export type BulkActionMode = {
  type: 'move';
  originPosition: FloorplanCoordinates | null;
  targetPosition: FloorplanCoordinates | null;
  mousePosition: FloorplanCoordinates | null;
  nextPlacement: FloorplanCoordinates | null;
};

export namespace State {
  export function getInitialState(
    floorplan: Floorplan,
    floorId: string,
    buildingId: string,
    viewport: Viewport,
    floorplanImage: HTMLImageElement | null,
    floorplanImageKey: string | null,
    measurement?: Measurement,
    lastSensorIndices?: LastSensorIndices
  ): State {
    return {
      planMetadata: {
        floorId,
        buildingId,
      },
      panels: {
        settingsOpen: false,
        csvOpen: false,
        dxfOpen: false,
      },
      locked: false,
      uuidMap: new Map<string, string>(),
      defaultSensorType: {
        openArea: 'oaMidRange',
        entry: 'tofEntry',
        waffle: 'waffle',
      },
      defaultOAHardware: 'oa1b',
      defaultEntryHardware: 's5b',
      syncHeightsButtonEnabled: true,
      unsavedModifications: false,
      undoStack: UndoStack.create(),
      renderOrder: 'forwards',
      floorplan,
      floorplanImage,
      floorplanImageKey,
      floorplanCADOrigin: computeDefaultCADOrigin(floorplan),
      spaceHierarchyDataLoadingStatus: 'pending',
      heightMap: { enabled: false },
      heightMapUpdated: false,
      walls: FloorplanCollection.create(),
      wallsFullyPopulated: false,
      viewport,
      objectListType: 'sensor',
      duplicateSensorParams: null,
      duplicateSpaceBoxParams: null,
      duplicateSpaceCircleParams: null,
      duplicateSpacePolygonParams: null,
      duplicateAreaOfConcernParams: null,
      planSensors: FloorplanCollection.create(),
      sensorConnections: new Map(),
      planSensorCoverageIntersectionVectors: new Map(),
      lastSensorIndices,
      spaces: FloorplanCollection.create(),
      areasOfConcern: FloorplanCollection.create(),
      references: FloorplanCollection.create(),
      thresholds: FloorplanCollection.create(),
      photoGroups: FloorplanCollection.create(),
      photoGroupIdsToDelete: [],
      validations: new Map(),
      placementMode: null,
      highlightedObject: null,
      focusedObject: null,
      focusedObjectLink: null,
      focusedPhotoGroupId: null,
      spaceOccupancy: new Map(),
      aggregatedPointsData: [],
      aggregatedTracksData: [],
      planning: {
        showOriginMarker: true,
        showSensors: true,
        showSensorNoisePolygons: false,
        showSensorTracks: false,
        showSensorsOpenArea: true,
        showSensorsOpenAreaLR: true,
        showSensorsOpenAreaMR: true,
        showSensorsOpenAreaSR: true,
        showOpenAreaLabels: true,
        showOpenAreaCoverage: true,
        showOpenAreaCoverageExtents: false,
        showOpenAreaDoorFOV: false,
        showLiveStreamOpenArea: false,
        showLiveStreamOpenAreaTracks: false,
        showLiveStreamOpenAreaPoints: false,
        showLiveStreamEntry: false,
        showLiveStreamEntryCounts: false,
        showLiveStreamEntryDashboard: false,
        showLiveStreamEntryTracks: false,
        showLiveStreamEntryPoints: false,
        showSensorsEntry: true,
        showSensorsEntryLR: true,
        showSensorsEntryTOF: true,
        showEntryCoverage: true,
        showEntryLabels: true,
        showSensorsWaffle: true,
        showFloorplanImage: true,
        showSpaces: true,
        showSpacesEntry: true,
        showSpacesOpenArea: true,
        showSpaceNames: false,
        showThresholds: true,
        showIngressEgressRegions: true,
        showTriggerRegions: true,
        showAreasOfConcern: true,
        showRulers: true,
        showHeights: true,
        showScale: false,
        showPhotoGroups: true,
        showCeilingHeightMap: false,
        showWalls: true,
        showHeatMap: false,
        showSpacesWaffle: false,
      },
      cachedPlanningState: null,
      measurement,
      entityPasteMode: {
        mode: PasteChoice.ROTATION,
        affix: 'post',
        labelMode: 'replace',
        amount: 1,
      },
      displayUnit: 'feet_and_inches',
      savePending: false,
      lastSavedAt: moment.utc().format(),
      tracksStartDate: null,
      tracksEndDate: null,
      tracksCount: 10,
      boundingBoxFilter: 'none',
      scaleEdit: { status: 'inactive' },
      csvImport: {
        active: false,
        uploading: false,
        mutations: null,
        csvUpdates: null,
        csvErrors: null,
      },
      spaceMetaCSVImport: {
        active: false,
        uploading: false,
        spaceMetaCsvUpdates: null,
        spaceMetaCsvErrors: null,
      },
      latestDXF: null,
      latestDXFEdit: { active: false },
      activeDXFId: null,
      activeDXFFullRasterUrl: null,
      activeExport: null,
      heightMapImport: { view: 'disabled' },
      wallsEdit: { active: false },
      heatmap: { enabled: false },
      selectionMode: { active: false },
      bulkSelection: {
        ids: new Set<string>(),
        action: null,
      },
      keys: {
        ctrl: false,
        ctrlShift: false,
      },
      copiedEntitySettings: { id: '', type: '' },
      misc: { showWallImportReminder: true },
    };
  }

  export function setViewport(state: State, viewport: Viewport): State {
    return {
      ...state,
      viewport,
    };
  }

  export function updateViewport(
    state: State,
    update: Partial<Viewport>
  ): State {
    return {
      ...state,
      viewport: {
        ...state.viewport,
        ...update,
      },
    };
  }

  export function unfocusLinkObject(state: State): State {
    return {
      ...state,
      focusedObjectLink: null,
    };
  }

  export function focusLayer(state: State, layerId: LayerId): State {
    return {
      ...state,
      objectListType: 'layer',
      focusedObject: {
        type: 'layer',
        id: layerId,
      },
    };
  }

  export function focusThreshold(
    state: State,
    thresholdId: Threshold['id']
  ): State {
    return {
      ...state,
      objectListType: 'threshold',
      focusedObject: {
        type: 'threshold',
        id: thresholdId,
      },
    };
  }

  export function isThresholdFocused(
    state: State,
    thresholdId: Threshold['id']
  ) {
    return Boolean(
      state.focusedObject &&
        state.focusedObject.type === 'threshold' &&
        state.focusedObject.id === thresholdId
    );
  }

  export function highlightThreshold(
    state: State,
    thresholdId: Threshold['id']
  ): State {
    return {
      ...state,
      objectListType: 'threshold',
      highlightedObject: {
        type: 'threshold',
        id: thresholdId,
      },
    };
  }

  export function isThresholdHighlighted(
    state: State,
    thresholdId: Threshold['id']
  ) {
    return Boolean(
      state.highlightedObject &&
        state.highlightedObject.type === 'threshold' &&
        state.highlightedObject.id === thresholdId
    );
  }

  export function isLayerFocused(state: State, layerId: LayerId) {
    return Boolean(
      state.focusedObject &&
        state.focusedObject.type === 'layer' &&
        state.focusedObject.id === layerId
    );
  }

  export function highlightLayer(state: State, layerId: LayerId): State {
    return {
      ...state,
      objectListType: 'layer',
      highlightedObject: {
        type: 'layer',
        id: layerId,
      },
    };
  }

  export function isLayerHighlighted(state: State, layerId: LayerId) {
    return Boolean(
      state.highlightedObject &&
        state.highlightedObject.type === 'layer' &&
        state.highlightedObject.id === layerId
    );
  }

  export function addAreaOfConcern(
    state: State,
    areaOfConcern: AreaOfConcern,
    addedWithUserInteraction = true
  ): State {
    return {
      ...state,
      areasOfConcern: FloorplanCollection.addItem(
        state.areasOfConcern,
        areaOfConcern
      ),
      focusedObject: addedWithUserInteraction
        ? {
            type: 'areaofconcern',
            id: areaOfConcern.id,
          }
        : state.focusedObject,
      unsavedModifications: addedWithUserInteraction,
    };
  }

  export function addSpace(
    state: State,
    space: Space,
    addedWithUserInteraction = true
  ): State {
    const nextSpaceOccupancy = new Map(state.spaceOccupancy);
    nextSpaceOccupancy.set(space.id, {
      occupied: false,
      confidence: 0,
      dwellTime: 0,
      lastUpdate: Seconds.fromMilliseconds(Date.now()),
    });

    // Put a placeholder value to tell downstream code that validations for this sensor need to
    // be computed
    const nextValidations = new Map(state.validations);
    nextValidations.set(space.id, 'empty');

    return {
      ...state,
      spaces: FloorplanCollection.addItem(state.spaces, space),
      focusedObject: addedWithUserInteraction
        ? {
            type: 'space',
            id: space.id,
          }
        : state.focusedObject,
      validations: nextValidations,
      spaceOccupancy: nextSpaceOccupancy,
      unsavedModifications: addedWithUserInteraction,
    };
  }

  export function lockSpace(
    state: State,
    id: Space['id'],
    locked: boolean
  ): State {
    const space = state.spaces.items.get(id);
    if (typeof space === 'undefined') {
      throw new Error(`Space with id ${id} not found`);
    }

    return {
      ...state,
      spaces: FloorplanCollection.updateItem(state.spaces, id, { locked }),
      unsavedModifications: true,
    };
  }

  export function updateSpace(
    state: State,
    id: Space['id'],
    update: Update<Space> | ((prev: Space) => Update<Space>),
    ignoreLocked = false
  ): State {
    const space = state.spaces.items.get(id);
    if (typeof space === 'undefined') {
      console.warn(`Cannot find space with id ${id}, skipping update...`);
      return state;
    }

    // disallow state updates for locked spaces
    if (!ignoreLocked && space.locked) {
      return state;
    }

    if (typeof update === 'function') {
      return updateSpace(state, id, update(space));
    }

    // Put a placeholder value to tell downstream code that validations for this sensor need to
    // be computed
    const nextValidations = new Map(state.validations);
    // NOTE: With the manual "Validate Spaces" button we dont want this.
    // Uncomment when a proper web worker is implemented...
    //nextValidations.set(space.id, 'empty');

    return {
      ...state,
      spaces: FloorplanCollection.updateItem(state.spaces, id, update),
      validations: nextValidations,
      unsavedModifications: true,
    };
  }

  export function changeSpaceId(
    state: State,
    oldId: Space['id'],
    newId: Space['id']
  ): State {
    // When a space id is saved to the server, migrate it's old uuid to a new server-generated
    // id.

    // 1. Change ids in the floorplan collection
    state = {
      ...state,
      spaces: FloorplanCollection.changeId(state.spaces, oldId, newId),
      // unsavedModifications: true, // TODO: I don't think this should be set?
    };

    // Update the focused / highlghted states
    if (State.isSpaceHighlighted(state, oldId)) {
      state = State.highlightItem(state, 'space', newId);
    }
    if (State.isSpaceFocused(state, oldId)) {
      state = State.focusSpace(state, newId);
    }

    // Move `spaceOccupancy` data from old id to new id
    const spaceOccupancyItemValue = state.spaceOccupancy.get(oldId);
    if (spaceOccupancyItemValue) {
      const nextSpaceOccupancy = new Map(state.spaceOccupancy);
      nextSpaceOccupancy.delete(oldId);
      nextSpaceOccupancy.set(newId, spaceOccupancyItemValue);
      state.spaceOccupancy = nextSpaceOccupancy;
    }

    // Move `velidations` data from old id to new id
    const sensorValidations = state.validations.get(oldId);
    if (sensorValidations) {
      const nextValidations = new Map(state.validations);
      nextValidations.delete(oldId);
      nextValidations.set(newId, sensorValidations);
      state.validations = nextValidations;
    }

    // Update undo list
    state = {
      ...state,
      undoStack: UndoStack.changeObjectId(state.undoStack, oldId, newId),
    };

    return state;
  }

  export function focusAreaOfConcern(
    state: State,
    id: AreaOfConcern['id']
  ): State {
    return {
      ...state,
      spaces: FloorplanCollection.sendToFront(state.spaces, id),
      objectListType: 'areaofconcern',
      focusedObject: {
        type: 'areaofconcern',
        id,
      },
    };
  }

  export function changeAreaOfConcernId(
    state: State,
    oldId: AreaOfConcern['id'],
    newId: AreaOfConcern['id']
  ): State {
    // When a areaOfConcern id is saved to the server, migrate it's old uuid to a new server-generated
    // id.

    // 1. Change ids in the floorplan collection
    state = {
      ...state,
      areasOfConcern: FloorplanCollection.changeId(
        state.areasOfConcern,
        oldId,
        newId
      ),
      // unsavedModifications: true, // TODO: I don't think this should be set?
    };

    // Update the focused / highlghted states
    if (State.isAreaOfConcernHighlighted(state, oldId)) {
      state = State.highlightItem(state, 'areaofconcern', newId);
    }
    if (State.isAreaOfConcernFocused(state, oldId)) {
      state = State.focusAreaOfConcern(state, newId);
    }

    // Update undo list
    state = {
      ...state,
      undoStack: UndoStack.changeObjectId(state.undoStack, oldId, newId),
    };

    return state;
  }

  export function updateAreaOfConcern(
    state: State,
    id: AreaOfConcern['id'],
    update:
      | Update<AreaOfConcern>
      | ((prev: AreaOfConcern) => Update<AreaOfConcern>),
    ignoreLocked = false
  ): State {
    const areaOfConcern = state.areasOfConcern.items.get(id);
    if (typeof areaOfConcern === 'undefined') {
      console.warn(
        `Cannot find area of concern with id ${id}, skipping update...`
      );
      return state;
    }

    // disallow state updates for locked area of concern
    if (!ignoreLocked && areaOfConcern.locked) {
      return state;
    }

    if (typeof update === 'function') {
      return updateAreaOfConcern(
        state,
        id,
        update(areaOfConcern),
        ignoreLocked
      );
    }

    return {
      ...state,
      areasOfConcern: FloorplanCollection.updateItem(
        state.areasOfConcern,
        id,
        update
      ),
      unsavedModifications: true,
    };
  }

  export function lockAreaOfConcern(
    state: State,
    id: AreaOfConcern['id'],
    locked: boolean
  ): State {
    const areasOfConcern = state.areasOfConcern.items.get(id);
    if (typeof areasOfConcern === 'undefined') {
      throw new Error(`area of concern with id ${id} not found`);
    }

    return {
      ...state,
      areasOfConcern: FloorplanCollection.updateItem(state.areasOfConcern, id, {
        locked,
      }),
      unsavedModifications: true,
    };
  }

  export function removeAreaOfConcern(
    state: State,
    id: AreaOfConcern['id'],
    ignoreLocked = false
  ): State {
    const areaOfConcern = state.areasOfConcern.items.get(id);
    if (typeof areaOfConcern === 'undefined') {
      console.warn(
        `Cannot find area of concern with id ${id}, skipping update...`
      );
      return state;
    }

    // disallow removal if locked
    if (areaOfConcern.locked) {
      return state;
    }

    return {
      ...state,
      areasOfConcern: FloorplanCollection.removeItem(state.areasOfConcern, id),
      focusedObject: null,
      unsavedModifications: true,
    };
  }

  export function highlightItem(
    state: State,
    itemType:
      | 'sensor'
      | 'areaofconcern'
      | 'space'
      | 'reference'
      | 'photogroup'
      | 'threshold',
    id: string
  ) {
    return {
      ...state,
      highlightedObject: {
        type: itemType,
        id,
      },
    };
  }

  export function highlightSpace(state: State, id: Space['id']): State {
    return {
      ...state,
      highlightedObject: {
        type: 'space',
        id,
      },
    };
  }

  export function focusSpace(state: State, id: Space['id']): State {
    return {
      ...state,
      spaces: FloorplanCollection.sendToFront(state.spaces, id),
      objectListType: 'space',
      focusedObject: {
        type: 'space',
        id,
      },
    };
  }

  function refreshDeskBenchNextDeskNumber(state: State): State {
    if (
      !state.placementMode ||
      state.placementMode.type !== 'space' ||
      state.placementMode.shape !== 'desk-bench'
    ) {
      return state;
    }

    return {
      ...state,
      placementMode: {
        ...state.placementMode,
        nextDeskNumber: Space.getNextGeneratedNameNumber(
          FloorplanCollection.list(state.spaces),
          'Desk'
        ),
      },
    };
  }

  export function removeSpace(
    state: State,
    id: Space['id'],
    ignoreLocked = false
  ): State {
    const space = state.spaces.items.get(id);
    if (typeof space === 'undefined') {
      console.warn(`Cannot find space with id ${id}, skipping update...`);
      return state;
    }

    // disallow removal if locked
    if (!ignoreLocked && space.locked) {
      return state;
    }

    const nextSpaceOccupancy = new Map(state.spaceOccupancy);
    nextSpaceOccupancy.delete(id);

    const nextValidations = new Map(state.validations);
    nextValidations.delete(id);

    return refreshDeskBenchNextDeskNumber({
      ...state,
      spaces: FloorplanCollection.removeItem(state.spaces, id),
      focusedObject: null,
      spaceOccupancy: nextSpaceOccupancy,
      validations: nextValidations,
      unsavedModifications: true,
    });
  }

  export function updateSensorIndices(
    state: State,
    indices: LastSensorIndices
  ): State {
    return {
      ...state,
      lastSensorIndices: indices,
    };
  }

  export function addSensor(
    state: State,
    sensor: PlanSensor,
    addedWithUserInteraction = true
  ): State {
    const sensorIsOA = sensor.type === 'oa';

    const nextOASensorIndex = state.lastSensorIndices?.lastOASensorIndex
      ? state.lastSensorIndices?.lastOASensorIndex + 1
      : 1;
    const nextEntrySensorIndex = state.lastSensorIndices?.lastEntrySensorIndex
      ? state.lastSensorIndices?.lastEntrySensorIndex + 1
      : 1;
    const planSensorIndex = addedWithUserInteraction
      ? sensorIsOA
        ? nextOASensorIndex
        : nextEntrySensorIndex
      : sensor.plan_sensor_index;
    sensor = {
      ...sensor,
      plan_sensor_index: planSensorIndex,
    };

    // Put a placeholder value to tell downstream code that coverage vectors for this sensor need to
    // be computed
    const nextSensorCoverageIntersectionVectors = new Map(
      state.planSensorCoverageIntersectionVectors
    );
    nextSensorCoverageIntersectionVectors.set(sensor.id, 'empty');

    // Put a placeholder value to tell downstream code that validations for this sensor need to
    // be computed
    const nextValidations = new Map(state.validations);
    nextValidations.set(sensor.id, 'empty');

    return {
      ...state,
      planSensorCoverageIntersectionVectors:
        nextSensorCoverageIntersectionVectors,
      validations: nextValidations,
      lastSensorIndices: addedWithUserInteraction
        ? {
            lastOASensorIndex: sensorIsOA
              ? (planSensorIndex as number | undefined)
              : state.lastSensorIndices?.lastOASensorIndex,
            lastEntrySensorIndex: !sensorIsOA
              ? (planSensorIndex as number | undefined)
              : state.lastSensorIndices?.lastEntrySensorIndex,
          }
        : state.lastSensorIndices,
      planSensors: FloorplanCollection.addItem(state.planSensors, sensor),
      focusedObject: addedWithUserInteraction
        ? {
            type: 'sensor',
            id: sensor.id,
          }
        : state.focusedObject,
      unsavedModifications: addedWithUserInteraction,
    };
  }

  export function removeSensor(
    state: State,
    id: PlanSensor['id'],
    ignoreLocked = false
  ): State {
    const sensor = state.planSensors.items.get(id);
    if (typeof sensor === 'undefined') {
      console.warn(`Cannot find sensor with id ${id}, skipping update...`);
      return state;
    }

    // disallow removal if locked
    if (!ignoreLocked && sensor.locked) {
      return state;
    }

    const nextSensorConnections = state.sensorConnections;
    nextSensorConnections.delete(id);

    const nextSensorCoverageIntersectionVectors = new Map(
      state.planSensorCoverageIntersectionVectors
    );
    nextSensorCoverageIntersectionVectors.delete(sensor.id);

    const nextValidations = new Map(state.validations);
    nextValidations.delete(sensor.id);

    return {
      ...state,
      planSensors: FloorplanCollection.removeItem(state.planSensors, id),
      sensorConnections: nextSensorConnections,
      planSensorCoverageIntersectionVectors:
        nextSensorCoverageIntersectionVectors,
      validations: nextValidations,
      focusedObject: null,
      unsavedModifications: true,
    };
  }

  export function highlightSensor(state: State, id: PlanSensor['id']): State {
    return {
      ...state,
      highlightedObject: {
        type: 'sensor',
        id,
      },
    };
  }

  export function focusSensor(state: State, id: PlanSensor['id']): State {
    return {
      ...state,
      planSensors: FloorplanCollection.sendToFront(state.planSensors, id),
      objectListType: 'sensor',
      focusedObject: {
        type: 'sensor',
        id,
      },
    };
  }

  export function lockSensor(
    state: State,
    id: PlanSensor['id'],
    locked: boolean
  ): State {
    const sensor = state.planSensors.items.get(id);
    if (typeof sensor === 'undefined') {
      throw new Error(`Sensor with id ${id} not found`);
    }

    return {
      ...state,
      planSensors: FloorplanCollection.updateItem(state.planSensors, id, {
        locked,
      }),
      unsavedModifications: true,
    };
  }

  export function updateSensor(
    state: State,
    id: PlanSensor['id'],
    update: Update<PlanSensor> | ((prev: PlanSensor) => Update<PlanSensor>),
    ignoreLocked = false
  ): State {
    const sensor = state.planSensors.items.get(id);
    if (typeof sensor === 'undefined') {
      console.warn(`Cannot find sensor with id ${id}, skipping update...`);
      return state;
    }

    // disallow state updates for locked sensors
    if (!ignoreLocked && sensor.locked) {
      return state;
    }

    if (typeof update === 'function') {
      return updateSensor(state, id, update(sensor), ignoreLocked);
    }

    // If any positional attributes about the sensor change, then recompute the sensor coverage
    // vectors and validations
    let nextSensorCoverageIntersectionVectors =
      state.planSensorCoverageIntersectionVectors;
    let nextValidations = state.validations;
    if (
      typeof update.height !== 'undefined' ||
      typeof update.rotation !== 'undefined' ||
      typeof update.position !== 'undefined'
    ) {
      nextSensorCoverageIntersectionVectors = new Map(
        state.planSensorCoverageIntersectionVectors
      );
      nextSensorCoverageIntersectionVectors.set(sensor.id, 'empty');

      nextValidations = new Map(state.validations);
      nextValidations.set(sensor.id, 'empty');
    }

    return {
      ...state,
      planSensors: FloorplanCollection.updateItem(
        state.planSensors,
        id,
        update
      ),
      planSensorCoverageIntersectionVectors:
        nextSensorCoverageIntersectionVectors,
      validations: nextValidations,
      unsavedModifications: true,
    };
  }

  export function updateOrCreateSensorConnection(
    state: State,
    id: PlanSensor['id'],
    update: SensorConnection
  ): State {
    const sensor = state.planSensors.items.get(id);
    if (typeof sensor === 'undefined') {
      console.warn(`Cannot find sensor with id ${id}, skipping update...`);
      return state;
    }

    const nextSensorConnections = new Map(state.sensorConnections);
    nextSensorConnections.set(id, update);

    return {
      ...state,
      sensorConnections: nextSensorConnections,
      unsavedModifications: true,
    };
  }

  export function changeSensorId(
    state: State,
    oldId: PlanSensor['id'],
    newId: PlanSensor['id']
  ): State {
    // When a sensor id is saved to the server, migrate it's old uuid to a new server-generated
    // id.

    // 1. Change ids in the floorplan collection
    state = {
      ...state,
      planSensors: FloorplanCollection.changeId(
        state.planSensors,
        oldId,
        newId
      ),
      // unsavedModifications: true, // TODO: I don't think this should be set?
    };

    // Update the focused / highlghted states
    if (State.isSensorHighlighted(state, oldId)) {
      state = State.highlightItem(state, 'sensor', newId);
    }
    if (State.isSensorFocused(state, oldId)) {
      state = State.focusSensor(state, newId);
    }

    // Move `sensorCoverageIntersectionVectors` data from old id to new id
    const sensorCoverageIntersectionVectorsItemValue =
      state.planSensorCoverageIntersectionVectors.get(oldId);
    if (sensorCoverageIntersectionVectorsItemValue) {
      const nextSensorCoverageIntersectionVectors = new Map(
        state.planSensorCoverageIntersectionVectors
      );
      nextSensorCoverageIntersectionVectors.delete(oldId);
      nextSensorCoverageIntersectionVectors.set(
        newId,
        sensorCoverageIntersectionVectorsItemValue
      );
      state.planSensorCoverageIntersectionVectors =
        nextSensorCoverageIntersectionVectors;
    }

    // Move `velidations` data from old id to new id
    const sensorValidations = state.validations.get(oldId);
    if (sensorValidations) {
      const nextValidations = new Map(state.validations);
      nextValidations.delete(oldId);
      nextValidations.set(newId, sensorValidations);
      state.validations = nextValidations;
    }

    // Update undo list
    state = {
      ...state,
      undoStack: UndoStack.changeObjectId(state.undoStack, oldId, newId),
    };

    return state;
  }

  export function addThreshold(
    state: State,
    threshold: Threshold,
    addedWithUserInteraction = true
  ): State {
    return {
      ...state,
      thresholds: FloorplanCollection.addItem(state.thresholds, threshold),
      unsavedModifications: addedWithUserInteraction,
    };
  }

  export function removeThreshold(
    state: State,
    id: Threshold['id'],
    ignoreLocked = false
  ): State {
    const threshold = state.thresholds.items.get(id);
    if (typeof threshold === 'undefined') {
      console.warn(`Cannot find threshold with id ${id}, skipping update...`);
      return state;
    }

    // disallow removal if locked
    if (!ignoreLocked && threshold.locked) {
      return state;
    }

    return {
      ...state,
      thresholds: FloorplanCollection.removeItem(state.thresholds, id),
      unsavedModifications: true,
    };
  }

  export function changeThresholdDoorSwing(
    state: State,
    id: Threshold['id'],
    swingsIntoFOV: boolean
  ): State {
    const threshold = state.thresholds.items.get(id);
    if (typeof threshold === 'undefined') {
      throw new Error(`Threshold with id ${id} not found`);
    }

    return {
      ...state,
      thresholds: FloorplanCollection.updateItem(state.thresholds, id, {
        swingsIntoFOV,
      }),
      unsavedModifications: true,
    };
  }

  export function lockThreshold(
    state: State,
    id: Threshold['id'],
    locked: boolean
  ): State {
    const threshold = state.thresholds.items.get(id);
    if (typeof threshold === 'undefined') {
      throw new Error(`Threshold with id ${id} not found`);
    }

    return {
      ...state,
      thresholds: FloorplanCollection.updateItem(state.thresholds, id, {
        locked,
      }),
      unsavedModifications: true,
    };
  }

  export function updateThreshold(
    state: State,
    id: Threshold['id'],
    update: Update<Threshold> | ((prev: Threshold) => Update<Threshold>)
  ): State {
    const threshold = state.thresholds.items.get(id);

    if (typeof threshold === 'undefined') {
      console.warn(`Cannot find threshold with id ${id}, skipping update...`);
      return state;
    }

    if (typeof update === 'function') {
      return updateThreshold(state, id, update(threshold));
    }

    return {
      ...state,
      thresholds: FloorplanCollection.updateItem(state.thresholds, id, update),
      unsavedModifications: true,
    };
  }

  export function changeThresholdId(
    state: State,
    oldId: Threshold['id'],
    newId: Threshold['id']
  ): State {
    // When a threshold id is saved to the server, migrate it's old uuid to a new server-generated
    // id.

    // 1. Change ids in the floorplan collection
    state = {
      ...state,
      thresholds: FloorplanCollection.changeId(state.thresholds, oldId, newId),
      // unsavedModifications: true, // TODO: I don't think this should be set?
    };

    // Update the highlghted states
    if (State.isThresholdHighlighted(state, oldId)) {
      state = State.highlightItem(state, 'threshold', newId);
    }

    // Update undo list
    state = {
      ...state,
      undoStack: UndoStack.changeObjectId(state.undoStack, oldId, newId),
    };

    return state;
  }

  export function addReference(
    state: State,
    reference: Reference,
    addedWithUserInteraction = true
  ): State {
    return {
      ...state,
      references: FloorplanCollection.addItem(state.references, reference),
      unsavedModifications: addedWithUserInteraction,
    };
  }

  export function removeReference(state: State, id: Reference['id']): State {
    return {
      ...state,
      references: FloorplanCollection.removeItem(state.references, id),
      unsavedModifications: true,
    };
  }

  export function updateReference<T extends ReferenceRuler | ReferenceHeight>(
    state: State,
    id: Reference['id'],
    update: Update<T> | ((prev: T) => Update<T>)
  ): State {
    if (typeof update === 'function') {
      const reference = state.references.items.get(id) as T;

      if (typeof reference === 'undefined') {
        console.warn(`Cannot find reference with id ${id}, skipping update...`);
        return state;
      }

      return {
        ...state,
        unsavedModifications: true,
        references: FloorplanCollection.updateItem(
          state.references,
          id,
          update(reference)
        ),
      };
    }

    return {
      ...state,
      references: FloorplanCollection.updateItem(state.references, id, update),
      unsavedModifications: true,
    };
  }

  export function changeReferenceId(
    state: State,
    oldId: Reference['id'],
    newId: Reference['id']
  ): State {
    // When a reference id is saved to the server, migrate it's old uuid to a new server-generated
    // id.

    // 1. Change ids in the floorplan collection
    state = {
      ...state,
      references: FloorplanCollection.changeId(state.references, oldId, newId),
      // unsavedModifications: true, // TODO: I don't think this should be set?
    };

    // Update the highlghted states
    if (State.isReferenceHighlighted(state, oldId)) {
      state = State.highlightItem(state, 'reference', newId);
    }
    // NOTE: references cannot be focused

    // Update undo list
    state = {
      ...state,
      undoStack: UndoStack.changeObjectId(state.undoStack, oldId, newId),
    };

    return state;
  }

  export function addPhotoGroup(
    state: State,
    photoGroup: PhotoGroup,
    addedWithUserInteraction = true
  ): State {
    return {
      ...state,
      photoGroups: FloorplanCollection.addItem(state.photoGroups, photoGroup),
      unsavedModifications: addedWithUserInteraction,
    };
  }

  export function focusPhotoGroup(state: State, id: PhotoGroup['id']): State {
    return {
      ...state,
      photoGroups: FloorplanCollection.sendToFront(state.photoGroups, id),
      objectListType: 'photogroup',
      focusedPhotoGroupId: id,
    };
  }

  export function removePhotoGroup(
    state: State,
    id: PhotoGroup['id'],
    ignoreLocked = false
  ): State {
    const photoGroup = state.photoGroups.items.get(id);
    if (typeof photoGroup === 'undefined') {
      console.warn(`Cannot find photo group with id ${id}, skipping delete...`);
      return state;
    }

    // disallow removal if locked
    if (!ignoreLocked && photoGroup.locked) {
      return state;
    }

    let photoGroupIdsToDelete = state.photoGroupIdsToDelete;
    if (isFloorplanPhotoGroupId(id)) {
      photoGroupIdsToDelete = [...photoGroupIdsToDelete, id];
    }

    return {
      ...state,
      photoGroups: FloorplanCollection.removeItem(state.photoGroups, id),
      photoGroupIdsToDelete,
      focusedPhotoGroupId: null,
      unsavedModifications: true,
    };
  }

  export function updatePhotoGroup(
    state: State,
    id: Reference['id'],
    update: Update<PhotoGroup> | ((prev: PhotoGroup) => Update<PhotoGroup>),
    ignoreLocked = false
  ): State {
    const photoGroup = state.photoGroups.items.get(id) as PhotoGroup;

    // disallow state updates for locked photo groups
    if (!ignoreLocked && photoGroup.locked) {
      return state;
    }

    if (typeof update === 'function') {
      if (typeof photoGroup === 'undefined') {
        console.warn(
          `Cannot find photo group with id ${id}, skipping update...`
        );
        return state;
      }
      return {
        ...state,
        photoGroups: FloorplanCollection.updateItem(
          state.photoGroups,
          id,
          update(photoGroup)
        ),
        unsavedModifications: true,
      };
    }
    return {
      ...state,
      photoGroups: FloorplanCollection.updateItem(
        state.photoGroups,
        id,
        update
      ),
      unsavedModifications: true,
    };
  }

  export function lockPhotoGroup(state: State, id: PhotoGroup['id']): State {
    return State.updatePhotoGroup(
      state,
      id,
      (photoGroup) => PhotoGroup.lock(photoGroup),
      true
    );
  }

  export function unlockPhotoGroup(state: State, id: PhotoGroup['id']): State {
    return State.updatePhotoGroup(
      state,
      id,
      (photoGroup) => PhotoGroup.unlock(photoGroup),
      true
    );
  }

  export function appendPhotoToPhotoGroup(
    state: State,
    id: PhotoGroup['id'],
    fileName: string,
    photoDataUrl: string,
    uploadedPhotoId: string
  ): State {
    return State.updatePhotoGroup(
      state,
      id,
      (photoGroup) => {
        const photo = PhotoGroupPhoto.createFromUploadedPhotoId(
          uploadedPhotoId,
          fileName,
          photoDataUrl
        );
        return PhotoGroup.appendPhoto(photoGroup, photo);
      },
      true
    );
  }
  export function removePhotoFromPhotoGroup(
    state: State,
    id: PhotoGroup['id'],
    photoId: PhotoGroupPhoto['id']
  ): State {
    return State.updatePhotoGroup(
      state,
      id,
      (photoGroup) => {
        return PhotoGroup.removePhoto(photoGroup, photoId);
      },
      true
    );
  }
  export function changePhotoNameInPhotoGroup(
    state: State,
    id: PhotoGroup['id'],
    photoId: PhotoGroupPhoto['id'],
    name: PhotoGroupPhoto['name']
  ): State {
    return State.updatePhotoGroup(
      state,
      id,
      (photoGroup) => {
        const existingPhoto = PhotoGroup.getPhotoById(photoGroup, photoId);
        if (!existingPhoto) {
          return photoGroup;
        }

        const updatedPhoto = PhotoGroupPhoto.updateName(existingPhoto, name);

        return {
          ...photoGroup,
          photos: photoGroup.photos.map((photo) =>
            photo.id === updatedPhoto.id ? updatedPhoto : photo
          ),
        };
      },
      true
    );
  }
  export function reorderPhotosInPhotoGroup(
    state: State,
    id: PhotoGroup['id'],
    oldIndex: number,
    newIndex: number
  ): State {
    return State.updatePhotoGroup(
      state,
      id,
      (photoGroup) => {
        return {
          ...photoGroup,
          photos: arrayMove(photoGroup.photos, oldIndex, newIndex),
        };
      },
      true
    );
  }

  export function changePhotoGroupId(
    state: State,
    oldId: PhotoGroup['id'],
    newId: PhotoGroup['id']
  ): State {
    // When a photo group is saved to the server, migrate it's old uuid to a new server-generated
    // id.

    // 1. Change ids in the floorplan collection
    state = {
      ...state,
      photoGroups: FloorplanCollection.changeId(
        state.photoGroups,
        oldId,
        newId
      ),
      unsavedModifications: true,
    };

    // Update the focused / highlghted states
    if (State.isPhotoGroupHighlighted(state, oldId)) {
      state = State.highlightItem(state, 'photogroup', newId);
    }
    if (State.isPhotoGroupFocused(state, oldId)) {
      state = State.focusPhotoGroup(state, newId);
    }

    // Update undo list
    state = {
      ...state,
      undoStack: UndoStack.changeObjectId(state.undoStack, oldId, newId),
    };

    return state;
  }

  export function changePhotoGroupPhotoId(
    state: State,
    photoGroupId: PhotoGroup['id'],
    oldId: PhotoGroupPhoto['id'],
    newId: PhotoGroupPhoto['id']
  ): State {
    // When a photo group photo is saved to the server, migrate it's old uuid to a new
    // server-generated id.

    // 1. Change ids in the photoGroups floorplan collection
    state = State.updatePhotoGroup(
      state,
      photoGroupId,
      (photoGroup) => {
        const existingPhoto = PhotoGroup.getPhotoById(photoGroup, oldId);
        if (!existingPhoto) {
          return photoGroup;
        }

        return {
          ...photoGroup,
          photos: photoGroup.photos.map((photo) =>
            photo.id === oldId ? { ...photo, id: newId } : photo
          ),
        };
      },
      true
    );

    // 2. Update undo list
    state = {
      ...state,
      undoStack: UndoStack.changeObjectId(state.undoStack, oldId, newId),
    };

    return state;
  }

  export function resetAllPhotoGroupOperationAfterSave(state: State): State {
    // Loop through every photo group and reset the operation
    const nextPhotoGroupsItems = new Map(state.photoGroups.items);
    Array.from(nextPhotoGroupsItems.entries()).forEach(([id, photoGroup]) => {
      nextPhotoGroupsItems.set(
        id,
        PhotoGroup.resetOperationAfterSave(photoGroup)
      );
    });

    return {
      ...state,
      photoGroups: {
        ...state.photoGroups,
        items: nextPhotoGroupsItems,
      },
    };
  }

  export function updatePhotoGroupPhotoImageUrl(
    state: State,
    photoGroupPhotoUrls: { [photoId: string]: string }
  ): State {
    // Ensure that photos within photo groups have the most up to date "image.url" property and
    // "image.dirty" is set to false once the plan is saved and the most up to date image urls are
    // available
    const newPhotoGroupItems = new Map(state.photoGroups.items);
    FloorplanCollection.list(state.photoGroups).forEach((photoGroup) => {
      photoGroup.photos.forEach((photo, photoIndex) => {
        const newPhotoUrl = photoGroupPhotoUrls[photo.id];
        if (newPhotoUrl) {
          // Update the url of the photo within the photo group without mutating the state
          const newPhotos = [...photoGroup.photos];
          newPhotos[photoIndex] = {
            ...photo,
            image: { dirty: false, url: newPhotoUrl },
          };
          const newPhotoGroup = {
            ...photoGroup,
            photos: newPhotos,
          };
          newPhotoGroupItems.set(photoGroup.id, newPhotoGroup);
        }
      });
    });

    return {
      ...state,
      photoGroups: {
        ...state.photoGroups,
        items: newPhotoGroupItems,
      },
    };
  }

  export function unhighlight(state: State): State {
    return {
      ...state,
      highlightedObject: null,
    };
  }

  export function blurFocusedObject(state: State): State {
    return {
      ...state,
      focusedObject: null,
    };
  }

  export function blurPhotoGroup(state: State): State {
    return {
      ...state,
      focusedPhotoGroupId: null,
    };
  }

  export function blurAll(
    state: State,
    clickCoordinates: ViewportCoordinates,
    ctrlKey: boolean | undefined
  ): State {
    return {
      ...state,
      focusedObject: null,
      focusedPhotoGroupId: null,
      bulkSelection: {
        ...state.bulkSelection,
        ids: ctrlKey ? state.bulkSelection.ids : new Set<string>(),
        action: null,
      },
    };
  }

  export function isAreaOfConcernHighlighted(
    state: State,
    areaOfConcernId: AreaOfConcern['id']
  ) {
    return Boolean(
      state.highlightedObject &&
        state.highlightedObject.type === 'areaofconcern' &&
        state.highlightedObject.id === areaOfConcernId
    );
  }

  export function isAreaOfConcernFocused(
    state: State,
    areaOfConcernId: AreaOfConcern['id']
  ) {
    return Boolean(
      state.focusedObject &&
        state.focusedObject.type === 'areaofconcern' &&
        state.focusedObject.id === areaOfConcernId
    );
  }

  export function isSpaceHighlighted(state: State, spaceId: Space['id']) {
    return Boolean(
      state.highlightedObject &&
        state.highlightedObject.type === 'space' &&
        state.highlightedObject.id === spaceId
    );
  }

  export function isSpaceFocused(state: State, spaceId: Space['id']) {
    return Boolean(
      state.focusedObject &&
        state.focusedObject.type === 'space' &&
        state.focusedObject.id === spaceId
    );
  }

  export function isSensorHighlighted(
    state: State,
    sensorId: PlanSensor['id']
  ) {
    return Boolean(
      state.highlightedObject &&
        state.highlightedObject.type === 'sensor' &&
        state.highlightedObject.id === sensorId
    );
  }

  export function isSensorFocused(state: State, sensorId: PlanSensor['id']) {
    return Boolean(
      state.focusedObject &&
        state.focusedObject.type === 'sensor' &&
        state.focusedObject.id === sensorId
    );
  }

  export function isPhotoGroupHighlighted(
    state: State,
    photoGroupId: PhotoGroup['id']
  ) {
    return Boolean(
      state.highlightedObject &&
        state.highlightedObject.type === 'photogroup' &&
        state.highlightedObject.id === photoGroupId
    );
  }

  export function isPhotoGroupFocused(
    state: State,
    photoGroupId: PhotoGroup['id']
  ) {
    return state.focusedPhotoGroupId === photoGroupId;
  }

  export function getFocusedPhotoGroup(state: State) {
    if (!state.focusedPhotoGroupId) {
      return null;
    }
    return state.photoGroups.items.get(state.focusedPhotoGroupId) || null;
  }

  export function isReferenceEnabled(
    state: State,
    referenceId: Reference['id']
  ) {
    const reference = state.references.items.get(referenceId);
    if (!reference) {
      return false;
    }

    // Height references are disabled when there is not a height map uploaded
    if (reference.type === 'height' && !state.heightMap.enabled) {
      return false;
    }

    return reference.enabled;
  }

  export function isReferenceHighlighted(
    state: State,
    referenceId: Reference['id']
  ) {
    return Boolean(
      state.highlightedObject &&
        state.highlightedObject.type === 'reference' &&
        state.highlightedObject.id === referenceId
    );
  }

  export function getFocusedSensor(state: State) {
    return (
      (state.focusedObject && state.focusedObject.type === 'sensor'
        ? state.planSensors.items.get(state.focusedObject?.id)
        : null) || null
    );
  }

  export function getFocusedAreaOfConcern(state: State) {
    return (
      (state.focusedObject && state.focusedObject.type === 'areaofconcern'
        ? state.areasOfConcern.items.get(state.focusedObject?.id)
        : null) || null
    );
  }

  export function getFocusedThreshold(state: State) {
    return (
      (state.focusedObject && state.focusedObject.type === 'threshold'
        ? state.thresholds.items.get(state.focusedObject?.id)
        : null) || null
    );
  }

  export function getFocusedSpace(state: State) {
    return (
      (state.focusedObject && state.focusedObject.type === 'space'
        ? state.spaces.items.get(state.focusedObject?.id)
        : null) || null
    );
  }

  export function isCaptureAllowed(state: State): boolean {
    // make sure at least one of the sensors has a serial number
    return Array.from(state.planSensors.items.values()).some(
      (sensor) => sensor.serialNumber
    );
  }

  export function addWallSegments(
    state: State,
    wallSegments: Array<WallSegment>,
    addedWithUserInteraction = true
  ): State {
    let nextWalls = state.walls;
    for (const wallSegment of wallSegments) {
      nextWalls = FloorplanCollection.addItem(nextWalls, wallSegment);
    }
    return {
      ...state,
      walls: nextWalls,
      unsavedModifications: addedWithUserInteraction,

      // Invalidate sensor coverage now that the walls have changed
      planSensorCoverageIntersectionVectors: new Map(
        FloorplanCollection.list(state.planSensors).map((sensor) => [
          sensor.id,
          'empty',
        ])
      ),

      // Invalidate all validations now that the walls have changed
      validations: new Map(
        Array.from(state.validations).map(([id]) => [id, 'empty'])
      ),

      // Invalidate area of concern sensor placements
      areasOfConcern: FloorplanCollection.map(
        state.areasOfConcern,
        (areaOfConcern) => ({
          ...areaOfConcern,
          sensorPlacements: { type: 'empty' },
        })
      ),
    };
  }

  export function changeWallSegmentId(
    state: State,
    oldId: WallSegment['id'],
    newId: WallSegment['id']
  ): State {
    return {
      ...state,
      walls: FloorplanCollection.changeId(state.walls, oldId, newId),
      undoStack: UndoStack.changeObjectId(state.undoStack, oldId, newId),
    };
  }
}

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'mutation.begin': {
      const item = UndoStackItem.create(
        action.options.initialActionCreator,
        action.options.rollbackActionCreator,
        action.options.syncToAPI,
        action.objectIds
      );

      return {
        ...state,
        undoStack: UndoStack.append(state.undoStack, item),
        savePending: true,
      };
    }
    case 'mutation.commit': {
      return {
        ...state,
        savePending: false,
        lastSavedAt: moment.utc().format(),
      };
    }
    case 'mutation.rollback': {
      return {
        ...state,
        savePending: false,
        undoStack: UndoStack.removeLastItem(state.undoStack),
      };
    }
    case 'undoStack.begin': {
      return {
        ...state,
        savePending: true,
        undoStack: { ...state.undoStack, loading: true },
      };
    }
    case 'undoStack.setIndex': {
      return {
        ...state,
        lastSavedAt: moment.utc().format(),
        savePending: false,
        undoStack: {
          ...state.undoStack,
          currentIndex: action.index,
          loading: false,
        },
      };
    }
    case 'panels.csv.setState': {
      return {
        ...state,
        panels: {
          ...state.panels,
          csvOpen: action.setOpen,
          settingsOpen: action.setOpen ? false : state.panels.settingsOpen,
          dxfOpen: action.setOpen ? false : state.panels.dxfOpen,
        },
      };
    }
    case 'panels.dxf.setState': {
      return {
        ...state,
        panels: {
          ...state.panels,
          dxfOpen: action.setOpen,
          settingsOpen: action.setOpen ? false : state.panels.settingsOpen,
          csvOpen: action.setOpen ? false : state.panels.csvOpen,
        },
      };
    }
    case 'panels.settings.setState': {
      return {
        ...state,
        panels: {
          ...state.panels,
          csvOpen: action.setOpen ? false : state.panels.csvOpen,
          settingsOpen: action.setOpen,
          dxfOpen: action.setOpen ? false : state.panels.dxfOpen,
        },
      };
    }
    case 'viewport.mousedown': {
      let nextState = {
        ...state,
        panels: {
          settingsOpen: false,
          csvOpen: false,
          dxfOpen: false,
        },
      };

      if (nextState.focusedObject) {
        return State.blurFocusedObject(nextState);
      }
      return State.blurAll(nextState, action.position, action.ctrlKey);
    }
    case 'placement.cancel': {
      let nextState = {
        ...state,
        placementMode: null,
      };

      if (nextState.cachedPlanningState) {
        nextState = {
          ...nextState,
          planning: nextState.cachedPlanningState,
          cachedPlanningState: null,
        };
      }

      return nextState;
    }
    case 'placement.change': {
      return { ...state, placementMode: action.placementMode };
    }
    case 'placement.addSensor': {
      const sensor = { ...action.sensor, id: action.sensorId };
      if (state.duplicateSensorParams) {
        sensor.height = state.duplicateSensorParams.height;
        sensor.rotation = state.duplicateSensorParams.rotation;
      }

      const nextState = State.addSensor(state, sensor);
      return {
        ...nextState,
        duplicateSensorParams: null,
        // disable placement mode once the new object is placed
        placementMode: null,
        unsavedModifications: true,
      };
    }
    case 'placement.addAreaOfConcern': {
      const areaOfConcern = {
        ...action.areaOfConcern,
        id: action.areaOfConcernId,
        coverageIntersectionHeightMapEnabled: state.heightMap.enabled,
        coverageIntersectionWallsEnabled: !FloorplanCollection.isEmpty(
          state.walls
        ),
      };

      if (state.duplicateAreaOfConcernParams) {
        const originalPosition =
          state.duplicateAreaOfConcernParams.originalPosition;

        // Given the vertices of the previous polygon, offset them all so that they are
        // relative to the coordinate that the user clicked on
        areaOfConcern.vertices =
          state.duplicateAreaOfConcernParams.vertices.map((vertex) => {
            const normalized = FloorplanCoordinates.create(
              vertex.x - originalPosition.x,
              vertex.y - originalPosition.y
            );
            return FloorplanCoordinates.create(
              normalized.x + areaOfConcern.position.x,
              normalized.y + areaOfConcern.position.y
            );
          });

        areaOfConcern.name = state.duplicateAreaOfConcernParams.name + '*';
      }

      const nextState = State.addAreaOfConcern(state, areaOfConcern);
      return {
        ...nextState,
        duplicateAreaOfConcernParams: null,
        // disable placement mode once the new object is placed
        placementMode: null,
        unsavedModifications: true,
      };
    }
    case 'placement.addSpace': {
      const space = { ...action.space, id: action.spaceId };
      switch (space.shape.type) {
        case 'box':
          if (state.duplicateSpaceBoxParams) {
            space.shape.width = state.duplicateSpaceBoxParams.width;
            space.shape.height = state.duplicateSpaceBoxParams.height;
            space.name = state.duplicateSpaceBoxParams.name + '*';
            space.countingMode = state.duplicateSpaceBoxParams.countingMode;
            //FIXME: make sure countingMode gets duplicated.
          }
          break;
        case 'circle':
          if (state.duplicateSpaceCircleParams) {
            space.shape.radius = state.duplicateSpaceCircleParams.radius;
            space.name = state.duplicateSpaceCircleParams.name;
            space.countingMode = 'oa';
          }
          break;
        case 'polygon':
          if (state.duplicateSpacePolygonParams) {
            const originalPosition =
              state.duplicateSpacePolygonParams.originalPosition;
            // Given the vertices of the previous polygon, offset them all so that they are
            // relative to the coordinate that the user clicked on
            space.name = state.duplicateSpacePolygonParams.name + '*';
            space.countingMode = state.duplicateSpacePolygonParams.countingMode;
            space.shape.vertices =
              state.duplicateSpacePolygonParams.vertices.map((vertex) => {
                const normalized = FloorplanCoordinates.create(
                  vertex.x - originalPosition.x,
                  vertex.y - originalPosition.y
                );
                return FloorplanCoordinates.create(
                  normalized.x + space.position.x,
                  normalized.y + space.position.y
                );
              });
          }
          break;
      }

      const nextState = State.addSpace(state, space);
      return {
        ...nextState,
        duplicateSpaceBoxParams: null,
        duplicateSpaceCircleParams: null,
        duplicateSpacePolygonParams: null,

        // disable placement mode once the new object is placed
        placementMode: null,
        unsavedModifications: true,
      };
    }
    case 'placement.addSpaces': {
      let nextState = state;
      action.spaces.forEach((space, index) => {
        nextState = State.addSpace(
          nextState,
          { ...space, id: action.spaceIds[index] },
          false
        );
      });

      return {
        ...nextState,
        placementMode: action.nextPlacementMode ?? null,
        unsavedModifications: true,
      };
    }
    case 'placement.addThreshold': {
      let nextState = State.addThreshold(state, {
        ...action.threshold,
        id: action.thresholdId,
      });

      if (nextState.cachedPlanningState) {
        nextState = {
          ...nextState,
          planning: nextState.cachedPlanningState,
          cachedPlanningState: null,
        };
      }

      return {
        ...nextState,

        // disable placement mode once the new object is placed
        placementMode: null,
        planning: { ...nextState.planning, showThresholds: true },
        unsavedModifications: true,
        focusedObject: { type: 'threshold', id: action.thresholdId },
        objectListType: 'threshold',
      };
    }
    case 'placement.addReference': {
      const nextState = State.addReference(state, {
        ...action.reference,
        id: action.referenceId,
      });
      return {
        ...nextState,

        // disable placement mode once the new object is placed
        placementMode: null,
        unsavedModifications: true,
      };
    }
    case 'placement.addPhotoGroup': {
      const nextState = State.addPhotoGroup(state, {
        ...action.photoGroup,
        id: action.photoGroupId,
      });
      return {
        ...nextState,

        // disable placement mode once the new object is placed
        placementMode: null,
        unsavedModifications: true,
      };
    }
    case 'sensor.remove': {
      return State.removeSensor(state, action.id);
    }
    case 'sensor.rotateRight90': {
      return State.updateSensor(state, action.id, (sensor: PlanSensor) => {
        return {
          rotation: Number(modulo(sensor.rotation + 90, 360).toFixed(1)),
        };
      });
    }

    case 'sensor.copy': {
      return {
        ...state,
        copiedEntitySettings: {
          id: action.sensor.id,
          type: PastableEntity.SENSOR,
          sensor: {
            height: action.sensor.height,
            rotation: action.sensor.rotation,
            sensorFunction: action.sensor.sensorFunction,
          },
        },
      };
    }

    case 'sensor.changeLocked': {
      return State.lockSensor(state, action.id, action.locked);
    }
    case 'sensor.saveNotes': {
      const id = action.id;
      const sensor = state.planSensors.items.get(id);
      if (typeof sensor === 'undefined') {
        throw new Error(`Sensor with id ${id} not found`);
      }

      return {
        ...state,
        planSensors: FloorplanCollection.updateItem(state.planSensors, id, {
          notes: action.notes,
        }),
        unsavedModifications: true,
      };
    }
    case 'sensor.changeHeight': {
      return State.updateSensor(state, action.id, (sensor) => {
        const nextHeight = Math.min(
          PlanSensor.computeMaxHeight(sensor.sensorFunction),
          Math.max(
            PlanSensor.computeMinHeight(sensor.sensorFunction),
            action.height
          )
        );
        return {
          height: nextHeight,
        };
      });
    }
    case 'sensor.changeRotation': {
      const rotation = Number(modulo(action.rotation, 360).toFixed(1));
      return State.updateSensor(state, action.id, {
        rotation,
      });
    }
    case 'sensor.changeFunction': {
      return State.updateSensor(state, action.id, {
        sensorFunction: action.sensorFunction,
      });
    }
    case 'sensor.changeCadId': {
      return State.updateSensor(state, action.id, {
        cadId: action.cadId,
      });
    }
    case 'sensor.bulkChangeSerialNumber': {
      let nextState = state;

      for (const update of action.updates) {
        nextState = State.updateSensor(nextState, update.id, {
          serialNumber: update.serialNumber,

          // Reset diagnostic data
          status: undefined,
          last_heartbeat: undefined,
          mac: undefined,
          ipv4: undefined,
          ipv6: undefined,
          os: undefined,
        });

        // When changing the serial number, stop streaming data
        const existingConnection = nextState.sensorConnections.get(update.id);
        if (!existingConnection) {
          console.warn(
            `Sensor with id ${update.id} does not have a connection`
          );
          continue;
        }

        const nextConnection = {
          ...existingConnection,
          status: 'disconnected',
        } as const;

        nextState = State.updateOrCreateSensorConnection(
          nextState,
          update.id,
          nextConnection
        );
      }

      return nextState;
    }
    case 'sensor.changeSerialNumber': {
      let nextState = State.updateSensor(state, action.id, {
        serialNumber: action.serialNumber,

        // Reset diagnostic data
        status: undefined,
        last_heartbeat: undefined,
        mac: undefined,
        ipv4: undefined,
        ipv6: undefined,
        os: undefined,
      });

      // When changing the serial number, stop streaming data
      const existingConnection = state.sensorConnections.get(action.id);
      if (!existingConnection) {
        console.warn(`Sensor with id ${action.id} does not have a connection`);
        return nextState;
      }

      const nextConnection = {
        ...existingConnection,
        status: 'disconnected',
      } as const;

      return State.updateOrCreateSensorConnection(
        nextState,
        action.id,
        nextConnection
      );
    }
    case 'sensor.boundingBoxFilter': {
      return State.updateSensor(state, action.id, {
        // I'm unsure what state we should manipulate here
        boundingBoxFilter: action.boundingBoxFilter,
      });
    }
    case 'sensor.setDiagnosticMetadata': {
      return State.updateSensor(state, action.id, {
        status: action.status,
        last_heartbeat: action.last_heartbeat,
        mac: action.mac,
        ipv4: action.ipv4,
        ipv6: action.ipv6,
        os: action.os,
      });
    }
    case 'sensor.dismiss': {
      return State.blurFocusedObject(state);
    }
    case 'sensor.changeId': {
      return State.changeSensorId(state, action.oldId, action.newId);
    }
    case 'sensor.focus': {
      return State.focusSensor(state, action.id);
    }
    case 'sensor.highlight': {
      return State.highlightItem(state, 'sensor', action.id);
    }
    case 'sensor.duplicate': {
      const nextState = State.addSensor(state, action.sensor);
      return State.focusSensor(nextState, action.sensor.id);
    }
    case 'sensor.importPageFromAPI': {
      let nextState = state;

      let nullGeometryIdx = 0;
      for (const floorplanSensor of action.responseBody.results) {
        const planSensor = PlanSensor.createFromFloorplanSensor(
          floorplanSensor,
          state.floorplan,
          nullGeometryIdx
        );
        if (!floorplanSensor.centroid_from_origin_x_meters) {
          nullGeometryIdx += 1;
        }

        nextState = State.addSensor(nextState, planSensor, false);
      }
      return nextState;
    }

    // case 'sensor.address.submit': {
    //   return State.updateSensor(state, action.id, {
    //     connection: {
    //       type: 'local',
    //       localIPAddress: action.address,
    //       status: 'connecting',
    //     },
    //   });
    // }

    case 'sensor.menu.unlink': {
      // TODO: implement
      return state;
    }

    case 'sensor.menu.link': {
      // TODO: implement
      return state;
    }

    case 'sensor.menu.connect': {
      return State.updateOrCreateSensorConnection(state, action.id, {
        serialNumber: action.serialNumber,
        status: 'connecting',
      });
    }

    case 'sensor.menu.disconnect': {
      const existingConnection = state.sensorConnections.get(action.id);
      if (!existingConnection) {
        console.warn(`Sensor with id ${action.id} does not have a connection`);
        return state;
      }

      const nextConnection = {
        ...existingConnection,
        status: 'disconnected',
      } as const;

      return State.updateOrCreateSensorConnection(
        state,
        action.id,
        nextConnection
      );
    }

    case 'sensorConnection.update': {
      return State.updateOrCreateSensorConnection(
        state,
        action.id,
        action.sensorConnection
      );
    }

    case 'space.dismiss': {
      return State.blurFocusedObject(state);
    }

    case 'space.copy': {
      const {
        shape,
        position,
        coreSpaceLabels,
        countingMode,
        coreSpaceCapacity,
        coreSpaceFunction,
        name,
      } = action.space;
      return {
        ...state,
        copiedEntitySettings: {
          id: action.space.id,
          type: PastableEntity.SPACE,
          space: {
            shape,
            position,
            coreSpaceLabels,
            countingMode,
            coreSpaceCapacity,
            coreSpaceFunction,
            name,
          },
        },
      };
    }

    case 'space.duplicate': {
      const nextState = State.addSpace(state, action.space);
      return State.focusSpace(nextState, action.space.id);
    }

    case 'space.duplicate.bulk': {
      let nextState = state;
      // Pass addedWithUserInteraction=false so focus doesn't cascade to each added space in
      // turn; the caller already has a meaningful focus/selection for the group.
      for (const space of action.spaces) {
        nextState = State.addSpace(nextState, space, false);
      }
      return {
        ...nextState,
        unsavedModifications: true,
      };
    }

    case 'space.remove.bulk': {
      let nextState = state;
      for (const id of action.ids) {
        nextState = State.removeSpace(nextState, id);
      }
      return nextState;
    }

    case 'space.resize.box': {
      return State.updateSpace(state, action.id, (space) => {
        if (space.shape.type !== 'box') {
          return space;
        }

        return {
          ...space,
          position: action.position,
          shape: {
            ...space.shape,
            width: action.width,
            height: action.height,
          },
        };
      });
    }

    case 'space.resize.circle': {
      return State.updateSpace(state, action.id, (space) => {
        if (space.shape.type !== 'circle') {
          return space;
        }

        return {
          ...space,
          position: action.position,
          shape: {
            ...space.shape,
            radius: action.radius,
          },
        };
      });
    }

    case 'space.resize.polygon': {
      return State.updateSpace(state, action.id, (space) => {
        if (space.shape.type !== 'polygon') {
          return space;
        }
        return {
          ...space,
          position: action.position,
          shape: {
            ...space.shape,
            vertices: action.vertices,
          },
        };
      });
    }

    case 'space.remove': {
      return State.removeSpace(state, action.id);
    }

    case 'space.focus': {
      return State.focusSpace(state, action.id);
    }

    case 'space.changeLocked': {
      return State.lockSpace(state, action.id, action.locked);
    }

    case 'space.changeCapacity': {
      return State.updateSpace(state, action.id, {
        coreSpaceCapacity: action.capacity,
      });
    }

    case 'space.changeFunction': {
      return State.updateSpace(state, action.id, {
        coreSpaceFunction: action.function,
      });
    }

    case 'space.changeCountingMode': {
      return State.updateSpace(state, action.id, {
        countingMode: action.countingMode,
      });
    }

    case 'space.changeLabels': {
      return State.updateSpace(state, action.id, (space) => {
        let coreSpaceLabels = space.coreSpaceLabels;

        for (const label of action.labelsToRemove) {
          coreSpaceLabels = coreSpaceLabels.filter((l) => l.id !== label.id);
        }

        for (const label of action.labelsToAdd) {
          coreSpaceLabels = [...coreSpaceLabels, label];
        }

        return {
          ...space,
          coreSpaceLabels,
        };
      });
    }

    case 'space.changeShape': {
      return State.updateSpace(state, action.id, {
        shape: action.shape,
      });
    }

    case 'space.changeName': {
      const newState = { ...state };
      if (action.copy || action.rollback) {
        newState.entityPasteMode = {
          ...newState.entityPasteMode,
          amount: newState.entityPasteMode.amount! + (action.copy ? 1 : -1),
        };
      }

      return State.updateSpace(newState, action.id, {
        name: action.name,
      });
    }

    case 'space.changeIWMS': {
      const newState = { ...state };

      return State.updateSpace(newState, action.id, {
        iwmsId: action.iwmsId,
      });
    }

    case 'space.changeDebounceSettings': {
      const newState = { ...state };

      return State.updateSpace(newState, action.id, {
        presenceDebounceEnabled: action.presenceDebounceEnabled,
        presenceDebounceTrueMs: action.presenceDebounceTrueMs,
        presenceDebounceFalseMs: action.presenceDebounceFalseMs,
      });
    }

    case 'space.bulkChangeDebounceSettings': {
      let nextState = state;
      for (const space of FloorplanCollection.list(state.spaces)) {
        if (space.locked && !action.includeLocked) {
          continue;
        }
        nextState = State.updateSpace(nextState, space.id, {
          presenceDebounceEnabled: action.presenceDebounceEnabled,
          presenceDebounceTrueMs: action.presenceDebounceTrueMs,
          presenceDebounceFalseMs: action.presenceDebounceFalseMs,
        });
      }
      return nextState;
    }

    case 'space.polygon.addVertex': {
      const vertexPosition = ViewportCoordinates.toFloorplanCoordinates(
        action.position,
        state.viewport,
        state.floorplan
      );

      return State.updateSpace(state, action.id, (space) => {
        if (space.shape.type !== 'polygon') {
          return space;
        }

        const newVertices = [
          ...space.shape.vertices.slice(0, action.polygonVertexIndex),
          vertexPosition,
          ...space.shape.vertices.slice(action.polygonVertexIndex),
        ];

        return {
          ...space,
          position: calculatePolygonCentroid(newVertices),
          shape: { ...space.shape, vertices: newVertices },
        };
      });
    }

    case 'space.polygon.removeVertex': {
      return State.updateSpace(state, action.id, (space) => {
        if (space.shape.type !== 'polygon') {
          return space;
        }

        // Don't allow deleting vertices if the polygon is already a triangle
        // Polygons should always have an area and not be linear
        if (space.shape.vertices.length <= 3) {
          return space;
        }

        // Remove the vertex at the given index
        const verticesCopy = space.shape.vertices.slice();
        verticesCopy.splice(action.polygonVertexIndex, 1);

        return {
          ...space,
          position: calculatePolygonCentroid(verticesCopy),
          shape: {
            ...space.shape,
            vertices: verticesCopy,
          },
        };
      });
    }

    case 'space.changeId': {
      return State.changeSpaceId(state, action.oldId, action.newId);
    }
    case 'space.importPageFromAPI': {
      let nextState = state;

      let nullGeometryIdx = 0;
      for (const floorplanSpace of action.responseBody.results) {
        const planSpace = Space.createFromFloorplanSpace(
          floorplanSpace,
          state.floorplan,
          nullGeometryIdx
        );
        if (
          floorplanSpace.shape === 'polygon' &&
          !floorplanSpace.polygon_verticies
        ) {
          nullGeometryIdx += 1;
        }
        nextState = State.addSpace(nextState, planSpace, false);
      }
      return nextState;
    }

    case 'areaOfConcern.changeLocked': {
      return State.lockAreaOfConcern(state, action.id, action.locked);
    }

    case 'areaOfConcern.changeId': {
      return State.changeAreaOfConcernId(state, action.oldId, action.newId);
    }
    case 'areaOfConcern.changeName': {
      return State.updateAreaOfConcern(state, action.id, {
        name: action.name,
      });
    }
    case 'areaOfConcern.focus': {
      return State.focusAreaOfConcern(state, action.id);
    }
    case 'areaOfConcern.changeNotes': {
      return State.updateAreaOfConcern(state, action.id, {
        notes: action.notes,
      });
    }
    case 'areaOfConcern.importPageFromAPI': {
      let nextState = state;
      for (const floorplanAreaOfConcern of action.responseBody.results) {
        const planAreaOfConcern =
          AreaOfConcern.createFromFloorplanAreaOfConcern(
            floorplanAreaOfConcern
          );
        nextState = State.addAreaOfConcern(nextState, planAreaOfConcern, false);
      }
      return nextState;
    }

    case 'areaOfConcern.resize': {
      return State.updateAreaOfConcern(state, action.id, (areaOfConcern) => {
        // Check to see if the origin position is within the area of concern. If not, move it to be
        // the center of the area of concern.
        let sensorsOrigin = areaOfConcern.sensorsOrigin;
        const isOriginInsideAreaOfConcern =
          robustPointInPolygon(
            areaOfConcern.vertices.map((v) => [v.x, v.y]),
            [sensorsOrigin.x, sensorsOrigin.y]
          ) !== 1; /* 1 = outside polygon */
        if (!isOriginInsideAreaOfConcern) {
          sensorsOrigin = AreaOfConcern.generateInitialOriginPosition(
            areaOfConcern.vertices
          );
        }

        return {
          ...areaOfConcern,
          position: action.position,
          vertices: action.vertices,
          sensorsOrigin,

          // Recompute sensor placements when an area of concern is moved
          sensorPlacements: { type: 'empty' },
        };
      });
    }

    case 'areaOfConcern.remove': {
      return State.removeAreaOfConcern(state, action.id);
    }

    case 'areaOfConcern.dismiss': {
      return State.blurFocusedObject(state);
    }

    case 'areaOfConcern.changeSensorsEnabled': {
      return State.updateAreaOfConcern(state, action.id, (areaOfConcern) => {
        return {
          ...areaOfConcern,
          sensorsEnabled: action.enabled,
        };
      });
    }

    case 'areaOfConcern.changeOriginPosition': {
      return State.updateAreaOfConcern(state, action.id, (areaOfConcern) => {
        if (areaOfConcern.sensorPlacements.type === 'loading') {
          return areaOfConcern;
        }

        return {
          ...areaOfConcern,
          sensorsOrigin: action.sensorsOrigin,

          // Recompute sensor placements when an area of concern is moved
          sensorPlacements: { type: 'empty' },
        };
      });
    }

    case 'areaOfConcern.changeMinimumExclusiveArea': {
      return State.updateAreaOfConcern(state, action.id, (areaOfConcern) => {
        if (areaOfConcern.sensorPlacements.type === 'loading') {
          return areaOfConcern;
        }

        return {
          ...areaOfConcern,
          minimumExclusiveArea: action.value,

          // Recompute sensor placements when an area of concern is moved
          sensorPlacements: { type: 'empty' },
        };
      });
    }

    case 'areaOfConcern.changeSafetyFactorPercentage': {
      return State.updateAreaOfConcern(state, action.id, (areaOfConcern) => {
        if (areaOfConcern.sensorPlacements.type === 'loading') {
          return areaOfConcern;
        }

        return {
          ...areaOfConcern,
          safetyFactorPercentage: action.value,

          // Recompute sensor placements when an area of concern is moved
          sensorPlacements: { type: 'empty' },
        };
      });
    }

    case 'areaOfConcern.changeSensorHeight': {
      return State.updateAreaOfConcern(state, action.id, (areaOfConcern) => {
        if (areaOfConcern.sensorPlacements.type === 'loading') {
          return areaOfConcern;
        }

        return {
          ...areaOfConcern,
          sensorHeight: action.height,

          // Recompute sensor placements when an area of concern is moved
          sensorPlacements: { type: 'empty' },
        };
      });
    }

    case 'areaOfConcern.changeSensorBaseAngle': {
      return State.updateAreaOfConcern(state, action.id, (areaOfConcern) => {
        if (areaOfConcern.sensorPlacements.type === 'loading') {
          return areaOfConcern;
        }

        return {
          ...areaOfConcern,
          sensorBaseAngleDegrees: action.angle,

          // Recompute sensor placements when an area of concern is moved
          sensorPlacements: { type: 'empty' },
        };
      });
    }

    case 'areaOfConcern.changeCadIdPrefix': {
      return State.updateAreaOfConcern(state, action.id, (areaOfConcern) => {
        return {
          ...areaOfConcern,
          cadIdPrefix: action.prefix,

          // NOTE: DO NOT recompute sensor placements when the cad id prefix changes, because that
          // doesn't effect the resulting sensor positions.
        };
      });
    }

    case 'areaOfConcern.changeCoverageIntersectionHeightMapEnabled': {
      return State.updateAreaOfConcern(state, action.id, (areaOfConcern) => {
        return {
          ...areaOfConcern,
          coverageIntersectionHeightMapEnabled: action.enabled,

          // Recompute sensor placements when the height map flag changes
          sensorPlacements: { type: 'empty' },
        };
      });
    }

    case 'areaOfConcern.changeCoverageIntersectionWallsEnabled': {
      return State.updateAreaOfConcern(state, action.id, (areaOfConcern) => {
        return {
          ...areaOfConcern,
          coverageIntersectionWallsEnabled: action.enabled,

          // Recompute sensor placements when the wall segments flag changes
          sensorPlacements: { type: 'empty' },
        };
      });
    }

    case 'areaOfConcern.changeSmallRoomMode': {
      return State.updateAreaOfConcern(state, action.id, (areaOfConcern) => {
        return {
          ...areaOfConcern,
          smallRoomMode: action.enabled,

          // Recompute sensor placements when the wall segments flag changes
          sensorPlacements: { type: 'empty' },
        };
      });
    }

    case 'areaOfConcern.sensorPlacements.beginCalculating': {
      const now = performance.now();

      let nextState = state;
      for (const areaOfConcernId of action.areaOfConcernIds) {
        nextState = State.updateAreaOfConcern(
          nextState,
          areaOfConcernId,
          (areaOfConcern) => {
            return {
              ...areaOfConcern,
              sensorPlacements: {
                type: 'loading',
                data: [],
                autodetectedRooms: [],
                startTimestamp: now,
              },
            };
          },
          true
        );
      }
      return nextState;
    }

    case 'areaOfConcern.sensorPlacements.setData': {
      // Reject updates for areas that no longer exist
      if (!state.areasOfConcern.items.has(action.areaOfConcernId)) {
        return state;
      }

      const now = performance.now();

      return State.updateAreaOfConcern(
        state,
        action.areaOfConcernId,
        (areaOfConcern) => {
          if (!action.done) {
            return {
              ...areaOfConcern,
              sensorPlacements: {
                type: 'loading',
                data: action.data,
                autodetectedRooms: action.autodetectedRooms,
                startTimestamp:
                  areaOfConcern.sensorPlacements.type === 'loading'
                    ? areaOfConcern.sensorPlacements.startTimestamp
                    : performance.now(),
              },
            };
          }

          let ellapsedMilliseconds = 0;
          switch (areaOfConcern.sensorPlacements.type) {
            case 'loading':
              ellapsedMilliseconds =
                now - areaOfConcern.sensorPlacements.startTimestamp;
              break;
            case 'failed':
              return areaOfConcern;
            case 'done':
              ellapsedMilliseconds =
                areaOfConcern.sensorPlacements.ellapsedMilliseconds;
              break;
          }

          return {
            ...areaOfConcern,
            sensorPlacements: {
              type: 'done',
              data: action.data,
              autodetectedRooms: action.autodetectedRooms,
              ellapsedMilliseconds,
            },
          };
        },
        true
      );
    }
    case 'areaOfConcern.sensorPlacements.failWithError': {
      // Reject updates for areas that no longer exist
      if (!state.areasOfConcern.items.has(action.areaOfConcernId)) {
        return state;
      }

      return State.updateAreaOfConcern(
        state,
        action.areaOfConcernId,
        (areaOfConcern) => {
          return {
            ...areaOfConcern,
            sensorPlacements: {
              type: 'failed',
              error: action.error,
            },
          };
        },
        true
      );
    }

    case 'areaOfConcern.duplicate': {
      const nextState = State.addAreaOfConcern(state, action.areaOfConcern);
      return State.focusAreaOfConcern(nextState, action.areaOfConcern.id);
    }

    case 'areaOfConcern.mergeAreas': {
      let nextState = state;

      for (const areaOfConcern of action.areasToCreate) {
        nextState = State.addAreaOfConcern(nextState, areaOfConcern);
      }

      for (let { id } of action.areasToRemove) {
        nextState = State.removeAreaOfConcern(
          nextState,
          state.undoStack.uuidMapping.get(id)
            ? state.undoStack.uuidMapping.get(id)!
            : id,
          true
        );
      }

      return { ...nextState, unsavedModifications: true };
    }

    case 'areaOfConcern.swapSensors': {
      let nextState = state;

      // Delete all sensors that are within the area of concern
      for (const { id } of action.existingPlanSensors) {
        nextState = State.removeSensor(nextState, id, true);
      }

      // Add in all new plan sensors that were created during the swap process
      for (const planSensor of action.newPlanSensors) {
        nextState = State.addSensor(nextState, planSensor, false);
      }

      return State.updateAreaOfConcern(
        nextState,
        action.areaOfConcernId,
        (areaOfConcern) => ({
          ...areaOfConcern,
          // Disable sensor placements in area of concern
          sensorsEnabled: false,
        })
      );
    }
    case 'bulk.selectAllWalls': {
      const newBulkSelectedIds = state.bulkSelection.ids;
      state.walls.items.forEach((item) => newBulkSelectedIds.add(item.id));

      return {
        ...state,
        bulkSelection: { ...state.bulkSelection, ids: newBulkSelectedIds },
        focusedObject: null,
      };
    }
    case 'bulk.selectAllThresholds': {
      const newBulkSelectedIds = state.bulkSelection.ids;
      state.thresholds.items.forEach((item) => newBulkSelectedIds.add(item.id));

      return {
        ...state,
        bulkSelection: { ...state.bulkSelection, ids: newBulkSelectedIds },
        focusedObject: null,
      };
    }
    case 'bulk.selectAllAreasOfConcern': {
      const newBulkSelectedIds = state.bulkSelection.ids;
      state.areasOfConcern.items.forEach((item) =>
        newBulkSelectedIds.add(item.id)
      );

      return {
        ...state,
        bulkSelection: { ...state.bulkSelection, ids: newBulkSelectedIds },
        focusedObject: null,
      };
    }
    case 'bulk.selectAllSpaces': {
      const newBulkSelectedIds = state.bulkSelection.ids;
      state.spaces.items.forEach((item) => newBulkSelectedIds.add(item.id));

      return {
        ...state,
        bulkSelection: { ...state.bulkSelection, ids: newBulkSelectedIds },
        focusedObject: null,
      };
    }
    case 'bulk.selectAllEntry': {
      const newBulkSelectedIds = state.bulkSelection.ids;
      state.planSensors.items.forEach((item) =>
        item.type === 'entry' ? newBulkSelectedIds.add(item.id) : null
      );

      return {
        ...state,
        bulkSelection: { ...state.bulkSelection, ids: newBulkSelectedIds },
        focusedObject: null,
      };
    }
    case 'bulk.selectAllOpenArea': {
      const newBulkSelectedIds = state.bulkSelection.ids;
      state.planSensors.items.forEach((item) =>
        item.type === 'oa' ? newBulkSelectedIds.add(item.id) : null
      );

      return {
        ...state,
        bulkSelection: { ...state.bulkSelection, ids: newBulkSelectedIds },
        focusedObject: null,
      };
    }
    case 'bulk.setAction.move': {
      return {
        ...state,
        bulkSelection: {
          ...state.bulkSelection,
          action: 'move',
        },
      };
    }
    case 'bulk.setAction.bulkEditSpaces': {
      return {
        ...state,
        bulkSelection: {
          ...state.bulkSelection,
          action: 'editSpaces',
        },
      };
    }
    case 'bulk.move': {
      return {
        ...state,
        bulkSelection: {
          ...state.bulkSelection,
          ids: new Set<string>(),
          action: null,
        },
      };
    }
    case 'bulk.end': {
      // If we are in the middle of moving objects,
      // just cancel that operation.
      if (state.bulkSelection.action) {
        return {
          ...state,
          bulkSelection: { ...state.bulkSelection, action: null },
        };
      }

      return {
        ...state,
        bulkSelection: {
          ...state.bulkSelection,
          ids: new Set<string>(),
        },
      };
    }

    case 'bulk.editSpaces': {
      let nextState = state;

      /*
      action.objectIds.forEach((objectId) => {
        if (isFloorplanPlanSensorId(objectId)) {
          // `sensor.changeLocked`, etc
          nextState = State.lockSensor(nextState, objectId, action.locked);
        } else if (isFloorplanSpaceId(objectId)) {
          nextState = State.lockSpace(nextState, objectId, action.locked);
        } else if (isFloorplanThresholdId(objectId)) {
          nextState = State.lockThreshold(nextState, objectId, action.locked);
        } else if (isFloorplanAreaOfConcernId(objectId)) {
          nextState = State.lockAreaOfConcern(
            nextState,
            objectId,
            action.locked
          );
        }
      });
      */

      return nextState;
    }

    case 'bulk.changeLocked': {
      let nextState = state;

      action.objectIds.forEach((objectId) => {
        if (isFloorplanPlanSensorId(objectId)) {
          // `sensor.changeLocked`, etc
          nextState = State.lockSensor(nextState, objectId, action.locked);
        } else if (isFloorplanSpaceId(objectId)) {
          nextState = State.lockSpace(nextState, objectId, action.locked);
        } else if (isFloorplanThresholdId(objectId)) {
          nextState = State.lockThreshold(nextState, objectId, action.locked);
        } else if (isFloorplanAreaOfConcernId(objectId)) {
          nextState = State.lockAreaOfConcern(
            nextState,
            objectId,
            action.locked
          );
        }
      });

      return nextState;
    }

    case 'bulk.delete': {
      let nextState = state;

      action.objectIds.forEach((objectId) => {
        if (isFloorplanPlanSensorId(objectId)) {
          // `sensor.changeLocked`, etc
          nextState = State.removeSensor(nextState, objectId, true);
        } else if (isFloorplanSpaceId(objectId)) {
          nextState = State.removeSpace(nextState, objectId, true);
        } else if (isFloorplanThresholdId(objectId)) {
          nextState = State.removeThreshold(nextState, objectId, true);
        } else if (isFloorplanAreaOfConcernId(objectId)) {
          nextState = State.removeAreaOfConcern(nextState, objectId, true);
        }
      });

      return {
        ...nextState,
        bulkSelection: { ids: new Set<string>(), action: null },
      };
    }

    case 'bulk.create': {
      let nextState = state;

      for (const object of action.objects) {
        if (isFloorplanPlanSensorId(object.id)) {
          nextState = State.addSensor(nextState, object as PlanSensor);
        } else if (isFloorplanSpaceId(object.id)) {
          nextState = State.addSpace(nextState, object as Space);
        } else if (isFloorplanThresholdId(object.id)) {
          nextState = State.addThreshold(nextState, object as Threshold);
        } else if (isFloorplanAreaOfConcernId(object.id)) {
          nextState = State.addAreaOfConcern(
            nextState,
            object as AreaOfConcern
          );
        }
      }
      return nextState;
    }

    case 'threshold.importPageFromAPI': {
      let nextState = state;
      let nullGeometryIdx = 0;
      for (const floorplanThreshold of action.responseBody.results) {
        const threshold = Threshold.createFromFloorplanThreshold(
          floorplanThreshold,
          state.floorplan,
          nullGeometryIdx
        );
        if (!floorplanThreshold.position_a_x_meters) {
          nullGeometryIdx += 1;
        }

        nextState = State.addThreshold(nextState, threshold, false);
      }
      return nextState;
    }
    case 'threshold.saveNotes': {
      const id = action.id;
      const threshold = state.thresholds.items.get(id);
      if (typeof threshold === 'undefined') {
        throw new Error(`Threshold with id ${id} not found`);
      }

      return {
        ...state,
        thresholds: FloorplanCollection.updateItem(state.thresholds, id, {
          notes: action.notes,
        }),
        unsavedModifications: true,
      };
    }
    case 'threshold.changeLocked': {
      return State.lockThreshold(state, action.id, action.locked);
    }

    case 'threshold.changeDoorSwing': {
      return State.changeThresholdDoorSwing(
        state,
        action.id,
        action.swingsIntoFOV
      );
    }

    case 'threshold.dismiss': {
      return State.blurFocusedObject(state);
    }

    case 'threshold.remove': {
      return State.removeThreshold(state, action.id);
    }

    case 'threshold.duplicate': {
      return State.addThreshold(state, action.threshold);
    }

    case 'threshold.positionChange': {
      return State.updateThreshold(state, action.id, (threshold) => {
        // Clear the cached cursor position
        let update: Update<Threshold> = {};

        update.positionA = action.positionA;
        update.positionB = action.positionB;
        update.distanceLabelPosition = action.distanceLabelPosition;

        return update;
      });
    }

    case 'threshold.changeId': {
      return State.changeThresholdId(state, action.oldId, action.newId);
    }

    case 'threshold.changeName': {
      return State.updateThreshold(state, action.id, {
        name: action.name,
      });
    }

    case 'threshold.unlinkSpace': {
      const threshold = state.thresholds.items.get(
        action.thresholdId
      ) as Threshold;

      const space = state.spaces.items.get(action.spaceId) as Space;

      if (!threshold || !space) {
        return state;
      }

      return State.updateThreshold(
        state,
        action.thresholdId,
        (threshold: Threshold) => {
          return {
            relatedSpaces: threshold.relatedSpaces.filter(
              (relatedSpace) => relatedSpace.spaceId !== action.spaceId
            ),
          };
        }
      );
    }

    case 'threshold.flipSensorPlacement': {
      const threshold = state.thresholds.items.get(
        action.thresholdId
      ) as Threshold;

      const space = state.spaces.items.get(action.spaceId) as Space;

      if (!threshold || !space) {
        console.error('Unable to find the Threshold or Space!');
        return state;
      }

      return State.updateThreshold(
        state,
        action.thresholdId,
        (threshold: Threshold) => {
          return {
            relatedSpaces: threshold.relatedSpaces.map((relatedSpace) => {
              if (relatedSpace.spaceId === action.spaceId) {
                return {
                  ...relatedSpace,
                  sensorPlacement: relatedSpace.sensorPlacement === 1 ? -1 : 1,
                };
              }
              return relatedSpace;
            }),
          };
        }
      );
    }

    case 'threshold.linkSpace': {
      let nextState = state;

      if (nextState.selectionMode.active) {
        nextState = {
          ...state,
          selectionMode: {
            active: false,
          },
          planning: nextState.cachedPlanningState || nextState.planning,
          cachedPlanningState: null,
        };
      }

      const threshold = state.thresholds.items.get(
        action.thresholdId
      ) as Threshold;

      if (!threshold) {
        console.error('Unable to find the Threshold!');
        return nextState;
      }

      // Disallow linking the same Space twice -
      if (threshold.relatedSpaces.some((s) => s.spaceId === action.spaceId)) {
        return state;
      }

      const space = state.spaces.items.get(action.spaceId) as Space;

      if (!space) {
        console.error('Unable to find the Space!');
        return nextState;
      }

      // Determine whether the sensor is inside or outside of the Space...
      const sensorPlacement = checkSensorPlacement(state, threshold, space);

      nextState = State.updateThreshold(
        nextState,
        action.thresholdId,
        (threshold: Threshold) => {
          return {
            relatedSpaces: [
              ...threshold.relatedSpaces,
              {
                spaceId: action.spaceId,
                sensorPlacement: sensorPlacement,
                linkId: null,
                spaceName: null,
                spaceType: null,
                planId: null,
              },
            ],
          };
        }
      );

      // After linking, enable the Space layer, as otherwise it is
      // odd to see the spaces disappearing right after the selection disappearing...
      nextState = {
        ...nextState,
        selectionMode: {
          active: false,
        },
        planning: {
          ...nextState.planning,
          showSpaces: true,
          showSpacesEntry:
            space.countingMode === 'entry' || state.planning.showSpacesEntry,
          showSpacesOpenArea:
            space.countingMode !== 'entry' || state.planning.showSpacesOpenArea,
        },
      };

      return nextState;
    }

    case 'threshold.editSpaces': {
      const threshold = state.thresholds.items.get(action.id) as Threshold;

      if (!threshold) {
        console.warn('No threshold to unlink!');
        return state;
      }

      return State.updateThreshold(state, action.id, {
        relatedSpaces: action.relatedSpaces,
      });
    }

    case 'threshold.editPlanSensors': {
      const threshold = state.thresholds.items.get(action.id);

      if (!threshold) {
        console.warn('This threshold was not found!');
        return state;
      }

      return State.updateThreshold(state, action.id, {
        relatedPlanSensors: action.relatedPlanSensors,
      });
    }

    case 'threshold.unlinkPlanSensor': {
      const threshold = state.thresholds.items.get(action.thresholdId);

      if (!threshold) {
        console.warn('No threshold to unlink!');
        return state;
      }

      return State.updateThreshold(state, action.thresholdId, {
        relatedPlanSensors: threshold.relatedPlanSensors.filter(
          (planSensorId) => planSensorId !== action.planSensorId
        ),
      });
    }

    case 'threshold.linkPlanSensor': {
      let nextState = state;
      if (nextState.selectionMode.active) {
        nextState = {
          ...state,
          selectionMode: {
            active: false,
          },
          planning:
            nextState.cachedPlanningState !== null
              ? nextState.cachedPlanningState
              : nextState.planning,
          cachedPlanningState: null,
        };
      }

      const threshold = state.thresholds.items.get(
        action.thresholdId
      ) as Threshold;

      if (!threshold) {
        console.warn('No threshold?');
        return nextState;
      }

      // Disallow linking the same PlanSensor twice -
      if (
        threshold.relatedPlanSensors.some(
          (sensor) => sensor === action.planSensorId
        )
      ) {
        console.warn('Already linked!');
        return state;
      }

      return State.updateThreshold(
        nextState,
        action.thresholdId,
        (threshold: Threshold) => {
          return {
            relatedPlanSensors: [
              ...threshold.relatedPlanSensors,
              action.planSensorId,
            ],
          };
        }
      );
    }

    case 'reference.remove': {
      return State.removeReference(state, action.id);
    }

    case 'reference.duplicate': {
      return State.addReference(state, action.reference);
    }

    case 'reference.heightPosition.change': {
      return State.updateReference<ReferenceHeight>(
        state,
        action.id,
        (reference) => {
          let update: Update<ReferenceHeight> = {};
          update.position = action.position;
          update.heightMeters = { step: 'loading' as const };
          return update;
        }
      );
    }

    case 'reference.heightPosition.changeHeight': {
      return State.updateReference<ReferenceHeight>(
        state,
        action.id,
        (reference) => {
          let update: Update<ReferenceHeight> = {};
          update.heightMeters = {
            step: 'complete' as const,
            value: action.heightMetersValue,
          };
          return update;
        }
      );
    }

    case 'reference.rulerPosition.change': {
      return State.updateReference<ReferenceRuler>(
        state,
        action.id,
        (reference) => {
          // Clear the cached cursor position
          let update: Update<ReferenceRuler> = {};

          update.positionA = action.positionA;
          update.positionB = action.positionB;
          update.distanceLabelPosition = action.distanceLabelPosition;

          // Move distance marker to keep it in the center if needed
          if (reference.currentIsDistanceLockedToCenter) {
            update.distanceLabelPosition =
              ReferenceRuler.calculateCenterPoint(reference);
          }

          return update;
        }
      );
    }

    case 'reference.changeId': {
      return State.changeReferenceId(state, action.oldId, action.newId);
    }

    case 'reference.importReferenceRulerPageFromAPI': {
      let nextState = state;
      for (const floorplanReferenceRuler of action.responseBody.results) {
        const referenceRuler = ReferenceRuler.createFromFloorplanReferenceRuler(
          floorplanReferenceRuler
        );
        nextState = State.addReference(nextState, referenceRuler, false);
      }
      return nextState;
    }
    case 'reference.importReferenceHeightPageFromAPI': {
      let nextState = state;
      for (const floorplanReferenceHeight of action.responseBody.results) {
        const referenceHeight =
          ReferenceHeight.createFromFloorplanReferenceHeight(
            floorplanReferenceHeight
          );
        nextState = State.addReference(nextState, referenceHeight, false);
      }
      return nextState;
    }

    case 'settingsPanel.setDisplayUnit': {
      return {
        ...state,
        displayUnit: action.displayUnit,
        unsavedModifications: true,
      };
    }

    case 'settingsPanel.setEntityPasteMode': {
      return {
        ...state,
        entityPasteMode: { ...action.entityPasteMode },
        unsavedModifications: true,
      };
    }

    case 'settingsPanel.setTracksStartDate': {
      return {
        ...state,
        tracksStartDate: action.tracksStartDate,
        unsavedModifications: true,
      };
    }

    case 'settingsPanel.setTracksEndDate': {
      return {
        ...state,
        tracksEndDate: action.tracksEndDate,
        unsavedModifications: true,
      };
    }

    case 'settingsPanel.setTracksCount': {
      return {
        ...state,
        tracksCount: action.tracksCount,
        unsavedModifications: true,
      };
    }

    case 'photoGroup.remove': {
      return State.removePhotoGroup(state, action.id);
    }

    case 'photoGroup.dragmove': {
      return State.updatePhotoGroup(
        state,
        action.id,
        (photoGroup) => {
          return {
            ...photoGroup,
            position: action.itemPosition,
          };
        },
        true
      );
    }

    case 'photoGroup.changeLocked': {
      if (action.locked) {
        return State.lockPhotoGroup(state, action.id);
      } else {
        return State.unlockPhotoGroup(state, action.id);
      }
    }

    case 'photoGroup.changeName': {
      return State.updatePhotoGroup(state, action.id, (photoGroup) =>
        PhotoGroup.changeName(photoGroup, action.name)
      );
    }

    case 'photoGroup.saveNotes': {
      return State.updatePhotoGroup(state, action.id, (photoGroup) =>
        PhotoGroup.changeNotes(photoGroup, action.notes)
      );
    }

    case 'photoGroup.dismiss': {
      return State.blurPhotoGroup(state);
    }

    case 'photoGroup.changeId': {
      return State.changePhotoGroupId(state, action.oldId, action.newId);
    }

    case 'photoGroup.photos.changeId': {
      return State.changePhotoGroupPhotoId(
        state,
        action.photoGroupId,
        action.oldId,
        action.newId
      );
    }

    case 'photoGroup.photos.append': {
      return State.appendPhotoToPhotoGroup(
        state,
        action.id,
        action.fileName,
        action.photoDataUrl,
        action.uploadedPhotoId
      );
    }
    case 'photoGroup.photos.add': {
      return State.updatePhotoGroup(
        state,
        action.id,
        (photoGroup) => {
          return PhotoGroup.appendPhoto(photoGroup, action.photo);
        },
        true
      );
    }
    case 'photoGroup.photos.remove': {
      return State.removePhotoFromPhotoGroup(state, action.id, action.photoId);
    }
    case 'photoGroup.photos.changeName': {
      return State.changePhotoNameInPhotoGroup(
        state,
        action.id,
        action.photoId,
        action.name
      );
    }
    case 'photoGroup.photos.updateWithUploadUrl': {
      return State.updatePhotoGroup(
        state,
        action.id,
        (photoGroup) => {
          const existingPhoto = PhotoGroup.getPhotoById(
            photoGroup,
            action.photoId
          );
          if (!existingPhoto) {
            return photoGroup;
          }

          const updatedPhoto = {
            ...existingPhoto,
            image: {
              dirty: false as const,
              url: action.uploadUrl,
            },
          };

          return {
            ...photoGroup,
            photos: photoGroup.photos.map((photo) =>
              photo.id === updatedPhoto.id ? updatedPhoto : photo
            ),
          };
        },
        true
      );
    }
    case 'photoGroup.photos.reorder': {
      return State.reorderPhotosInPhotoGroup(
        state,
        action.id,
        action.oldIndex,
        action.newIndex
      );
    }
    case 'photoGroup.importPageFromAPI': {
      let nextState = state;
      for (const floorplanPhotoGroup of action.responseBody.results) {
        const photoGroup =
          PhotoGroup.createFromFloorplanPhotoGroup(floorplanPhotoGroup);
        nextState = State.addPhotoGroup(nextState, photoGroup, false);
      }
      return nextState;
    }

    case 'scaleEdit.begin': {
      return {
        ...state,
        scaleEdit: {
          status: 'fetching_image_upload_url',
        },
      };
    }

    case 'scaleEdit.beginUploading': {
      if (state.scaleEdit.status !== 'fetching_image_upload_url') {
        return state;
      }

      return {
        ...state,
        scaleEdit: {
          status: 'uploading',
          fileUploadPercent: 0,
        },
      };
    }

    case 'scaleEdit.uploadProgress': {
      if (state.scaleEdit.status !== 'uploading') {
        return state;
      }
      return {
        ...state,
        scaleEdit: {
          ...state.scaleEdit,
          fileUploadPercent: action.percent,
        },
      };
    }

    case 'scaleEdit.edit': {
      if (
        state.scaleEdit.status !== 'uploading' &&
        state.scaleEdit.status !== 'inactive'
      ) {
        return state;
      }

      const image = action.image || state.floorplanImage;
      if (!image) {
        return state;
      }

      return {
        ...state,
        scaleEdit: {
          status: action.openIntoMeasureMode
            ? 'measuring'
            : 'image_registration',
          floorplanImage: image,
          floorplan: {
            ...state.floorplan,
            width: image.width,
            height: image.height,
          },
          objectKey: action.objectKey,
          loading: false,
        },
      };
    }

    case 'scaleEdit.startLoading': {
      if (state.scaleEdit.status !== 'image_registration') {
        return state;
      }

      return {
        ...state,
        scaleEdit: {
          ...state.scaleEdit,
          loading: true,
        },
      };
    }

    case 'scaleEdit.error': {
      if (state.scaleEdit.status !== 'image_registration') {
        return state;
      }

      return {
        ...state,
        scaleEdit: {
          ...state.scaleEdit,
          loading: false,
        },
      };
    }

    case 'scaleEdit.cancel': {
      return {
        ...state,
        scaleEdit: { status: 'inactive' },
      };
    }

    case 'scaleEdit.submit': {
      if (state.scaleEdit.status === 'inactive') {
        return state;
      }
      return {
        ...state,
        scaleEdit: { status: 'inactive' },
      };
    }

    case 'scaleAndImage.change': {
      let nextState = state;
      if (
        // If new origin parameters were specified, then offset the floorplan by these
        typeof action.newOriginX !== 'undefined' &&
        typeof action.newOriginY !== 'undefined' &&
        typeof action.newRotationDegrees !== 'undefined'
      ) {
        nextState = {
          ...nextState,
          floorplan: {
            ...nextState.floorplan,
            origin: ImageCoordinates.create(
              action.newOriginX,
              action.newOriginY
            ),
            rotation: modulo(action.newRotationDegrees, 360),
          },
        };
      }

      const image = action.floorplanImage || state.floorplanImage;
      const imageKey = action.floorplanImageKey || state.floorplanImageKey;
      return {
        ...nextState,
        floorplanImage: image,
        floorplanImageKey: imageKey,
        measurement: action.measurement,
        floorplan: {
          ...nextState.floorplan,
          width: image ? image.width : 0,
          height: image ? image.height : 0,
          scale: action.measurement.computedScale,
        },
        unsavedModifications: true,
      };
    }

    case 'menu.focusObjectViaLink': {
      return {
        ...state,
        focusedObjectLink: action.objectId,
      };
    }

    case 'menu.unfocusLinkedObject': {
      return {
        ...state,
        focusedObjectLink: null,
      };
    }

    case 'menu.showSensors': {
      return {
        ...state,
        objectListType: 'sensor',
      };
    }
    case 'menu.showAreasOfConcern': {
      return {
        ...state,
        objectListType: 'areaofconcern',
      };
    }
    case 'menu.showSpaces': {
      return {
        ...state,
        objectListType: 'space',
      };
    }
    case 'menu.showReferences': {
      return {
        ...state,
        objectListType: 'reference',
      };
    }
    case 'menu.showPhotoGroups': {
      return {
        ...state,
        objectListType: 'photogroup',
      };
    }
    case 'menu.showLayers': {
      return {
        ...state,
        objectListType: 'layer',
      };
    }
    case 'menu.showThresholds': {
      return {
        ...state,
        objectListType: 'threshold',
      };
    }

    case 'menu.addSensor': {
      // Prevent creating waffle sensors - they are read-only
      if (action.sensorType === 'waffle') {
        // Ignore the action and return the state unchanged
        return state;
      }

      // Disable placing a sensor if the mode is already active
      if (
        state.placementMode &&
        state.placementMode.type === 'sensor' &&
        state.placementMode.sensorType === action.sensorType
      ) {
        return {
          ...state,
          placementMode: null,
        };
      }

      const enableSensorLayers = { ...state.planning };

      if (action.sensorType === 'entry') {
        enableSensorLayers.showSensors = true;
        enableSensorLayers.showSensorsEntry = true;
        enableSensorLayers.showSensorsEntryLR = true;
        enableSensorLayers.showSensorsEntryTOF = true;
      } else {
        enableSensorLayers.showSensors = true;
        enableSensorLayers.showSensorsOpenArea = true;
        enableSensorLayers.showOpenAreaCoverage = true;
        enableSensorLayers.showSensorsOpenAreaLR = true;
        enableSensorLayers.showSensorsOpenAreaMR = true;
        enableSensorLayers.showSensorsOpenAreaSR = true;
      }

      return {
        ...state,
        placementMode: {
          type: 'sensor',
          sensorType: action.sensorType,
          mousePosition: null,
        },
        planning: { ...enableSensorLayers },
      };
    }
    case 'menu.addAreaOfConcern': {
      // Disable placing an area of concern if the mode is already active
      if (state.placementMode && state.placementMode.type === 'areaofconcern') {
        return {
          ...state,
          placementMode: null,
        };
      }

      return {
        ...state,
        placementMode: {
          type: 'areaofconcern',
          vertices: [],
          mouseOverFinalPoint: false,
          nextPointSelfIntersection: null,
          mousePosition: null,
          nextPointPosition: null,
        },
      };
    }
    case 'menu.validateSpaces': {
      const validations = new Map<
        string,
        Array<SpaceValidation | SensorValidation>
      >();

      const spacesList = FloorplanCollection.list(state.spaces);
      const areasOfConcernList = FloorplanCollection.list(state.areasOfConcern);

      if (areasOfConcernList.length === 0) {
        // If there are no AOCs defined, don't run any validations.
        return { ...state, validations };
      }

      for (const space of spacesList) {
        validations.set(
          space.id,
          Space.validate(space, spacesList, areasOfConcernList)
        );
      }

      // NOTE: This overwrites all sensors/all other validations!
      const nextState = {
        ...state,
        validations,
      };
      return nextState;
    }
    case 'menu.addSpace': {
      // Disable placing a space if the mode is already active
      if (
        state.placementMode &&
        state.placementMode.type === 'space' &&
        state.placementMode.shape === action.shape &&
        state.placementMode.countingMode === action.countingMode
      ) {
        return {
          ...state,
          placementMode: null,
        };
      }

      const enableSpaceLayers = { ...state.planning };

      // As we are placing a new Space, let's enable the appropriate layer.
      // Otherwise it's odd to see the object disappear upon placement.
      if (action.countingMode === 'entry') {
        enableSpaceLayers.showSpaces = true;
        enableSpaceLayers.showSpacesEntry = true;
      } else {
        enableSpaceLayers.showSpaces = true;
        enableSpaceLayers.showSpacesOpenArea = true;
      }

      let nextState: State;
      if (action.shape === 'polygon') {
        nextState = {
          ...state,
          placementMode: {
            type: 'space',
            shape: 'polygon',
            countingMode: action.countingMode,
            vertices: [],
            mouseOverFinalPoint: false,
            nextPointSelfIntersection: null,
            mousePosition: null,
            nextPointPosition: null,
          },
        };
      } else if (action.shape === 'circle') {
        nextState = {
          ...state,
          placementMode: {
            type: 'space',
            shape: 'circle',
            countingMode: 'oa',
            mousePosition: null,
          },
        };
      } else {
        nextState = {
          ...state,
          placementMode: {
            type: 'space',
            shape: 'box',
            countingMode: action.countingMode,
            mousePosition: null,
          },
        };
      }
      return { ...nextState, planning: { ...enableSpaceLayers } };
    }
    case 'menu.addDeskBench': {
      const deskWidthMeters = Meters.fromInches(action.deskWidthInches);
      const deskDepthMeters = Meters.fromInches(action.deskDepthInches);

      if (
        state.placementMode &&
        state.placementMode.type === 'space' &&
        state.placementMode.shape === 'desk-bench' &&
        state.placementMode.countingMode === action.countingMode &&
        state.placementMode.deskWidthMeters === deskWidthMeters &&
        state.placementMode.deskDepthMeters === deskDepthMeters
      ) {
        return {
          ...state,
          placementMode: null,
        };
      }

      const enableSpaceLayers = { ...state.planning };
      enableSpaceLayers.showSpaces = true;
      if (action.countingMode === 'waffle') {
        enableSpaceLayers.showSpacesWaffle = true;
      } else {
        enableSpaceLayers.showSpacesOpenArea = true;
      }

      return {
        ...state,
        placementMode: {
          type: 'space',
          shape: 'desk-bench',
          countingMode: action.countingMode,
          deskWidthMeters,
          deskDepthMeters,
          nextDeskNumber: Space.getNextGeneratedNameNumber(
            FloorplanCollection.list(state.spaces),
            'Desk'
          ),
          sideMode: 'both',
          positionA: null,
          positionB: null,
          mousePosition: null,
        },
        planning: { ...enableSpaceLayers },
      };
    }
    case 'menu.addReference': {
      // Disable placing a reference if the mode is already active
      if (
        state.placementMode &&
        state.placementMode.type === 'reference' &&
        state.placementMode.referenceType === action.referenceType
      ) {
        return {
          ...state,
          placementMode: null,
        };
      }

      switch (action.referenceType) {
        case 'ruler':
          return {
            ...state,
            placementMode: {
              type: 'reference',
              referenceType: 'ruler',
              positionA: null,
              positionB: null,
              mousePosition: null,
            },
          };
        case 'height':
          return {
            ...state,
            placementMode: {
              type: 'reference',
              referenceType: 'height',
              mousePosition: null,
            },
          };
        default:
          return state;
      }
    }
    case 'menu.addPhotoGroup': {
      // Disable placing a photo group if the mode is already active
      if (state.placementMode && state.placementMode.type === 'photogroup') {
        return {
          ...state,
          placementMode: null,
        };
      }

      return {
        ...state,
        placementMode: {
          type: 'photogroup',
          mousePosition: null,
        },
      };
    }
    case 'menu.addThreshold': {
      if (state.placementMode && state.placementMode.type === 'threshold') {
        return {
          ...state,
          placementMode: null,
        };
      }

      return {
        ...state,
        placementMode: {
          type: 'threshold',
          positionA: null,
          positionB: null,
          mousePosition: null,
        },
        cachedPlanningState: state.planning,
        planning: { ...state.planning, showSpaces: false },
      };
    }
    case 'menu.removeFocusedObject': {
      if (state.focusedPhotoGroupId) {
        return State.removePhotoGroup(state, state.focusedPhotoGroupId);
      }

      if (!state.focusedObject) {
        return state;
      }

      switch (state.focusedObject.type) {
        case 'sensor':
          return State.removeSensor(state, state.focusedObject.id);
        case 'areaofconcern':
          return State.removeAreaOfConcern(state, state.focusedObject.id);
        case 'space':
          return State.removeSpace(state, state.focusedObject.id);
        default:
          return state;
      }
    }
    case 'menu.duplicateSensor': {
      const sensor = state.planSensors.items.get(action.id);
      if (typeof sensor === 'undefined') {
        throw new Error(`Sensor with id ${action.id} not found`);
      }
      return {
        ...state,
        duplicateSensorParams: {
          height: sensor.height,
          rotation: sensor.rotation,
        },
        placementMode: {
          type: 'sensor',
          sensorType: sensor.type,
          mousePosition: null,
        },
      };
    }

    case 'menu.duplicateAreaOfConcern': {
      const areaOfConcern = state.areasOfConcern.items.get(action.id);
      if (typeof areaOfConcern === 'undefined') {
        throw new Error(`AreaOfConcern with id ${action.id} not found`);
      }

      //TODO: AreaOfConcern, duplicate the rest of attributes

      return {
        ...state,
        placementMode: {
          type: 'areaofconcern-duplicate',
          mousePosition: null,
        },
        duplicateAreaOfConcernParams: {
          vertices: areaOfConcern.vertices,
          originalPosition: areaOfConcern.position,
          name: areaOfConcern.name,
        },
      };
    }

    case 'menu.duplicateSpace': {
      const space = state.spaces.items.get(action.id);
      if (typeof space === 'undefined') {
        throw new Error(`Space with id ${action.id} not found`);
      }

      let update: Partial<
        Pick<
          State,
          | 'duplicateSpaceBoxParams'
          | 'duplicateSpaceCircleParams'
          | 'duplicateSpacePolygonParams'
        >
      > = {};

      if (space.shape.type === 'box') {
        update = {
          duplicateSpaceBoxParams: {
            name: space.name,
            width: space.shape.width,
            height: space.shape.height,
            countingMode: space.countingMode,
          },
        };
      }
      if (space.shape.type === 'circle') {
        update = {
          duplicateSpaceCircleParams: {
            name: space.name,
            radius: space.shape.radius,
            countingMode: 'oa',
          },
        };
      }
      if (space.shape.type === 'polygon') {
        update = {
          duplicateSpacePolygonParams: {
            name: space.name,
            vertices: space.shape.vertices,
            originalPosition: space.position,
            countingMode: space.countingMode,
          },
        };
      }

      return {
        ...state,
        placementMode: {
          type: 'space',
          shape:
            space.shape.type === 'polygon'
              ? 'polygon-duplicate'
              : space.shape.type,
          countingMode: space.countingMode,
          mousePosition: null,
        },
        ...update,
      };
    }

    case 'menu.flipRenderOrder': {
      return {
        ...state,
        renderOrder:
          state.renderOrder === 'forwards' ? 'backwards' : 'forwards',
      };
    }

    case 'menu.planning.loadFromLocalStorage': {
      const cachedPlanningStateStr = localStorage.getItem('planningBookmark');
      if (!cachedPlanningStateStr) {
        return state;
      }

      const cachedPlanningState = JSON.parse(cachedPlanningStateStr);

      return {
        ...state,
        planning: { ...state.planning, ...cachedPlanningState },
      };
    }

    case 'menu.planning.toggleAllOff': {
      let nextState = State.blurFocusedObject(state);

      nextState = {
        ...nextState,
        planning: {
          ...Object.keys(nextState.planning).reduce((acc, key: string) => {
            acc[key] = false;
            return acc;
          }, {} as { [key: string]: boolean }),
          showFloorplanImage: true,
          showLiveStreamOpenArea: state.planning.showLiveStreamOpenArea,
          showLiveStreamOpenAreaTracks:
            state.planning.showLiveStreamOpenAreaTracks,
          showLiveStreamOpenAreaPoints:
            state.planning.showLiveStreamOpenAreaPoints,
          showLiveStreamEntry: state.planning.showLiveStreamEntry,
          showLiveStreamEntryCounts: state.planning.showLiveStreamEntryCounts,
          showLiveStreamEntryTracks: state.planning.showLiveStreamEntryTracks,
          showLiveStreamEntryPoints: state.planning.showLiveStreamEntryPoints,
          showHeatMap: state.planning.showHeatMap,
        } as Planning,
      };

      savePlanningStateToLocalStorage(nextState.planning);
      return nextState;
    }

    case 'menu.planning.toggleAllOn': {
      let nextState = State.blurFocusedObject(state);

      nextState = {
        ...nextState,
        planning: {
          ...Object.keys(nextState.planning).reduce((acc, key: string) => {
            acc[key] = true;
            return acc;
          }, {} as { [key: string]: boolean }),
          showLiveStreamOpenArea: state.planning.showLiveStreamOpenArea,
          showLiveStreamOpenAreaTracks:
            state.planning.showLiveStreamOpenAreaTracks,
          showLiveStreamOpenAreaPoints:
            state.planning.showLiveStreamOpenAreaPoints,
          showLiveStreamEntry: state.planning.showLiveStreamEntry,
          showLiveStreamEntryCounts: state.planning.showLiveStreamEntryCounts,
          showLiveStreamEntryTracks: state.planning.showLiveStreamEntryTracks,
          showLiveStreamEntryPoints: state.planning.showLiveStreamEntryPoints,
          showHeatMap: state.planning.showHeatMap,
          showCeilingHeightMap: false,
          showOpenAreaCoverageExtents: false,
          showSpaceNames: false,
        } as Planning,
      };

      savePlanningStateToLocalStorage(nextState.planning);
      return nextState;
    }

    case 'menu.planning.toggleOffAllButSpaces': {
      let nextState = state;
      if (nextState.focusedObject && nextState.focusedObject.type !== 'space') {
        nextState = State.blurFocusedObject(nextState);
      }

      nextState = {
        ...nextState,
        planning: {
          ...Object.keys(nextState.planning).reduce((acc, key: string) => {
            acc[key] = false;
            return acc;
          }, {} as { [key: string]: boolean }),
          showFloorplanImage: true,
          showSpaces: true,
          showSpacesOpenArea: true,
          showSpacesEntry: true,
          showSpaceNames: true,
          showLiveStreamOpenArea: nextState.planning.showLiveStreamOpenArea,
          showLiveStreamOpenAreaTracks:
            nextState.planning.showLiveStreamOpenAreaTracks,
          showLiveStreamOpenAreaPoints:
            nextState.planning.showLiveStreamOpenAreaPoints,
          showLiveStreamEntry: nextState.planning.showLiveStreamEntry,
          showLiveStreamEntryCounts:
            nextState.planning.showLiveStreamEntryCounts,
          showLiveStreamEntryTracks:
            nextState.planning.showLiveStreamEntryTracks,
          showLiveStreamEntryPoints:
            nextState.planning.showLiveStreamEntryPoints,
          showHeatMap: state.planning.showHeatMap,
        } as Planning,
      };

      savePlanningStateToLocalStorage(nextState.planning);
      return nextState;
    }

    case 'menu.planning.toggleOffAllButEntry': {
      let nextState = state;
      if (
        nextState.focusedObject &&
        nextState.focusedObject.type !== 'sensor'
      ) {
        nextState = State.blurFocusedObject(nextState);
      }

      nextState = {
        ...nextState,
        planning: {
          ...Object.keys(nextState.planning).reduce((acc, key: string) => {
            acc[key] = false;
            return acc;
          }, {} as { [key: string]: boolean }),
          showFloorplanImage: true,
          showSpaces: true,
          showSpacesEntry: true,
          showThresholds: true,
          showTriggerRegions: true,
          showIngressEgressRegions: true,
          showSensors: true,
          showSensorsEntry: true,
          showSensorsEntryLR: true,
          showSensorsEntryTOF: true,
          showEntryCoverage: true,
          showLiveStreamOpenArea: nextState.planning.showLiveStreamOpenArea,
          showLiveStreamOpenAreaTracks:
            nextState.planning.showLiveStreamOpenAreaTracks,
          showLiveStreamOpenAreaPoints:
            nextState.planning.showLiveStreamOpenAreaPoints,
          showLiveStreamEntry: nextState.planning.showLiveStreamEntry,
          showLiveStreamEntryTracks:
            nextState.planning.showLiveStreamEntryTracks,
          showLiveStreamEntryPoints:
            nextState.planning.showLiveStreamEntryPoints,
          showLiveStreamEntryCounts:
            nextState.planning.showLiveStreamEntryCounts,
          showHeatMap: state.planning.showHeatMap,
        } as Planning,
      };

      savePlanningStateToLocalStorage(nextState.planning);
      return nextState;
    }

    case 'menu.planning.toggleOffAllButOpenArea': {
      let nextState = state;
      if (
        nextState.focusedObject &&
        nextState.focusedObject.type !== 'sensor'
      ) {
        nextState = State.blurFocusedObject(nextState);
      }

      nextState = {
        ...nextState,
        planning: {
          ...Object.keys(nextState.planning).reduce((acc, key: string) => {
            acc[key] = false;
            return acc;
          }, {} as { [key: string]: boolean }),
          showFloorplanImage: true,
          showSpaces: true,
          showSpacesOpenArea: true,
          showSensors: true,
          showSensorsOpenArea: true,
          showOpenAreaCoverage: true,
          showSensorsOpenAreaLR: true,
          showSensorsOpenAreaMR: true,
          showSensorsOpenAreaSR: true,
          showLiveStreamOpenArea: nextState.planning.showLiveStreamOpenArea,
          showLiveStreamOpenAreaTracks:
            nextState.planning.showLiveStreamOpenAreaTracks,
          showLiveStreamOpenAreaPoints:
            nextState.planning.showLiveStreamOpenAreaPoints,
          showLiveStreamEntry: nextState.planning.showLiveStreamEntry,
          showLiveStreamEntryCounts:
            nextState.planning.showLiveStreamEntryCounts,
          showLiveStreamEntryTracks:
            nextState.planning.showLiveStreamEntryTracks,
          showLiveStreamEntryPoints:
            nextState.planning.showLiveStreamEntryPoints,
          showHeatMap: state.planning.showHeatMap,
        } as Planning,
      };

      savePlanningStateToLocalStorage(nextState.planning);
      return nextState;
    }

    case 'menu.planning.toggleSensors': {
      let nextState = state;
      // Deselect focused sensor if one is focused
      if (
        nextState.focusedObject &&
        nextState.focusedObject.type === 'sensor'
      ) {
        nextState = State.blurFocusedObject(nextState);
      }

      nextState = {
        ...nextState,
        planning: {
          ...nextState.planning,
          showSensors: !nextState.planning.showSensors,
          showSensorsEntry: !nextState.planning.showSensors,
          showSensorsOpenArea: !nextState.planning.showSensors,
          showEntryLabels: true,
          showEntryCoverage: true,
          showOpenAreaLabels: true,
          showOpenAreaCoverage: true,
          showOpenAreaCoverageExtents: false,
          showSensorNoisePolygons: false,
        },
      };

      savePlanningStateToLocalStorage(nextState.planning);
      return nextState;
    }

    case 'menu.planning.toggleFloorplan': {
      const nextState = {
        ...state,
        planning: {
          ...state.planning,
          showFloorplanImage: !state.planning.showFloorplanImage,
        },
      };

      savePlanningStateToLocalStorage(nextState.planning);
      return nextState;
    }

    case 'menu.planning.toggleSensorNoisePolygons': {
      let nextState = {
        ...state,
        planning: {
          ...state.planning,
          showSensorNoisePolygons: !state.planning.showSensorNoisePolygons,
        },
      };

      savePlanningStateToLocalStorage(nextState.planning);
      return nextState;
    }

    case 'menu.planning.toggleSensorsEntry': {
      let nextState = state;
      // Deselect focused sensor if one is focused
      if (
        nextState.focusedObject &&
        nextState.focusedObject.type === 'sensor'
      ) {
        nextState = State.blurFocusedObject(nextState);
      }

      nextState = {
        ...nextState,
        planning: {
          ...nextState.planning,
          showSensorsEntry: !nextState.planning.showSensorsEntry,
          showSensorsEntryLR: !nextState.planning.showSensorsEntry,
          showSensorsEntryTOF: !nextState.planning.showSensorsEntry,
          showThresholds: !nextState.planning.showSensorsEntry,
          showTriggerRegions: !nextState.planning.showSensorsEntry,
          showEntryLabels: !nextState.planning.showEntryLabels,
          showEntryCoverage: !nextState.planning.showEntryCoverage,
        },
      };

      savePlanningStateToLocalStorage(nextState.planning);
      return nextState;
    }

    case 'menu.planning.toggleSensorsOpenArea': {
      // Deselect focused sensor if one is focused
      let nextState = state;
      if (
        nextState.focusedObject &&
        nextState.focusedObject.type === 'sensor'
      ) {
        nextState = State.blurFocusedObject(nextState);
      }

      nextState = {
        ...nextState,
        planning: {
          ...nextState.planning,
          showSensorsOpenArea: !nextState.planning.showSensorsOpenArea,
          showSensorsOpenAreaLR: !nextState.planning.showSensorsOpenArea,
          showSensorsOpenAreaMR: !nextState.planning.showSensorsOpenArea,
          showSensorsOpenAreaSR: !nextState.planning.showSensorsOpenArea,
          showOpenAreaCoverage: !nextState.planning.showSensorsOpenArea,
          showOpenAreaCoverageExtents: false,
        },
      };

      savePlanningStateToLocalStorage(nextState.planning);
      return nextState;
    }

    case 'menu.planning.toggleEntryCountsOff': {
      const nextState = {
        ...state,
        planning: {
          ...state.planning,
          showLiveStreamEntryCounts: false,
        },
      };

      return nextState;
    }

    case 'menu.planning.toggleEntryDashOff': {
      const nextState = {
        ...state,
        planning: {
          ...state.planning,
          showLiveStreamEntryDashboard: false,
        },
      };

      return nextState;
    }

    case 'menu.planning.toggleEntryCounts': {
      const nextState = {
        ...state,
        planning: {
          ...state.planning,
          showSensors:
            !state.planning.showLiveStreamEntryCounts ||
            state.planning.showSensors,
          showSensorsEntry:
            !state.planning.showLiveStreamEntryCounts ||
            state.planning.showSensorsEntry,
          showSensorsEntryLR:
            !state.planning.showLiveStreamEntryCounts ||
            state.planning.showSensorsEntryLR,
          showSensorsEntryTOF:
            !state.planning.showLiveStreamEntryCounts ||
            state.planning.showSensorsEntryTOF,
          showLiveStreamEntryCounts: !state.planning.showLiveStreamEntryCounts,
        },
      };

      savePlanningStateToLocalStorage(nextState.planning);
      return nextState;
    }

    case 'menu.planning.toggleEntryDashboard': {
      const nextState = {
        ...state,
        planning: {
          ...state.planning,
          showLiveStreamEntryDashboard:
            !state.planning.showLiveStreamEntryDashboard,
        },
      };

      return nextState;
    }

    case 'menu.planning.toggleSensorTypeEntry': {
      const nextState = {
        ...state,
        planning: {
          ...state.planning,
          showSensors: true,
          showSensorsEntry: true,
          showSensorsEntryTOF: !state.planning.showSensorsEntryTOF,
          showLiveStreamEntryCounts:
            state.planning.showLiveStreamEntryCounts &&
            (state.planning.showSensorsEntryLR ||
              !state.planning.showSensorsEntryTOF),
        },
      };

      savePlanningStateToLocalStorage(nextState.planning);
      return nextState;
    }

    case 'menu.planning.toggleSensorTypeOpenEntry': {
      const nextState = {
        ...state,
        planning: {
          ...state.planning,
          showSensorsEntryLR: !state.planning.showSensorsEntryLR,
          showIngressEgressRegions: !state.planning.showIngressEgressRegions,
          showLiveStreamEntryCounts:
            state.planning.showLiveStreamEntryCounts &&
            (!state.planning.showSensorsEntryLR ||
              state.planning.showSensorsEntryTOF),
        },
      };

      savePlanningStateToLocalStorage(nextState.planning);
      return nextState;
    }

    case 'menu.planning.toggleIngressEgressRegions': {
      const nextState = {
        ...state,
        planning: {
          ...state.planning,
          showIngressEgressRegions: !state.planning.showIngressEgressRegions,
          showTriggerRegions:
            (!state.planning.showIngressEgressRegions &&
              !state.planning.showTriggerRegions) ||
            state.planning.showTriggerRegions,
        },
      };
      savePlanningStateToLocalStorage(nextState.planning);
      return nextState;
    }

    case 'menu.planning.toggleSensorTypeLongRange': {
      const nextState = {
        ...state,
        planning: {
          ...state.planning,
          showSensorsOpenAreaLR: !state.planning.showSensorsOpenAreaLR,
        },
      };
      savePlanningStateToLocalStorage(nextState.planning);
      return nextState;
    }

    case 'menu.planning.toggleSensorTypeMidRange': {
      const nextState = {
        ...state,
        planning: {
          ...state.planning,
          showSensorsOpenAreaMR: !state.planning.showSensorsOpenAreaMR,
        },
      };

      savePlanningStateToLocalStorage(nextState.planning);
      return nextState;
    }

    case 'menu.planning.toggleSensorTypeShortRange': {
      const nextState = {
        ...state,
        planning: {
          ...state.planning,
          showSensorsOpenAreaSR: !state.planning.showSensorsOpenAreaSR,
        },
      };

      savePlanningStateToLocalStorage(nextState.planning);
      return nextState;
    }

    case 'menu.planning.toggleEntryLabels': {
      const nextState = {
        ...state,
        planning: {
          ...state.planning,
          showEntryLabels: !state.planning.showEntryLabels,
        },
      };

      savePlanningStateToLocalStorage(nextState.planning);
      return nextState;
    }

    case 'menu.planning.toggleEntryCoverage': {
      const nextState = {
        ...state,
        planning: {
          ...state.planning,
          showEntryCoverage: !state.planning.showEntryCoverage,
        },
      };

      savePlanningStateToLocalStorage(nextState.planning);
      return nextState;
    }

    case 'menu.planning.toggleOpenAreaLabels': {
      const nextState = {
        ...state,
        planning: {
          ...state.planning,
          showOpenAreaLabels: !state.planning.showOpenAreaLabels,
        },
      };

      savePlanningStateToLocalStorage(nextState.planning);
      return nextState;
    }

    case 'menu.planning.toggleOpenAreaCoverage': {
      const nextState = {
        ...state,
        planning: {
          ...state.planning,
          showOpenAreaCoverage: !state.planning.showOpenAreaCoverage,
        },
      };

      savePlanningStateToLocalStorage(nextState.planning);
      return nextState;
    }
    case 'menu.planning.toggleOpenAreaDoorFOV': {
      const nextState = {
        ...state,
        planning: {
          ...state.planning,
          showOpenAreaDoorFOV: !state.planning.showOpenAreaDoorFOV,
        },
      };

      savePlanningStateToLocalStorage(nextState.planning);
      return nextState;
    }

    case 'menu.planning.toggleOpenAreaCoverageExtents': {
      const nextState = {
        ...state,
        planning: {
          ...state.planning,
          showOpenAreaCoverageExtents:
            !state.planning.showOpenAreaCoverageExtents,
        },
      };

      savePlanningStateToLocalStorage(nextState.planning);
      return nextState;
    }

    case 'menu.planning.toggleEntryLiveStream': {
      return {
        ...state,
        planning: {
          ...state.planning,
          showLiveStreamEntry: !state.planning.showLiveStreamEntry,
          showLiveStreamEntryPoints: false,
          showLiveStreamEntryTracks: false,
          showLiveStreamEntryCounts: false,
          showLiveStreamEntryDashboard: false,
        },
      };
    }

    case 'menu.planning.toggleOpenAreaLiveStream': {
      return {
        ...state,
        planning: {
          ...state.planning,
          showLiveStreamOpenArea: !state.planning.showLiveStreamOpenArea,
          showLiveStreamOpenAreaPoints: false,
          showLiveStreamOpenAreaTracks: false,
        },
      };
    }

    case 'menu.planning.toggleOpenAreaPoints': {
      return {
        ...state,
        planning: {
          ...state.planning,
          showLiveStreamOpenAreaPoints:
            !state.planning.showLiveStreamOpenAreaPoints,
        },
      };
    }

    case 'menu.planning.toggleEntryPoints': {
      return {
        ...state,
        planning: {
          ...state.planning,
          showLiveStreamEntryPoints: !state.planning.showLiveStreamEntryPoints,
        },
      };
    }

    case 'menu.planning.toggleEntryTracks': {
      return {
        ...state,
        planning: {
          ...state.planning,
          showLiveStreamEntryTracks: !state.planning.showLiveStreamEntryTracks,
        },
      };
    }

    case 'menu.planning.toggleOpenAreaTracks': {
      return {
        ...state,
        planning: {
          ...state.planning,
          showLiveStreamOpenAreaTracks:
            !state.planning.showLiveStreamOpenAreaTracks,
        },
      };
    }

    case 'menu.planning.toggleAreasOfConcern': {
      // Deselect focused area of concern if one is focused
      if (state.focusedObject && state.focusedObject.type === 'areaofconcern') {
        state = State.blurFocusedObject(state);
      }

      const nextState = {
        ...state,
        planning: {
          ...state.planning,
          showAreasOfConcern: !state.planning.showAreasOfConcern,
        },
      };

      savePlanningStateToLocalStorage(nextState.planning);
      return nextState;
    }

    case 'menu.planning.toggleSpaces': {
      // Deselect focused space / area if one is focused
      if (state.focusedObject && state.focusedObject.type === 'space') {
        state = State.blurFocusedObject(state);
      }

      const nextState = {
        ...state,
        planning: {
          ...state.planning,
          showSpaces: !state.planning.showSpaces,
          showSpacesOpenArea: !state.planning.showSpaces,
          showSpacesEntry: !state.planning.showSpaces,
          showSpaceNames: false,
        },
      };

      savePlanningStateToLocalStorage(nextState.planning);
      return nextState;
    }

    case 'menu.planning.toggleSpacesEntry': {
      // Deselect focused space / area if one is focused
      // TODO: Entry spaces
      /*
      if (state.focusedObject && state.focusedObject.type === 'space') {
        state = State.blurFocusedObject(state);
      }
      */

      const nextState = {
        ...state,
        planning: {
          ...state.planning,
          showSpaces: true,
          showSpacesEntry: !state.planning.showSpacesEntry,
        },
      };

      savePlanningStateToLocalStorage(nextState.planning);
      return nextState;
    }

    case 'menu.planning.toggleSpacesOpenArea': {
      // Deselect focused space / area if one is focused
      // TODO: Entry spaces
      /*
      if (state.focusedObject && state.focusedObject.type === 'space') {
        state = State.blurFocusedObject(state);
      }
      */

      const nextState = {
        ...state,
        planning: {
          ...state.planning,
          showSpaces: true,
          showSpacesOpenArea: !state.planning.showSpacesOpenArea,
        },
      };

      savePlanningStateToLocalStorage(nextState.planning);
      return nextState;
    }

    case 'menu.planning.toggleThresholds': {
      const nextState = {
        ...state,
        planning: {
          ...state.planning,
          showThresholds: !state.planning.showThresholds,
        },
      };

      savePlanningStateToLocalStorage(nextState.planning);
      return nextState;
    }

    case 'menu.planning.toggleTriggerRegions': {
      const nextState = {
        ...state,
        planning: {
          ...state.planning,
          showTriggerRegions: !state.planning.showTriggerRegions,
          showIngressEgressRegions: state.planning.showTriggerRegions
            ? false
            : state.planning.showIngressEgressRegions,
        },
      };

      savePlanningStateToLocalStorage(nextState.planning);
      return nextState;
    }

    case 'menu.planning.toggleSensorTracks': {
      const nextState = {
        ...state,
        planning: {
          ...state.planning,
          showSensorTracks: !state.planning.showSensorTracks,
        },
      };
      savePlanningStateToLocalStorage(nextState.planning);
      return nextState;
    }

    case 'menu.planning.toggleSpaceNames': {
      const nextState = {
        ...state,
        planning: {
          ...state.planning,
          showSpaceNames: !state.planning.showSpaceNames,
        },
      };

      savePlanningStateToLocalStorage(nextState.planning);
      return nextState;
    }

    case 'menu.planning.togglePhotoGroups': {
      state = State.blurPhotoGroup(state);
      const nextState = {
        ...state,
        planning: {
          ...state.planning,
          showPhotoGroups: !state.planning.showPhotoGroups,
        },
      };

      savePlanningStateToLocalStorage(nextState.planning);
      return nextState;
    }

    case 'menu.planning.toggleCeilingHeightMap': {
      const nextState = {
        ...state,
        planning: {
          ...state.planning,
          showCeilingHeightMap: !state.planning.showCeilingHeightMap,
        },
      };

      savePlanningStateToLocalStorage(nextState.planning);
      return nextState;
    }

    case 'menu.planning.toggleWalls': {
      const nextState = {
        ...state,
        planning: {
          ...state.planning,
          showWalls: !state.planning.showWalls,
        },
      };

      savePlanningStateToLocalStorage(nextState.planning);
      return nextState;
    }

    case 'menu.planning.toggleHeatMap': {
      const newShowHeatMap = !state.planning.showHeatMap;

      // Turn on the heatmap if this is the first time the layer is being shown
      let nextHeatmap = state.heatmap;
      if (newShowHeatMap && !nextHeatmap.enabled) {
        nextHeatmap = { ...Heatmap.create(), enabled: true };
      }

      let nextState = state;

      if (!state.planning.showHeatMap)
        nextState = State.focusLayer(state, LayerId.HEATMAP);

      return {
        ...nextState,
        planning: {
          ...state.planning,
          showHeatMap: newShowHeatMap,
        },
        heatmap: nextHeatmap,
      };
    }

    case 'menu.planning.toggleRulers': {
      const nextState = {
        ...state,
        planning: {
          ...state.planning,
          showRulers: !state.planning.showRulers,
        },
      };

      savePlanningStateToLocalStorage(nextState.planning);
      return nextState;
    }

    case 'menu.planning.toggleHeights': {
      const nextState = {
        ...state,
        planning: {
          ...state.planning,
          showHeights: !state.planning.showHeights,
        },
      };

      savePlanningStateToLocalStorage(nextState.planning);
      return nextState;
    }

    case 'menu.planning.toggleScale': {
      const nextState = {
        ...state,
        planning: {
          ...state.planning,
          showScale: !state.planning.showScale,
        },
      };

      savePlanningStateToLocalStorage(nextState.planning);
      return nextState;
    }

    case 'sensorList.item.mouseenter': {
      return State.highlightSensor(state, action.id);
    }
    case 'sensorList.item.mouseleave': {
      return State.unhighlight(state);
    }
    case 'spaceList.item.mouseenter': {
      return State.highlightSpace(state, action.id);
    }
    case 'spaceList.item.mouseleave': {
      return State.unhighlight(state);
    }
    case 'algorithm.releaseExpiredAggregateData': {
      return {
        ...state,
        aggregatedPointsData: AggregatedData.releaseExpiredData(
          state.aggregatedPointsData
        ),
      };
    }

    case 'algorithm.spaceOccupancyConfidenceChange': {
      const { confidence } = action;

      const RISING_THRESHOLD = 0.75;
      const FALLING_THRESHOLD = 0.25;

      const prevValue = state.spaceOccupancy.get(action.id);
      if (typeof prevValue === 'undefined') {
        throw new Error(`No spaceOccupancy entry for id ${action.id}`);
      }

      const nextValue = {
        ...prevValue,
        confidence,
        lastUpdate: action.timestamp,
      };

      const timeDelta = action.timestamp - prevValue.lastUpdate;

      if (prevValue.occupied) {
        if (confidence < FALLING_THRESHOLD) {
          nextValue.occupied = false;
        }
        nextValue.dwellTime += timeDelta;
      } else {
        if (confidence > RISING_THRESHOLD) {
          nextValue.occupied = true;
        }
      }

      const nextSpaceOccupancy = new Map(state.spaceOccupancy);
      nextSpaceOccupancy.set(action.id, nextValue);

      return {
        ...state,
        spaceOccupancy: nextSpaceOccupancy,
      };
    }

    case 'item.menu.mouseenter':
    case 'item.graphic.mouseenter': {
      const { itemType } = action;
      if (
        itemType === 'sensor' ||
        itemType === 'areaofconcern' ||
        itemType === 'space' ||
        itemType === 'reference' ||
        itemType === 'photogroup' ||
        itemType === 'threshold'
      ) {
        return State.highlightItem(state, itemType, action.itemId);
      }
      return state;
    }

    case 'item.menu.mouseleave':
    case 'item.graphic.mouseleave': {
      const { itemType } = action;
      if (
        itemType === 'sensor' ||
        itemType === 'areaofconcern' ||
        itemType === 'space' ||
        itemType === 'reference' ||
        itemType === 'photogroup' ||
        itemType === 'threshold'
      ) {
        return State.unhighlight(state);
      }
      return state;
    }

    case 'item.menu.mousedown': {
      const { itemType } = action;
      // dismiss the upper right hand panels if open
      let nextState = {
        ...state,
        panels: {
          settingsOpen: false,
          csvOpen: false,
          dxfOpen: false,
        },
      };

      if (itemType === 'sensor') {
        return State.focusSensor(nextState, action.itemId);
      } else if (itemType === 'areaofconcern') {
        return State.focusAreaOfConcern(nextState, action.itemId);
      } else if (itemType === 'space') {
        return State.focusSpace(nextState, action.itemId);
      } else if (itemType === 'threshold') {
        return State.focusThreshold(nextState, action.itemId);
      } else if (itemType === 'photogroup') {
        return State.focusPhotoGroup(nextState, action.itemId);
      }
      return nextState;
    }
    case 'item.graphic.clearCopy': {
      return {
        ...state,
        copiedEntitySettings: {
          ...state.copiedEntitySettings!,
          id: '',
          space: undefined,
          sensor: undefined,
        },
      };
    }
    // Right clicking functionality
    case 'item.graphic.rightclick': {
      let { itemType, sensor, space, itemId } = action;
      // dismiss the upper right hand panels if open
      let nextState = {
        ...state,
        panels: {
          settingsOpen: false,
          csvOpen: false,
          dxfOpen: false,
        },
      };

      if (
        state.copiedEntitySettings &&
        state.copiedEntitySettings.type !== itemType
      ) {
        if (state.copiedEntitySettings.type === PastableEntity.SPACE)
          nextState = {
            ...nextState,
            entityPasteMode: {
              mode: PasteChoice.TYPE,
              affix: 'post',
              amount: 1,
            },
          };
        if (state.copiedEntitySettings.type === PastableEntity.SENSOR)
          nextState = {
            ...nextState,
            entityPasteMode: {
              mode: PasteChoice.SHAPE,
              affix: 'post',
              amount: 1,
            },
          };
      }
      // if there's a copy set and the entity was selected again, uncopy it
      if (action.shiftKey && itemId === state.copiedEntitySettings?.id) {
        return {
          ...nextState,
          copiedEntitySettings: {
            id: '',
            type: state.copiedEntitySettings.type, // setting this to the same as last entity type so paste mode doesn't reset on new entity select of same type
          },
        };
      }

      // copy the sensor settings if the shift key is pressed
      if (itemType === 'sensor' && action.shiftKey && sensor) {
        return {
          ...nextState,
          copiedEntitySettings: {
            id: sensor.id,
            type: PastableEntity.SENSOR,
            sensor: {
              height: sensor.height,
              sensorFunction: sensor.sensorFunction,
              rotation: sensor.rotation,
            },
          },
        };
      }
      if (itemType === 'space' && action.shiftKey && space) {
        const shape =
          space.shape.type === 'box'
            ? {
                type: space.shape.type,
                height: space.shape.height,
                width: space.shape.width,
              }
            : space.shape.type === 'polygon'
            ? { type: space.shape.type, vertices: space.shape.vertices }
            : { type: space.shape.type, radius: space.shape.radius };

        return {
          ...nextState,
          entityPasteMode: {
            ...state.entityPasteMode,
            amount: 1,
          },
          copiedEntitySettings: {
            id: itemId,
            type: PastableEntity.SPACE,
            space: {
              name: space.name,
              shape,
              position: space.position,
              coreSpaceLabels: space.coreSpaceLabels,
              coreSpaceFunction: space.coreSpaceFunction,
              coreSpaceCapacity: space.coreSpaceCapacity,
              countingMode: space.countingMode,
            },
          },
        };
      }

      return nextState;
    }
    case 'item.graphic.mousedown': {
      let { itemType, itemId, itemPosition } = action;

      // dismiss the upper right hand panels if open
      let nextState = {
        ...state,
        panels: {
          settingsOpen: false,
          csvOpen: false,
          dxfOpen: false,
        },
      };

      // If ctrlKey is pressed, we want to be able to highlight multiple objects, but not focus any of them.
      // If the object isnt already added to bulkSelection.ids, add it, otherwise remove.
      if ((nextState.keys.ctrl || action.ctrlKey) && !nextState.locked) {
        const selectedIds = nextState.bulkSelection.ids;

        // Deselect already selected item ID -
        if (selectedIds.has(itemId)) {
          selectedIds.delete(itemId);
        } else {
          selectedIds.add(itemId);
        }

        // Add already focused object and blur it at the same time -
        if (nextState.focusedObject) {
          selectedIds.add(nextState.focusedObject.id);
          nextState = State.blurFocusedObject(nextState);
        }

        return {
          ...nextState,
          bulkSelection: {
            ...nextState.bulkSelection,
            ids: selectedIds,
          },
        };
      }

      // Regular focus logic -
      const position = ViewportCoordinates.toFloorplanCoordinates(
        itemPosition,
        nextState.viewport,
        nextState.floorplan
      );
      if (itemType === 'sensor') {
        nextState = State.focusSensor(nextState, action.itemId);
      } else if (itemType === 'areaofconcern') {
        nextState = State.focusAreaOfConcern(nextState, action.itemId);
      } else if (itemType === 'space') {
        nextState = State.focusSpace(nextState, action.itemId);
      } else if (itemType === 'photogroup') {
        nextState = State.focusPhotoGroup(nextState, action.itemId);
      } else if (itemType === 'threshold') {
        nextState = State.focusThreshold(nextState, action.itemId);
      } else if (itemType === 'reference' && action.altKey) {
        const reference = nextState.references.items.get(itemId);
        if (!reference) {
          throw new Error(
            'Reference duplicate dragged but original did not exist!'
          );
        }

        let newReference: Reference;
        if (reference.type === 'ruler') {
          newReference = Reference.createRuler(
            reference.positionA,
            reference.positionB
          );
        } else if (reference.type === 'height') {
          newReference = Reference.createHeight(position);
        } else {
          return nextState;
        }
        nextState = State.addReference(nextState, newReference);
        itemId = newReference.id;
      } else if (itemType === 'reference') {
        nextState = State.updateReference<ReferenceRuler>(
          nextState,
          action.itemId,
          (reference) => {
            // Cache the "isDistanceLockedToCenter" value so that the label reference point stays
            // pinned to the center if needed when the whole reference line moves
            const centerPoint = ReferenceRuler.calculateCenterPoint(reference);
            const isDistanceLockedToCenter =
              ReferenceRuler.isDistanceLockedToCenter(
                nextState.floorplan,
                nextState.viewport,
                reference.distanceLabelPosition,
                centerPoint
              );
            return {
              currentIsDistanceLockedToCenter: isDistanceLockedToCenter,
            };
          }
        );
      }

      // Preserve bulk selection when alt-clicking a space that's already part
      // of the selection — enables option-drag to clone the whole group.
      const preserveBulkSelectionForAltClone =
        action.altKey &&
        itemType === 'space' &&
        nextState.bulkSelection.ids.has(action.itemId);

      return {
        ...nextState,
        // If a single entity was selected without Ctrl pressed, clear the bulk selection
        bulkSelection: {
          ...nextState.bulkSelection,
          ids: preserveBulkSelectionForAltClone
            ? nextState.bulkSelection.ids
            : new Set<string>(),
        },
        // Reset render order when a graphic is selected
        renderOrder: 'forwards',
      };
    }

    case 'item.graphic.dragmove': {
      const position = action.itemPosition;

      const nextState = state;
      if (nextState.bulkSelection.ids.size > 0) {
        return nextState;
      }

      const { itemType, itemId } = action;
      if (itemType === 'sensor') {
        return State.updateSensor(nextState, itemId, {
          position,
        });
      }
      if (itemType === 'space') {
        return State.updateSpace(nextState, itemId, (space) => {
          if (space.shape.type !== 'polygon') {
            return { ...space, position };
          }

          // Polygonal spaces should have all their vertices translated the same amount as the
          // center
          const positionDeltaX = position.x - space.position.x;
          const positionDeltaY = position.y - space.position.y;
          return {
            ...space,
            position,
            shape: {
              ...space.shape,
              vertices: space.shape.vertices.map((v) =>
                FloorplanCoordinates.create(
                  v.x + positionDeltaX,
                  v.y + positionDeltaY
                )
              ),
            },
          };
        });
      }
      if (itemType === 'areaofconcern') {
        return State.updateAreaOfConcern(nextState, itemId, (areaOfConcern) => {
          // Areas of concern should have all their vertices translated the same amount
          // as the center
          const positionDeltaX = position.x - areaOfConcern.position.x;
          const positionDeltaY = position.y - areaOfConcern.position.y;

          return {
            ...areaOfConcern,
            position,
            sensorsOrigin: FloorplanCoordinates.create(
              areaOfConcern.sensorsOrigin.x + positionDeltaX,
              areaOfConcern.sensorsOrigin.y + positionDeltaY
            ),
            vertices: areaOfConcern.vertices.map((v) =>
              FloorplanCoordinates.create(
                v.x + positionDeltaX,
                v.y + positionDeltaY
              )
            ),

            // Recalculate sensor placements
            sensorPlacements: { type: 'empty' },
          };
        });
      }
      if (itemType === 'reference') {
        const reference = state.references.items.get(itemId);
        if (!reference) {
          return state;
        }

        switch (reference.type) {
          case 'ruler': {
            const previousPosition = action.itemPosition;
            const dx = position.x - previousPosition.x;
            const dy = position.y - previousPosition.y;

            const ruler = state.references.items.get(itemId) as
              | ReferenceRuler
              | undefined;

            if (!ruler) {
              return state;
            }
            const positionA = {
              ...ruler.positionA,
              x: ruler.positionA.x + dx,
              y: ruler.positionA.y + dy,
            };
            const positionB = {
              ...ruler.positionB,
              x: ruler.positionB.x + dx,
              y: ruler.positionB.y + dy,
            };

            // Move distance marker to keep it in the center if needed
            let distanceLabelPosition = ruler.distanceLabelPosition;
            if (reference.currentIsDistanceLockedToCenter) {
              const centerPoint = ReferenceRuler.calculateCenterPoint(ruler);
              distanceLabelPosition = {
                ...distanceLabelPosition,
                x: centerPoint.x,
                y: centerPoint.y,
              };
            }

            return State.updateReference<ReferenceRuler>(nextState, itemId, {
              positionA,
              positionB,
              distanceLabelPosition,
            });
          }
          case 'height': {
            return State.updateReference<ReferenceHeight>(nextState, itemId, {
              position,
            });
          }
        }
      }
      if (itemType === 'photogroup') {
        return State.updatePhotoGroup(nextState, itemId, (photoGroup) => {
          return {
            position,
            operationToPerform: PhotoGroup.computeOperationAfterModification(
              photoGroup.operationToPerform
            ),
          };
        });
      }
      return nextState;
    }

    case 'item.graphic.dragend': {
      return state;
    }

    case 'save.pending': {
      return { ...state, savePending: true };
    }

    case 'save.error': {
      // FIXME: for now this has no effect
      return { ...state, savePending: false };
    }

    case 'save.success': {
      return action.newState;
    }

    case 'csvImport.uploadStarted': {
      return {
        ...state,
        csvImport: {
          ...state.csvImport,
          uploading: true,
        },
      };
    }

    case 'csvImport.uploadCompleted': {
      return {
        ...state,
        csvImport: {
          ...state.csvImport,
          uploading: false,
        },
      };
    }

    case 'csvImport.begin': {
      return {
        ...state,
        csvImport: {
          ...state.csvImport,
          active: true,
          mutations: action.mutations,
          csvErrors: action.csvErrors,
          csvUpdates: action.csvUpdates,
        },
      };
    }

    case 'csvImport.cancel': {
      return {
        ...state,
        csvImport: {
          uploading: false,
          active: false,
          mutations: null,
          csvErrors: null,
          csvUpdates: null,
        },
        panels: {
          ...state.panels,
          csvOpen: false,
        },
      };
    }

    case 'spaceMetaCSVImport.uploadStarted': {
      return {
        ...state,
        spaceMetaCSVImport: {
          ...state.spaceMetaCSVImport,
          uploading: true,
        },
      };
    }

    case 'spaceMetaCSVImport.uploadCompleted': {
      return {
        ...state,
        spaceMetaCSVImport: {
          ...state.spaceMetaCSVImport,
          uploading: false,
        },
      };
    }

    case 'spaceMetaCSVImport.begin': {
      return {
        ...state,
        spaceMetaCSVImport: {
          ...state.spaceMetaCSVImport,
          active: true,
          spaceMetaCsvErrors: action.spaceMetaCsvErrors,
          spaceMetaCsvUpdates: action.spaceMetaCsvUpdates,
        },
      };
    }

    case 'spaceMetaCSVImport.cancel': {
      return {
        ...state,
        spaceMetaCSVImport: {
          uploading: false,
          active: false,
          spaceMetaCsvErrors: null,
          spaceMetaCsvUpdates: null,
        },
        panels: {
          ...state.panels,
          csvOpen: false,
        },
      };
    }

    case 'latestDXF.uploadBegin': {
      let nextState = State.blurFocusedObject(state);
      nextState = State.blurPhotoGroup(nextState);
      return {
        ...nextState,
        latestDXF: {
          status: 'uploading',
        },
      };
    }
    case 'latestDXF.uploadProgress': {
      if (!state.latestDXF || state.latestDXF.status !== 'uploading') {
        return state;
      }
      return {
        ...state,
        latestDXF: {
          ...state.latestDXF,
          fileUploadPercent: action.fileUploadPercent,
        },
      };
    }
    case 'latestDXF.uploadComplete': {
      if (!state.latestDXF) {
        return state;
      }
      return {
        ...state,
        latestDXF: {
          status: 'created',
          id: action.planDXF.id,
          createdAt: action.planDXF.created_at,
        },
      };
    }
    case 'latestDXF.uploadError': {
      return {
        ...state,
        latestDXF: {
          status: 'upload_error',
        },
      };
    }
    case 'latestDXF.update': {
      if (
        state.latestDXF &&
        state.latestDXF.status !== 'uploading' &&
        state.latestDXF.status !== 'upload_error' &&
        state.latestDXF.id === action.planDXF.id
      ) {
        return {
          ...state,
          latestDXF: {
            status: action.planDXF.status,
            id: action.planDXF.id,
            createdAt: action.planDXF.created_at,
          },
        };
      }
      return state;
    }
    case 'latestDXF.edit': {
      // This is where the DXF was fully processed and we're
      // about to enter the "compare changes" workflow.
      if (
        !state.latestDXF ||
        state.latestDXF.status === 'uploading' ||
        state.latestDXF.status === 'upload_error'
      ) {
        return state;
      }

      let nextState = State.blurFocusedObject(state);
      nextState = State.blurPhotoGroup(nextState);

      return {
        ...nextState,
        latestDXFEdit: {
          active: true,
          planDXF: parsePlanDXF(action.planDXF),
          cadFileUnit: action.planDXF.length_unit,
          cadFileScale: action.planDXF.scale || 1,
          pixelsPerCADUnit:
            action.fullImageAsset.pixels_per_unit ||
            DEFAULT_PIXELS_PER_CAD_UNIT,
          floorplanCADOrigin: state.floorplanCADOrigin,
          loading: false,
        },
      };
    }
    case 'latestDXFEdit.changeUnit': {
      if (!state.latestDXFEdit.active) {
        return state;
      }
      return {
        ...state,
        latestDXFEdit: {
          ...state.latestDXFEdit,
          cadFileUnit: action.unit,
        },
      };
    }
    case 'latestDXFEdit.changeScale': {
      if (!state.latestDXFEdit.active) {
        return state;
      }
      return {
        ...state,
        latestDXFEdit: {
          ...state.latestDXFEdit,
          cadFileScale: action.scale,
        },
      };
    }
    case 'latestDXFEdit.changeOASensorLayer': {
      if (!state.latestDXFEdit.active) {
        return state;
      }
      return {
        ...state,
        latestDXFEdit: {
          ...state.latestDXFEdit,
        },
      };
    }
    case 'latestDXFEdit.changeEntrySensorLayer': {
      if (!state.latestDXFEdit.active) {
        return state;
      }
      return {
        ...state,
        latestDXFEdit: {
          ...state.latestDXFEdit,
        },
      };
    }
    case 'latestDXFEdit.changeFloorplanCADOrigin': {
      if (!state.latestDXFEdit.active) {
        return state;
      }
      return {
        ...state,
        latestDXFEdit: {
          ...state.latestDXFEdit,
          floorplanCADOrigin: action.coords,
        },
      };
    }
    case 'latestDXFEdit.beginAsyncOperation': {
      if (!state.latestDXFEdit.active) {
        return state;
      }
      return {
        ...state,
        latestDXFEdit: {
          ...state.latestDXFEdit,
          loading: true,
        },
      };
    }
    case 'latestDXFEdit.cancel': {
      return {
        ...state,
        latestDXFEdit: {
          active: false,
        },
      };
    }
    case 'latestDXFEdit.applyFloorplanChanges': {
      // FIXME: For some reason, this action gets called twice.
      //        This is not critical but this is the reason why
      //        sensors get created twice on new DXF import.

      const cadFileUnitOrDefault = action.cadFileUnitOrDefault;
      const cadFileScaleOrDefault = action.cadFileScaleOrDefault;

      let newFloorplan: Floorplan;
      if (action.mode === 'create') {
        const initialFloorplan = {
          width: action.newBaseImage.width,
          height: action.newBaseImage.height,
          scale: 1,
          origin: ImageCoordinates.create(0, 0),
          rotation: 0,
        };

        // Calcualte the initial scale for the floorplan
        const coordA = CADCoordinates.toFloorplanCoordinates(
          CADCoordinates.create(0, 0),
          initialFloorplan,
          FloorplanCoordinates.create(0, 0),
          cadFileUnitOrDefault,
          cadFileScaleOrDefault
        );
        const coordB = CADCoordinates.toFloorplanCoordinates(
          CADCoordinates.create(1, 0),
          initialFloorplan,
          FloorplanCoordinates.create(0, 0),
          cadFileUnitOrDefault,
          cadFileScaleOrDefault
        );
        const metersPerCADUnit = coordB.x - coordA.x;
        const ratio = metersPerCADUnit / action.pixelsPerCADUnit;
        const cadImageScale = ratio * initialFloorplan.scale;
        newFloorplan = {
          ...initialFloorplan,
          scale: (1 / cadImageScale) * action.imageResizeScale,
        };
      } else {
        newFloorplan = computeNewFloorplanForCAD(
          state.floorplan,
          action.newBaseImage,
          state.floorplanCADOrigin,
          cadFileUnitOrDefault,
          cadFileScaleOrDefault,
          action.imageResizeScale,
          action.pixelsPerCADUnit
        );
      }
      const newFloorplanCADOrigin = computeDefaultCADOrigin(newFloorplan);
      let nextState = state;

      // Offset all sensors by the change floorplan cad origin so that they align with the dxf's new
      // coordinate system.
      //
      // NOTE: If the `imports` includes `sensors` then all the sensors will be overriden
      // later so this doesn't really do anything.
      const floorplanCADOrigin = action.floorplanCADOrigin;
      for (const sensor of Array.from(state.planSensors.items.values())) {
        nextState = State.updateSensor(
          nextState,
          sensor.id,
          (oldSensor) => {
            return {
              ...oldSensor,
              position: FloorplanCoordinates.create(
                oldSensor.position.x +
                  (newFloorplanCADOrigin.x - floorplanCADOrigin.x),
                oldSensor.position.y +
                  (newFloorplanCADOrigin.y - floorplanCADOrigin.y)
              ),
            };
          },
          true
        );
      }

      for (let change of action.floorplanSpacesChanges) {
        switch (change.type) {
          case 'addition':
            const space = Space.createFromParsedPlanDXFSpace(
              change.data,
              newFloorplan,
              newFloorplanCADOrigin,
              cadFileUnitOrDefault,
              cadFileScaleOrDefault
            );
            nextState = State.addSpace(nextState, space, true);
            break;
          case 'deletion':
            nextState = State.removeSpace(nextState, change.data.id, true);
            break;
          case 'modification':
            // Not supported nowadays...
            /*
            const changeData = change.data;
            nextState = State.updateSensor(
              nextState,
              change.oldData.id,
              (sensor: PlanSensor) => {
                return {
                  ...sensor,
                  ...changeData,
                  position: CADCoordinates.toFloorplanCoordinates(
                    changeData.position,
                    newFloorplan,
                    newFloorplanCADOrigin,
                    cadFileUnitOrDefault,
                    cadFileScaleOrDefault
                  ),
                };
              },
              true
            );
            */
            break;
          case 'no-change':
            break;
        }
      }

      // NOTE: If the `imports` includes `areasOfConcern` then all the AOCs will be overriden
      // later so this doesn't really do anything.
      for (const areaOfConcern of Array.from(
        state.areasOfConcern.items.values()
      )) {
        nextState = State.updateAreaOfConcern(
          nextState,
          areaOfConcern.id,
          (oldAreaOfConcern) => {
            return {
              ...oldAreaOfConcern,
              position: FloorplanCoordinates.create(
                oldAreaOfConcern.position.x +
                  (newFloorplanCADOrigin.x - floorplanCADOrigin.x),
                oldAreaOfConcern.position.y +
                  (newFloorplanCADOrigin.y - floorplanCADOrigin.y)
              ),
              vertices: oldAreaOfConcern.vertices.map((v) =>
                FloorplanCoordinates.create(
                  v.x + (newFloorplanCADOrigin.x - floorplanCADOrigin.x),
                  v.y + (newFloorplanCADOrigin.y - floorplanCADOrigin.y)
                )
              ),
            };
          },
          true
        );
      }

      for (let change of action.floorplanSensorsChanges) {
        switch (change.type) {
          case 'addition':
            const planSensor = PlanSensor.createFromCADSensorPlacement(
              change.data,
              state.planSensors,
              newFloorplan,
              newFloorplanCADOrigin,
              cadFileUnitOrDefault,
              cadFileScaleOrDefault
            );
            nextState = State.addSensor(nextState, planSensor, true);
            break;
          case 'deletion':
            nextState = State.removeSensor(nextState, change.data.id, true);
            break;
          case 'modification':
            const changeData = change.data;
            nextState = State.updateSensor(
              nextState,
              change.oldData.id,
              (sensor: PlanSensor) => {
                return {
                  ...sensor,
                  ...changeData,
                  position: CADCoordinates.toFloorplanCoordinates(
                    changeData.position,
                    newFloorplan,
                    newFloorplanCADOrigin,
                    cadFileUnitOrDefault,
                    cadFileScaleOrDefault
                  ),
                };
              },
              true
            );
            break;
          case 'no-change':
            break;
        }
      }

      for (let change of action.floorplanAreasOfConcernChanges) {
        switch (change.type) {
          case 'addition':
            const areaOfConcern = AreaOfConcern.createFromCADAreaOfConcern(
              change.data,
              state.areasOfConcern,
              newFloorplan,
              newFloorplanCADOrigin,
              cadFileUnitOrDefault,
              cadFileScaleOrDefault
            );
            nextState = State.addAreaOfConcern(nextState, areaOfConcern, true);
            break;
          case 'deletion':
            nextState = State.removeAreaOfConcern(
              nextState,
              change.data.id,
              true
            );
            break;
          case 'modification':
            /*
            const changeData = change.data;
            nextState = State.updateSensor(
              nextState,
              change.oldData.id,
              (sensor: PlanSensor) => {
                return {
                  ...sensor,
                  ...changeData,
                  position: CADCoordinates.toFloorplanCoordinates(
                    changeData.position,
                    newFloorplan,
                    newFloorplanCADOrigin,
                    cadFileUnitOrDefault,
                    cadFileScaleOrDefault
                  ),
                };
              },
              true
            );
            */
            break;
          case 'no-change':
            break;
        }
      }

      const measurementDistanceInPixels = action.newBaseImage.width;
      const measurementDistanceInMeters =
        measurementDistanceInPixels / newFloorplan.scale;
      const [feet, inches] = Meters.toFeetAndInches(
        measurementDistanceInMeters
      );

      const newMeasurement = {
        pointA: ImageCoordinates.create(0, 0),
        pointB: ImageCoordinates.create(measurementDistanceInPixels, 0),
        userEnteredLength: {
          feetText: `${feet}`,
          inchesText: `${inches}`,
        },
        computedLength: measurementDistanceInMeters,
        computedScale: newFloorplan.scale,
      };

      return {
        ...nextState,
        floorplanImage: action.newBaseImage,
        floorplanImageKey: action.newBaseImageKey,
        floorplanCADOrigin: newFloorplanCADOrigin,
        floorplan: newFloorplan,
        measurement: newMeasurement,
        activeDXFId: action.activeDXFId,
        latestDXFEdit: {
          active: false,
        },
      };
    }
    case 'latestDXFEdit.invertFloorplanChanges': {
      // TODO FIXME: Add support for inverting Spaces/AreaOfConcern imports.
      let nextState = state;
      for (let change of action.floorplanSensorsChanges) {
        switch (change.type) {
          case 'addition':
            // NOTE: when inverting an addition, there is unfortunately not a place where the id was
            // stored for the sensor that was created.
            //
            // Look up a sensor that has the same CAD ID and assume that is the sensor the was
            // created.
            const cadId = change.data.cadId;
            const sensorWithCadId = FloorplanCollection.list(
              state.planSensors
            ).find((sensor) => sensor.cadId === cadId);
            if (sensorWithCadId) {
              nextState = State.removeSensor(
                nextState,
                sensorWithCadId.id,
                true
              );
            }
            break;
          case 'deletion':
            const sensor = action.oldPlanSensors.items.get(change.data.id);
            if (sensor) {
              nextState = State.addSensor(nextState, sensor, true);
            }
            break;
          case 'modification':
            nextState = State.updateSensor(
              nextState,
              change.oldData.id,
              (sensor: PlanSensor) => {
                const oldPlanSensor = action.oldPlanSensors.items.get(
                  sensor.id
                );
                if (!oldPlanSensor) {
                  return sensor;
                }
                return {
                  ...sensor,
                  cadId: oldPlanSensor.cadId,
                  serialNumber: oldPlanSensor.serialNumber,
                  height: oldPlanSensor.height,
                  rotation: oldPlanSensor.rotation,
                  position: oldPlanSensor.position,
                };
              },
              true
            );
            break;
          case 'no-change':
            break;
        }
      }

      return {
        ...nextState,
        floorplanImage: action.floorplanImage,
        floorplanImageKey: action.floorplanImageKey,
        floorplanCADOrigin: action.floorplanCADOrigin,
        floorplan: action.floorplan,
        measurement: action.measurement,
      };
    }

    case 'export.begin': {
      return { ...state, activeExport: { status: 'requesting' } };
    }
    case 'export.beginFailed': {
      return { ...state, activeExport: { status: 'request-failed' } };
    }
    case 'export.update': {
      if (action.force) {
        return { ...state, activeExport: action.planExport };
      }

      // For non forced updates, the plan export id of the incoming update must match the plan
      // export id in the store for the active export
      if (
        state.activeExport &&
        state.activeExport.status !== 'requesting' &&
        state.activeExport.status !== 'request-failed' &&
        state.activeExport.status !== 'saving' &&
        state.activeExport.status !== 'saving-complete' &&
        state.activeExport.status !== 'saving-failed' &&
        state.activeExport.id === action.planExport.id
      ) {
        return { ...state, activeExport: action.planExport };
      }

      return state;
    }
    case 'export.saving': {
      return { ...state, activeExport: { status: 'saving' } };
    }
    case 'export.savingComplete': {
      return { ...state, activeExport: { status: 'saving-complete' } };
    }
    case 'export.savingFailed': {
      return { ...state, activeExport: { status: 'saving-failed' } };
    }
    case 'export.reset': {
      return { ...state, activeExport: null };
    }

    case 'layers.heightMap.focus': {
      const nextState = State.focusLayer(state, LayerId.HEIGHTMAP);
      if (nextState.heightMap.enabled) {
        return {
          ...nextState,
          planning: { ...nextState.planning, showCeilingHeightMap: true },
        };
      }
      return nextState;
    }

    case 'layers.heightMap.mouseenter': {
      return State.highlightLayer(state, LayerId.HEIGHTMAP);
    }

    case 'layers.heightMap.mouseleave': {
      return State.unhighlight(state);
    }

    case 'layers.walls.focus': {
      const nextState = State.focusLayer(state, LayerId.WALLS);
      if (!FloorplanCollection.isEmpty(nextState.walls)) {
        return {
          ...nextState,
          planning: { ...nextState.planning, showWalls: true },
        };
      }

      // Also dismiss the wall improt reminder once this button is clicked...
      return {
        ...nextState,
        misc: { ...state.misc, showWallImportReminder: false },
      };
    }

    case 'layers.walls.mouseenter': {
      return State.highlightLayer(state, LayerId.WALLS);
    }

    case 'layers.walls.mouseleave': {
      return State.unhighlight(state);
    }

    case 'layers.heatmap.focus': {
      const nextState = State.focusLayer(state, LayerId.HEATMAP);

      let nextHeatmap = state.heatmap;
      if (!nextHeatmap.enabled) {
        nextHeatmap = { ...Heatmap.create(), enabled: true };
      }

      return {
        ...nextState,
        planning: { ...nextState.planning, showHeatMap: true },
        heatmap: nextHeatmap,
      };
    }

    case 'layers.heatmap.mouseenter': {
      return State.highlightLayer(state, LayerId.HEATMAP);
    }

    case 'layers.heatmap.mouseleave': {
      return State.unhighlight(state);
    }

    case 'layers.dismiss': {
      return State.blurFocusedObject(state);
    }

    case 'heatmap.setStartDate': {
      if (!state.heatmap.enabled) {
        return state;
      }
      return {
        ...state,
        heatmap: {
          enabled: true,
          ...Heatmap.changeStartDate(state.heatmap, action.startDate),
        },
      };
    }
    case 'heatmap.setEndDate': {
      if (!state.heatmap.enabled) {
        return state;
      }
      return {
        ...state,
        heatmap: {
          enabled: true,
          ...Heatmap.changeEndDate(state.heatmap, action.endDate),
        },
      };
    }
    case 'heatmap.changeOpacity': {
      if (!state.heatmap.enabled) {
        return state;
      }
      return {
        ...state,
        heatmap: {
          enabled: true,
          ...Heatmap.changeOpacity(state.heatmap, action.opacity),
        },
      };
    }
    case 'heatmap.beginLoading': {
      if (!state.heatmap.enabled) {
        return state;
      }
      return {
        ...state,
        heatmap: {
          ...state.heatmap,
          data: {
            status: 'loading',
            page: 1, // TODO: Remove me,
            lastBucketTimestamp: action.lastBucketTimestamp,
          },
        },
      };
    }
    case 'heatmap.beginComputing': {
      if (!state.heatmap.enabled) {
        return state;
      }
      return {
        ...state,
        heatmap: {
          ...state.heatmap,
          data: { status: 'computing-heatmap' },
        },
      };
    }
    case 'heatmap.setResult': {
      if (!state.heatmap.enabled) {
        return state;
      }
      return {
        ...state,
        heatmap: {
          ...state.heatmap,
          data: {
            status: 'complete',
            globalHeatmap: action.globalHeatmap,
            globalMax: action.globalMax,
            limits: {
              min: typeof action.minLimit !== 'undefined' ? action.minLimit : 0,
              max:
                typeof action.maxLimit !== 'undefined'
                  ? action.maxLimit
                  : action.globalMax,
            },
          },
        },
      };
    }
    case 'heatmap.changeLimits': {
      if (!state.heatmap.enabled) {
        return state;
      }
      if (state.heatmap.data.status !== 'complete') {
        return state;
      }
      return {
        ...state,
        heatmap: {
          ...state.heatmap,
          data: {
            ...state.heatmap.data,
            limits: {
              min: action.min,
              max: action.max,
            },
          },
        },
      };
    }
    case 'heatmap.error': {
      if (!state.heatmap.enabled) {
        return state;
      }
      return {
        ...state,
        heatmap: {
          ...state.heatmap,
          data: { status: 'error' },
        },
      };
    }
    case 'heatmap.cancel': {
      if (!state.heatmap.enabled) {
        return state;
      }
      return {
        ...state,
        heatmap: {
          ...state.heatmap,
          data: { status: 'pending' },
        },
      };
    }

    case 'heightMap.saveNotes': {
      if (!state.heightMap.enabled) {
        return state;
      }
      return {
        ...state,
        heightMap: {
          ...state.heightMap,
          notes: action.notes,
        },
      };
    }

    case 'heightMap.changeOpacity': {
      if (!state.heightMap.enabled) {
        return state;
      }
      return {
        ...state,
        heightMap: {
          ...state.heightMap,
          opacity: action.opacity,
        },
      };
    }

    case 'heightMap.change': {
      return {
        ...state,
        heightMapImport: { view: 'disabled' },
        heightMap:
          action.heightMap !== null
            ? { enabled: true, ...action.heightMap }
            : { enabled: false },

        // Show the height map after saving
        planning: {
          ...state.planning,
          showCeilingHeightMap: action.heightMap !== null,
        },

        // Mark the height map as changed so clicking "save" will update it
        heightMapUpdated: true,

        // Invalidate sensor coverage now that the height map no longer exists
        planSensorCoverageIntersectionVectors: new Map(
          FloorplanCollection.list(state.planSensors).map((sensor) => [
            sensor.id,
            'empty',
          ])
        ),

        // Invalidate all validations now that the height map no longer exists
        validations: new Map(
          Array.from(state.validations).map(([id]) => [id, 'empty'])
        ),

        // Clear the heights stores in all ReferenceHeights, as the height map changed position so
        // they all need to be recalculated
        references: FloorplanCollection.map(state.references, (reference) => {
          if (reference.type !== 'height') {
            return reference;
          }

          return { ...reference, heightMeters: { step: 'empty' } };
        }),

        // Set coverage intersection height map flag on areas of concern so that the height map
        // will be taken into account
        areasOfConcern: FloorplanCollection.map(
          state.areasOfConcern,
          (areaOfConcern) => ({
            ...areaOfConcern,
            coverageIntersectionHeightMapEnabled: action.heightMap !== null,
          })
        ),
      };
    }

    case 'heightMapImport.startUpload': {
      return {
        ...state,
        heightMapImport: {
          view: 'uploading-image',
          fileName: action.fileName,
        },
      };
    }

    case 'heightMapImport.uploadProgress': {
      if (state.heightMapImport.view !== 'uploading-image') {
        return state;
      }
      return {
        ...state,
        heightMapImport: {
          ...state.heightMapImport,
          fileUploadPercent: action.percent,
        },
      };
    }

    case 'heightMapImport.finishUpload': {
      return {
        ...state,
        heightMapImport: {
          view: 'ready',
          objectKey: action.objectKey,
          url: action.url,
          position: FloorplanCoordinates.create(0, 0),
          rotation: 0,
          opacity: 100,
          notes: '',
          limits: { enabled: false },
          geotiffTransformationData: {
            enabled: false,
          },
        },
      };
    }

    case 'heightMapImport.edit': {
      if (state.heightMapImport.view !== 'ready') {
        return state;
      }
      return {
        ...state,
        heightMapImport: {
          ...state.heightMapImport,
          view: 'enabled',
        },
      };
    }

    case 'heightMapImport.beginRegistration': {
      if (!state.heightMap.enabled) {
        return state;
      }
      return {
        ...state,
        heightMapImport: {
          view: 'enabled',
          ...state.heightMap,
        },
      };
    }

    case 'heightMapImport.changeRegistration': {
      if (state.heightMapImport.view !== 'enabled') {
        return state;
      }
      return {
        ...state,
        heightMapImport: {
          ...state.heightMapImport,
          position: action.position,
          rotation: action.rotation,
        },
      };
    }

    case 'heightMapImport.changePosition': {
      if (state.heightMapImport.view !== 'enabled') {
        return state;
      }
      return {
        ...state,
        heightMapImport: {
          ...state.heightMapImport,
          position: action.position,
        },
      };
    }

    case 'heightMapImport.setGeoTiffOffsets': {
      if (state.heightMapImport.view !== 'enabled') {
        return state;
      }
      return {
        ...state,
        heightMapImport: {
          ...state.heightMapImport,
          geotiffTransformationData: {
            enabled: true,
            tiePoint: action.tiePoint,
            scale: action.scale,
          },
        },
      };
    }

    case 'heightMapImport.changeRotation': {
      if (state.heightMapImport.view !== 'enabled') {
        return state;
      }
      return {
        ...state,
        heightMapImport: {
          ...state.heightMapImport,
          rotation: action.rotation,
        },
      };
    }

    case 'heightMapImport.rotateRight90': {
      if (state.heightMapImport.view !== 'enabled') {
        return state;
      }

      let newRotation = state.heightMapImport.rotation + 90;
      if (newRotation >= 360) {
        newRotation -= 360;
      }

      return {
        ...state,
        heightMapImport: {
          ...state.heightMapImport,
          rotation: newRotation,
        },
      };
    }

    case 'heightMapImport.changeBounds': {
      if (state.heightMapImport.view !== 'enabled') {
        return state;
      }
      return {
        ...state,
        heightMapImport: {
          ...state.heightMapImport,
          limits: {
            enabled: true,
            minMeters: action.minMeters,
            maxMeters: action.maxMeters,
          },
        },
      };
    }

    case 'heightMapImport.changeOpacity': {
      if (state.heightMapImport.view !== 'enabled') {
        return state;
      }
      return {
        ...state,
        heightMapImport: {
          ...state.heightMapImport,
          opacity: action.opacity,
        },
      };
    }

    case 'heightMapImport.cancel': {
      if (state.heightMapImport.view !== 'enabled') {
        return state;
      }
      return {
        ...state,
        heightMapImport: {
          ...state.heightMapImport,
          view: state.heightMap ? 'disabled' : 'ready',
        },
      };
    }

    case 'sensorCoverageIntersectionVectors.beginCalculating': {
      const nextSensorCoverageIntersectionVectors = new Map(
        state.planSensorCoverageIntersectionVectors
      );
      for (const id of action.sensorIds) {
        nextSensorCoverageIntersectionVectors.set(id, 'loading');
      }

      return {
        ...state,
        planSensorCoverageIntersectionVectors:
          nextSensorCoverageIntersectionVectors,
      };
    }

    case 'sensorCoverageIntersectionVectors.setResult': {
      let nextSensorCoverageIntersectionVectors = new Map(
        state.planSensorCoverageIntersectionVectors
      );

      for (const [sensorId, result] of Array.from(action.results)) {
        // If the update is not in progress, then reject the result
        // An example situation where this could happen: a user moves a sensor while it's already
        // calculating
        if (
          state.planSensorCoverageIntersectionVectors.get(sensorId) !==
          'loading'
        ) {
          continue;
        }

        nextSensorCoverageIntersectionVectors.set(sensorId, result);
      }

      return {
        ...state,
        planSensorCoverageIntersectionVectors:
          nextSensorCoverageIntersectionVectors,
      };
    }

    case 'wallsEditor.begin': {
      return {
        ...state,
        wallsEdit: {
          active: true,
          imageLineSegmentImport: { active: false },
          walls: state.walls,
        },
      };
    }

    case 'wallsEditor.beginWithImageLineSegments': {
      return {
        ...state,
        wallsEdit: {
          active: true,
          imageLineSegmentImport: {
            active: true,
            strokeColors: action.strokeColors,
            layers: action.layers,
            lineSegments: action.lineSegments,
            imageWidth: action.imageWidth,
            imageHeight: action.imageHeight,
          },
          walls: state.walls,
        },
      };
    }
    case 'wallsEditor.completeImageLineSegmentImport': {
      if (!state.wallsEdit.active) {
        return state;
      }
      if (!state.wallsEdit.imageLineSegmentImport.active) {
        return state;
      }
      return {
        ...state,
        wallsEdit: {
          ...state.wallsEdit,
          imageLineSegmentImport: { active: false },
        },
      };
    }

    case 'wallsEditor.cancel': {
      return {
        ...state,
        wallsEdit: { active: false },
      };
    }

    case 'wallsEditor.save': {
      // if (!state.wallsEdit.active) {
      //   return state;
      // }

      return {
        ...state,
        walls: action.walls,
        wallsEdit: { active: false },

        // Invalidate sensor coverage now that the walls have changed
        planSensorCoverageIntersectionVectors: new Map(
          FloorplanCollection.list(state.planSensors).map((sensor) => [
            sensor.id,
            'empty',
          ])
        ),

        // Invalidate all validations now that the walls have changed
        validations: new Map(
          Array.from(state.validations).map(([id]) => [id, 'empty'])
        ),

        // Invalidate area of concern sensor placements
        areasOfConcern: FloorplanCollection.map(
          state.areasOfConcern,
          (areaOfConcern) => ({
            ...areaOfConcern,
            sensorPlacements: { type: 'empty' },
          })
        ),

        planning: {
          ...state.planning,
          // If there are still walls, make sure walls are visible after saving them
          showWalls: !FloorplanCollection.isEmpty(action.walls),

          // If all walls were removed and there is no heightmap, disable sensor coverage extents rendering
          showOpenAreaCoverageExtents:
            state.planning.showOpenAreaCoverageExtents &&
            action.walls.items.size > 0 &&
            state.heightMap.enabled,
        },
      };
    }

    case 'wallsEditor.clear': {
      return {
        ...state,
        walls: FloorplanCollection.create(),

        // Invalidate sensor coverage now that the walls have changed
        planSensorCoverageIntersectionVectors: new Map(
          FloorplanCollection.list(state.planSensors).map((sensor) => [
            sensor.id,
            'empty',
          ])
        ),

        // Invalidate all validations now that the walls have changed
        validations: new Map(
          Array.from(state.validations).map(([id]) => [id, 'empty'])
        ),

        // Invalidate area of concern sensor placements
        areasOfConcern: FloorplanCollection.map(
          state.areasOfConcern,
          (areaOfConcern) => ({
            ...areaOfConcern,
            sensorPlacements: { type: 'empty' },
          })
        ),

        planning: {
          ...state.planning,
          // Make sure walls are hidden after clearing them all
          showWalls: false,

          // Continue showing the sensor coverage extents if there is a height map uploaded
          showOpenAreaCoverageExtents:
            state.planning.showOpenAreaCoverageExtents &&
            state.heightMap.enabled,
        },
      };
    }

    case 'wallSegment.importPageFromAPI': {
      const nextState = State.addWallSegments(
        state,
        action.responseBody.results.map((w) =>
          WallSegment.createFromFloorplanWallSegment(w)
        ),
        false
      );

      if (action.isFinalPage) {
        return { ...nextState, wallsFullyPopulated: true };
      }
      return nextState;
    }

    case 'wallSegment.changeIds': {
      if (action.oldIds.length !== action.newIds.length) {
        return state;
      }

      for (let i = 0; i < action.oldIds.length; i += 1) {
        const oldId = action.oldIds[i];
        if (!oldId) {
          continue;
        }
        const newId = action.newIds[i];
        if (!newId) {
          continue;
        }
        state = State.changeWallSegmentId(state, oldId, newId);
      }

      return state;
    }

    case 'websocket.event.plan': {
      switch (action.message.event) {
        case 'plan.updated': {
          if (!action.message.plan) {
            return state;
          }

          let nextState = state;

          const floorplan = Floorplan.fromFloorplanAPIResponse(
            action.message.plan
          );
          const measurement = Measurement.fromFloorplanAPIResponse(
            action.message.plan
          );
          const heightMap = HeightMap.fromFloorplanAPIResponse(
            action.message.plan
          );

          // NOTE: only update the image if the image key changes! This is important
          // because if the image is updated constantly, it will have to constantly reload
          // which results in a lot of white flashing in the floorplan component.
          if (
            action.message.plan.image_key !== state.floorplanImageKey &&
            action.message.plan.image_url
          ) {
            const newFloorplanImage = new Image();
            newFloorplanImage.src = action.message.plan.image_url;

            nextState = {
              ...nextState,
              floorplanImage: newFloorplanImage,
              floorplanImageKey: action.message.plan.image_key,
            };
          }

          return {
            ...nextState,
            floorplan,
            measurement,
            floorplanCADOrigin: FloorplanCoordinates.create(
              action.message.plan.floorplan_cad_origin_x,
              action.message.plan.floorplan_cad_origin_y
            ),
            heightMap: heightMap
              ? { enabled: true, ...heightMap }
              : { enabled: false },
          };
        }
        default:
          return state;
      }
    }

    case 'websocket.event.threshold': {
      switch (action.message.event) {
        case 'plan.doorway.created': {
          if (!action.message.plan_doorway) {
            return state;
          }
          const doorway = Threshold.createFromFloorplanThreshold(
            action.message.plan_doorway
          );
          return State.addThreshold(state, doorway, false);
        }
        case 'plan.doorway.updated':
          if (!action.message.plan_doorway) {
            return state;
          }
          const doorwayUpdate = Threshold.createFromFloorplanThreshold(
            action.message.plan_doorway
          );
          return State.updateThreshold(
            state,
            action.message.plan_doorway_id,
            doorwayUpdate
          );
        case 'plan.doorway.deleted':
          return State.removeThreshold(state, action.message.plan_doorway_id);
        default:
          return state;
      }
    }

    case 'websocket.event.sensor': {
      switch (action.message.event) {
        case 'plan.sensor.created': {
          if (!action.message.plan_sensor) {
            return state;
          }
          const sensor = PlanSensor.createFromFloorplanSensor(
            action.message.plan_sensor
          );
          return State.addSensor(state, sensor, false);
        }
        case 'plan.sensor.updated':
          if (!action.message.plan_sensor) {
            return state;
          }
          const sensorUpdate = PlanSensor.createFromFloorplanSensor(
            action.message.plan_sensor
          );
          return State.updateSensor(
            state,
            action.message.plan_sensor_id,
            sensorUpdate,
            true
          );
        case 'plan.sensor.deleted':
          return State.removeSensor(state, action.message.plan_sensor_id, true);
        default:
          return state;
      }
    }

    case 'websocket.event.areaOfConcern': {
      switch (action.message.event) {
        case 'plan.area_of_coverage.created': {
          if (!action.message.plan_area_of_coverage) {
            return state;
          }
          const areaOfConcern = AreaOfConcern.createFromFloorplanAreaOfConcern(
            action.message.plan_area_of_coverage
          );
          return State.addAreaOfConcern(state, areaOfConcern, false);
        }
        case 'plan.area_of_coverage.updated':
          if (!action.message.plan_area_of_coverage) {
            return state;
          }
          const areaOfConcernUpdate =
            AreaOfConcern.createFromFloorplanAreaOfConcern(
              action.message.plan_area_of_coverage
            );
          return State.updateAreaOfConcern(
            state,
            action.message.plan_area_of_coverage_id,
            areaOfConcernUpdate,
            true
          );
        case 'plan.area_of_coverage.deleted':
          return State.removeAreaOfConcern(
            state,
            action.message.plan_area_of_coverage_id,
            true
          );
        default:
          return state;
      }
    }

    case 'websocket.event.space': {
      switch (action.message.event) {
        case 'plan.space.created': {
          if (!action.message.space) {
            return state;
          }
          const space = Space.createFromFloorplanSpace(action.message.space);
          return State.addSpace(state, space, false);
        }
        case 'plan.space.updated':
          if (!action.message.space) {
            return state;
          }
          const spaceUpdate = Space.createFromFloorplanSpace(
            action.message.space
          );
          return State.updateSpace(
            state,
            action.message.space_id,
            spaceUpdate,
            true
          );
        case 'plan.space.deleted':
          return State.removeSpace(state, action.message.space_id, true);
        default:
          return state;
      }
    }

    case 'websocket.event.referenceRuler': {
      switch (action.message.event) {
        case 'plan.ruler.created': {
          if (!action.message.plan_reference_ruler) {
            return state;
          }
          const reference = ReferenceRuler.createFromFloorplanReferenceRuler(
            action.message.plan_reference_ruler
          );
          return State.addReference(state, reference, false);
        }
        case 'plan.ruler.updated':
          if (!action.message.plan_reference_ruler) {
            return state;
          }
          const referenceUpdate =
            ReferenceRuler.createFromFloorplanReferenceRuler(
              action.message.plan_reference_ruler
            );
          return State.updateReference(
            state,
            action.message.plan_reference_ruler_id,
            referenceUpdate
          );
        case 'plan.ruler.deleted':
          return State.removeReference(
            state,
            action.message.plan_reference_ruler_id
          );
        default:
          return state;
      }
    }

    case 'websocket.event.referenceHeight': {
      switch (action.message.event) {
        case 'plan.reference_height.created': {
          if (!action.message.plan_reference_height) {
            return state;
          }
          const reference = ReferenceHeight.createFromFloorplanReferenceHeight(
            action.message.plan_reference_height
          );
          return State.addReference(state, reference, false);
        }
        case 'plan.reference_height.updated':
          if (!action.message.plan_reference_height) {
            return state;
          }
          const referenceUpdate =
            ReferenceHeight.createFromFloorplanReferenceHeight(
              action.message.plan_reference_height
            );
          return State.updateReference(
            state,
            action.message.plan_reference_height_id,
            referenceUpdate
          );
        case 'plan.reference_height.deleted':
          return State.removeReference(
            state,
            action.message.plan_reference_height_id
          );
        default:
          return state;
      }
    }

    case 'websocket.event.photoGroup': {
      switch (action.message.event) {
        case 'plan.photo_group.created': {
          if (!action.message.plan_photo_group) {
            return state;
          }
          const photoGroup = PhotoGroup.createFromFloorplanPhotoGroup(
            action.message.plan_photo_group
          );
          return State.addPhotoGroup(state, photoGroup, false);
        }
        case 'plan.photo_group.updated': {
          if (!action.message.plan_photo_group) {
            return state;
          }
          const photoGroupUpdate = PhotoGroup.createFromFloorplanPhotoGroup(
            action.message.plan_photo_group
          );
          return State.updatePhotoGroup(
            state,
            action.message.plan_photo_group_id,
            photoGroupUpdate,
            true
          );
        }
        case 'plan.photo_group.deleted': {
          return State.removePhotoGroup(
            state,
            action.message.plan_photo_group_id,
            true
          );
        }
        default:
          return state;
      }
    }

    case 'websocket.event.photo': {
      switch (action.message.event) {
        case 'plan.photo.created': {
          if (!action.message.photo) {
            return state;
          }

          const photo = PhotoGroupPhoto.createFromImageUrl(
            action.message.photo.id,
            action.message.photo.name,
            action.message.photo.url
          );

          return State.updatePhotoGroup(
            state,
            action.message.plan_photo_group_id,
            (photoGroup) => {
              const existingPhoto = PhotoGroup.getPhotoById(
                photoGroup,
                action.message.plan_photo_id
              );
              if (!existingPhoto) {
                return photoGroup;
              }

              return {
                ...photoGroup,
                photos: [...photoGroup.photos, photo],
              };
            },
            true
          );
        }
        case 'plan.photo.updated': {
          if (!action.message.photo) {
            return state;
          }

          const photoUpdate = PhotoGroupPhoto.createFromImageUrl(
            action.message.photo.id,
            action.message.photo.name,
            action.message.photo.url
          );

          return State.updatePhotoGroup(
            state,
            action.message.plan_photo_group_id,
            (photoGroup) => {
              const existingPhoto = PhotoGroup.getPhotoById(
                photoGroup,
                action.message.plan_photo_id
              );
              if (!existingPhoto) {
                return photoGroup;
              }

              return {
                ...photoGroup,
                photos: photoGroup.photos.map((photo) =>
                  photo.id === action.message.plan_photo_id
                    ? { ...photo, name: photoUpdate.name }
                    : photo
                ),
              };
            },
            true
          );
        }
        case 'plan.photo.deleted': {
          return State.updatePhotoGroup(
            state,
            action.message.plan_photo_group_id,
            (photoGroup) => {
              const existingPhoto = PhotoGroup.getPhotoById(
                photoGroup,
                action.message.plan_photo_id
              );
              if (!existingPhoto) {
                return photoGroup;
              }

              return {
                ...photoGroup,
                photos: photoGroup.photos.filter(
                  (photo) => photo.id !== action.message.plan_photo_id
                ),
              };
            },
            true
          );
        }
        default:
          return state;
      }
    }

    case 'spaceHierarchyData.begin': {
      return { ...state, spaceHierarchyDataLoadingStatus: 'loading' };
    }
    case 'spaceHierarchyData.complete': {
      // Step 1: Unroll the hierarchy into a flat space list
      const spaceMetadata = new Map<
        CoreSpaceHierarchyNode['id'],
        CoreSpaceHierarchyNode
      >();
      const traverse = (node: CoreSpaceHierarchyNode) => {
        spaceMetadata.set(node.id, node);
        for (const child of node.children || []) {
          traverse(child);
        }
      };
      traverse(action.data);

      // Step 2: Update each space in the state using the hierarchy data
      const spaces = FloorplanCollection.map(state.spaces, (space) => {
        if (!space.id) {
          return space;
        }

        const node = spaceMetadata.get(space.id);
        if (!node) {
          return space;
        }

        return {
          ...space,
          coreSpaceCapacity: node.capacity,
          // FIXME: the CoreSpaceHierarchyNode seems to be missing the function key?
          coreSpaceFunction: (node as any)['function'],
          // FIXME: the CoreSpaceHierarchyNode seems to be missing the labels key?
          coreSpaceLabels: (node as any).labels,
          coreSpaceMetadataLabelIdsToAdd: [],
          coreSpaceMetadataLabelIdsToRemove: [],
        };
      });

      return {
        ...state,
        planMetadata: {
          ...state.planMetadata,
          buildingName:
            (action.data as any)?.parent?.name ??
            state.planMetadata.buildingName,
          organizationName:
            (action.data as any)?.parent?.organization ??
            state.planMetadata.organizationName,
        },
        spaces,
        spaceHierarchyDataLoadingStatus: 'complete',
      };
    }
    case 'spaceHierarchyData.error': {
      return { ...state, spaceHierarchyDataLoadingStatus: 'error' };
    }

    case 'validations.beginCalculating': {
      const validationsCopy = new Map(state.validations);
      for (const id of action.ids) {
        validationsCopy.set(id, 'loading');
      }
      return { ...state, validations: validationsCopy };
    }

    case 'validations.set': {
      const validationsCopy = new Map(state.validations);
      for (const [k, v] of Array.from(action.validations)) {
        validationsCopy.set(k, v);
      }
      return { ...state, validations: validationsCopy };
    }

    case 'lock': {
      return {
        ...state,
        locked: true,
        placementMode: null,
        bulkSelection: {
          ...state.bulkSelection,
          ids: new Set<string>(),
          action: null,
        },
      };
    }
    case 'unlock': {
      return { ...state, locked: false };
    }

    case 'reminder.wallImport.dismiss': {
      return {
        ...state,
        misc: { ...state.misc, showWallImportReminder: false },
      };
    }

    case 'menu.enableSyncHeightsButton': {
      return {
        ...state,
        syncHeightsButtonEnabled: true,
      };
    }
    case 'menu.disableSyncHeightsButton': {
      return {
        ...state,
        syncHeightsButtonEnabled: false,
      };
    }

    case 'defaultSensorType.set.openArea': {
      return {
        ...state,
        defaultSensorType: {
          ...state.defaultSensorType,
          openArea: action.sensorType as OpenAreaFunction,
        },
      };
    }

    case 'defaultSensorType.set.entry': {
      return {
        ...state,
        defaultSensorType: {
          ...state.defaultSensorType,
          entry: action.sensorType as EntryFunction,
        },
      };
    }

    case 'selectionMode.begin': {
      let nextState = state;

      if (action.hideAllLayersBut) {
        nextState = {
          ...nextState,
          planning: {
            ...nextState.planning,
            showFloorplanImage: true,
            showSensors:
              action.hideAllLayersBut.includes('oaSensors') ||
              action.hideAllLayersBut.includes('entrySensors'),
            showSensorsOpenArea: action.hideAllLayersBut.includes('oaSensors'),
            showOpenAreaLabels: false,
            showOpenAreaCoverage: action.hideAllLayersBut.includes('oaSensors'),
            showOpenAreaCoverageExtents: false,

            showLiveStreamOpenArea: false,
            showLiveStreamOpenAreaTracks: false,
            showLiveStreamOpenAreaPoints: false,
            showLiveStreamEntry: false,
            showLiveStreamEntryCounts: false,
            showLiveStreamEntryTracks: false,
            showLiveStreamEntryPoints: false,

            showEntryLabels: false,
            showEntryCoverage: action.hideAllLayersBut.includes('entrySensors'),
            showSensorsEntry: action.hideAllLayersBut.includes('entrySensors'),
            showSensorsEntryLR:
              action.hideAllLayersBut.includes('entrySensors'),
            showSensorsEntryTOF:
              action.hideAllLayersBut.includes('entrySensors'),
            showSensorsOpenAreaLR: false,
            showSensorsOpenAreaMR: false,
            showSensorsOpenAreaSR: false,
            showAreasOfConcern:
              action.hideAllLayersBut.includes('areasofconcern'),
            showThresholds: action.hideAllLayersBut.includes('thresholds'),
            showTriggerRegions: false,
            showSpaces:
              action.hideAllLayersBut.includes('oaSpaces') ||
              action.hideAllLayersBut.includes('entrySpaces'),
            showSpacesEntry: action.hideAllLayersBut.includes('entrySpaces'),
            showSpacesOpenArea: action.hideAllLayersBut.includes('oaSpaces'),
            showSpaceNames:
              action.hideAllLayersBut.includes('oaSpaces') ||
              action.hideAllLayersBut.includes('entrySpaces'),
            showRulers: false,
            showHeights: false,
            showScale: false,
            showPhotoGroups: false,
            showCeilingHeightMap: false,
            showWalls: false,
            showHeatMap: false,
            showSpacesWaffle: false,
          },
        };
      }

      return {
        ...nextState,
        selectionMode: {
          active: true,
          pendingAction: action.pendingAction,
          expectedSelectionType: action.expectedSelectionType,
          initialActionCreator: action.initialActionCreator || undefined,
        },
        cachedPlanningState: state.planning,
      };
    }

    case 'selectionMode.end': {
      if (!state.selectionMode.active) {
        return state;
      }
      return {
        ...state,
        selectionMode: {
          active: false,
        },
        planning:
          state.cachedPlanningState !== null
            ? state.cachedPlanningState
            : state.planning,
        cachedPlanningState: null,
      };
    }

    case 'keys.ctrl': {
      return {
        ...state,
        keys: { ...state.keys, ctrl: action.active },
      };
    }

    case 'keys.ctrlShift': {
      return {
        ...state,
        keys: { ...state.keys, ctrlShift: action.active },
      };
    }

    case 'menu.planning.toggleSpacesWaffle': {
      return {
        ...state,
        planning: {
          ...state.planning,
          showSpacesWaffle: !state.planning.showSpacesWaffle,
        },
      };
    }

    case 'menu.planning.toggleSensorsWaffle': {
      return {
        ...state,
        planning: {
          ...state.planning,
          showSensorsWaffle: !state.planning.showSensorsWaffle,
        },
      };
    }
    // eslint-disable-next-line no-duplicate-case
    case 'menu.planning.toggleOpenAreaLabels':
      return {
        ...state,
        planning: {
          ...state.planning,
          showOpenAreaLabels: !state.planning.showOpenAreaLabels,
        },
      };

    case 'menu.planning.toggleOriginMarker': {
      return {
        ...state,
        planning: {
          ...state.planning,
          showOriginMarker: !state.planning.showOriginMarker,
        },
      };
    }

    default: {
      return state;
    }
  }
};
